package com.wiz3r.database

import app.cash.sqldelight.ExecutableQuery
import app.cash.sqldelight.Query
import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.ByteArray
import kotlin.Double
import kotlin.Long
import kotlin.String
import kotlin.collections.Collection

public class SchemaQueries(
  driver: SqlDriver,
) : SuspendingTransacterImpl(driver) {
  public fun <T : Any> selectUserByUsername(username: String, mapper: (
    id: Long,
    user_id: String,
    username: String,
    password_hash: String,
    email: String?,
    created_at: String,
    updated_at: String,
    last_login_at: String?,
    mfa_secret: String?,
    mfa_enabled: Long,
    backup_codes: String?,
    email_verified: Long,
    verification_token: String?,
    verification_token_expires_at: String?,
    password_reset_token_hash: String?,
    password_reset_expires_at: String?,
    stripe_customer_id: String?,
    subscription_status: String,
    subscription_id: String?,
    trial_ends_at: String,
    subscription_tier: String,
    last_activity_at: String?,
    phone_number: String?,
    wiz3r_ai_enabled: Long,
    timezone: String,
  ) -> T): Query<T> = SelectUserByUsernameQuery(username) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7),
      cursor.getString(8),
      cursor.getLong(9)!!,
      cursor.getString(10),
      cursor.getLong(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17)!!,
      cursor.getString(18),
      cursor.getString(19)!!,
      cursor.getString(20)!!,
      cursor.getString(21),
      cursor.getString(22),
      cursor.getLong(23)!!,
      cursor.getString(24)!!
    )
  }

  public fun selectUserByUsername(username: String): Query<Users> = selectUserByUsername(username) {
      id, user_id, username_, password_hash, email, created_at, updated_at, last_login_at,
      mfa_secret, mfa_enabled, backup_codes, email_verified, verification_token,
      verification_token_expires_at, password_reset_token_hash, password_reset_expires_at,
      stripe_customer_id, subscription_status, subscription_id, trial_ends_at, subscription_tier,
      last_activity_at, phone_number, wiz3r_ai_enabled, timezone ->
    Users(
      id,
      user_id,
      username_,
      password_hash,
      email,
      created_at,
      updated_at,
      last_login_at,
      mfa_secret,
      mfa_enabled,
      backup_codes,
      email_verified,
      verification_token,
      verification_token_expires_at,
      password_reset_token_hash,
      password_reset_expires_at,
      stripe_customer_id,
      subscription_status,
      subscription_id,
      trial_ends_at,
      subscription_tier,
      last_activity_at,
      phone_number,
      wiz3r_ai_enabled,
      timezone
    )
  }

  public fun <T : Any> selectUserById(user_id: String, mapper: (
    id: Long,
    user_id: String,
    username: String,
    password_hash: String,
    email: String?,
    created_at: String,
    updated_at: String,
    last_login_at: String?,
    mfa_secret: String?,
    mfa_enabled: Long,
    backup_codes: String?,
    email_verified: Long,
    verification_token: String?,
    verification_token_expires_at: String?,
    password_reset_token_hash: String?,
    password_reset_expires_at: String?,
    stripe_customer_id: String?,
    subscription_status: String,
    subscription_id: String?,
    trial_ends_at: String,
    subscription_tier: String,
    last_activity_at: String?,
    phone_number: String?,
    wiz3r_ai_enabled: Long,
    timezone: String,
  ) -> T): Query<T> = SelectUserByIdQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7),
      cursor.getString(8),
      cursor.getLong(9)!!,
      cursor.getString(10),
      cursor.getLong(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17)!!,
      cursor.getString(18),
      cursor.getString(19)!!,
      cursor.getString(20)!!,
      cursor.getString(21),
      cursor.getString(22),
      cursor.getLong(23)!!,
      cursor.getString(24)!!
    )
  }

  public fun selectUserById(user_id: String): Query<Users> = selectUserById(user_id) { id, user_id_,
      username, password_hash, email, created_at, updated_at, last_login_at, mfa_secret,
      mfa_enabled, backup_codes, email_verified, verification_token, verification_token_expires_at,
      password_reset_token_hash, password_reset_expires_at, stripe_customer_id, subscription_status,
      subscription_id, trial_ends_at, subscription_tier, last_activity_at, phone_number,
      wiz3r_ai_enabled, timezone ->
    Users(
      id,
      user_id_,
      username,
      password_hash,
      email,
      created_at,
      updated_at,
      last_login_at,
      mfa_secret,
      mfa_enabled,
      backup_codes,
      email_verified,
      verification_token,
      verification_token_expires_at,
      password_reset_token_hash,
      password_reset_expires_at,
      stripe_customer_id,
      subscription_status,
      subscription_id,
      trial_ends_at,
      subscription_tier,
      last_activity_at,
      phone_number,
      wiz3r_ai_enabled,
      timezone
    )
  }

  public fun selectAllUserIds(): Query<String> = Query(84_140_354, arrayOf("users"), driver,
      "schema.sq", "selectAllUserIds", "SELECT user_id FROM users") { cursor ->
    cursor.getString(0)!!
  }

  public fun <T : Any> getMfaStatus(user_id: String, mapper: (mfa_enabled: Long,
      mfa_secret: String?) -> T): Query<T> = GetMfaStatusQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)
    )
  }

  public fun getMfaStatus(user_id: String): Query<GetMfaStatus> = getMfaStatus(user_id) {
      mfa_enabled, mfa_secret ->
    GetMfaStatus(
      mfa_enabled,
      mfa_secret
    )
  }

  public fun <T : Any> getBackupCodes(user_id: String, mapper: (backup_codes: String?) -> T):
      Query<T> = GetBackupCodesQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)
    )
  }

  public fun getBackupCodes(user_id: String): Query<GetBackupCodes> = getBackupCodes(user_id) {
      backup_codes ->
    GetBackupCodes(
      backup_codes
    )
  }

  public fun <T : Any> getSubscriptionStatus(user_id: String, mapper: (
    subscription_status: String,
    subscription_id: String?,
    stripe_customer_id: String?,
    trial_ends_at: String,
    subscription_tier: String,
  ) -> T): Query<T> = GetSubscriptionStatusQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1),
      cursor.getString(2),
      cursor.getString(3)!!,
      cursor.getString(4)!!
    )
  }

  public fun getSubscriptionStatus(user_id: String): Query<GetSubscriptionStatus> =
      getSubscriptionStatus(user_id) { subscription_status, subscription_id, stripe_customer_id,
      trial_ends_at, subscription_tier ->
    GetSubscriptionStatus(
      subscription_status,
      subscription_id,
      stripe_customer_id,
      trial_ends_at,
      subscription_tier
    )
  }

  public fun <T : Any> getUserByStripeCustomerId(stripe_customer_id: String?, mapper: (
    id: Long,
    user_id: String,
    username: String,
    password_hash: String,
    email: String?,
    created_at: String,
    updated_at: String,
    last_login_at: String?,
    mfa_secret: String?,
    mfa_enabled: Long,
    backup_codes: String?,
    email_verified: Long,
    verification_token: String?,
    verification_token_expires_at: String?,
    password_reset_token_hash: String?,
    password_reset_expires_at: String?,
    stripe_customer_id: String?,
    subscription_status: String,
    subscription_id: String?,
    trial_ends_at: String,
    subscription_tier: String,
    last_activity_at: String?,
    phone_number: String?,
    wiz3r_ai_enabled: Long,
    timezone: String,
  ) -> T): Query<T> = GetUserByStripeCustomerIdQuery(stripe_customer_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7),
      cursor.getString(8),
      cursor.getLong(9)!!,
      cursor.getString(10),
      cursor.getLong(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17)!!,
      cursor.getString(18),
      cursor.getString(19)!!,
      cursor.getString(20)!!,
      cursor.getString(21),
      cursor.getString(22),
      cursor.getLong(23)!!,
      cursor.getString(24)!!
    )
  }

  public fun getUserByStripeCustomerId(stripe_customer_id: String?): Query<Users> =
      getUserByStripeCustomerId(stripe_customer_id) { id, user_id, username, password_hash, email,
      created_at, updated_at, last_login_at, mfa_secret, mfa_enabled, backup_codes, email_verified,
      verification_token, verification_token_expires_at, password_reset_token_hash,
      password_reset_expires_at, stripe_customer_id_, subscription_status, subscription_id,
      trial_ends_at, subscription_tier, last_activity_at, phone_number, wiz3r_ai_enabled,
      timezone ->
    Users(
      id,
      user_id,
      username,
      password_hash,
      email,
      created_at,
      updated_at,
      last_login_at,
      mfa_secret,
      mfa_enabled,
      backup_codes,
      email_verified,
      verification_token,
      verification_token_expires_at,
      password_reset_token_hash,
      password_reset_expires_at,
      stripe_customer_id_,
      subscription_status,
      subscription_id,
      trial_ends_at,
      subscription_tier,
      last_activity_at,
      phone_number,
      wiz3r_ai_enabled,
      timezone
    )
  }

  public fun getSubscriptionTier(user_id: String): Query<String> =
      GetSubscriptionTierQuery(user_id) { cursor ->
    cursor.getString(0)!!
  }

  public fun getAiEnabledStatus(user_id: String): Query<Long> = GetAiEnabledStatusQuery(user_id) {
      cursor ->
    cursor.getLong(0)!!
  }

  public fun getUserTimezone(user_id: String): Query<String> = GetUserTimezoneQuery(user_id) {
      cursor ->
    cursor.getString(0)!!
  }

  public fun <T : Any> selectUserByEmail(email: String?, mapper: (
    id: Long,
    user_id: String,
    username: String,
    password_hash: String,
    email: String?,
    created_at: String,
    updated_at: String,
    last_login_at: String?,
    mfa_secret: String?,
    mfa_enabled: Long,
    backup_codes: String?,
    email_verified: Long,
    verification_token: String?,
    verification_token_expires_at: String?,
    password_reset_token_hash: String?,
    password_reset_expires_at: String?,
    stripe_customer_id: String?,
    subscription_status: String,
    subscription_id: String?,
    trial_ends_at: String,
    subscription_tier: String,
    last_activity_at: String?,
    phone_number: String?,
    wiz3r_ai_enabled: Long,
    timezone: String,
  ) -> T): Query<T> = SelectUserByEmailQuery(email) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7),
      cursor.getString(8),
      cursor.getLong(9)!!,
      cursor.getString(10),
      cursor.getLong(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17)!!,
      cursor.getString(18),
      cursor.getString(19)!!,
      cursor.getString(20)!!,
      cursor.getString(21),
      cursor.getString(22),
      cursor.getLong(23)!!,
      cursor.getString(24)!!
    )
  }

  public fun selectUserByEmail(email: String?): Query<Users> = selectUserByEmail(email) { id,
      user_id, username, password_hash, email_, created_at, updated_at, last_login_at, mfa_secret,
      mfa_enabled, backup_codes, email_verified, verification_token, verification_token_expires_at,
      password_reset_token_hash, password_reset_expires_at, stripe_customer_id, subscription_status,
      subscription_id, trial_ends_at, subscription_tier, last_activity_at, phone_number,
      wiz3r_ai_enabled, timezone ->
    Users(
      id,
      user_id,
      username,
      password_hash,
      email_,
      created_at,
      updated_at,
      last_login_at,
      mfa_secret,
      mfa_enabled,
      backup_codes,
      email_verified,
      verification_token,
      verification_token_expires_at,
      password_reset_token_hash,
      password_reset_expires_at,
      stripe_customer_id,
      subscription_status,
      subscription_id,
      trial_ends_at,
      subscription_tier,
      last_activity_at,
      phone_number,
      wiz3r_ai_enabled,
      timezone
    )
  }

  public fun <T : Any> selectUserByVerificationToken(verification_token: String?, mapper: (
    id: Long,
    user_id: String,
    username: String,
    password_hash: String,
    email: String?,
    created_at: String,
    updated_at: String,
    last_login_at: String?,
    mfa_secret: String?,
    mfa_enabled: Long,
    backup_codes: String?,
    email_verified: Long,
    verification_token: String?,
    verification_token_expires_at: String?,
    password_reset_token_hash: String?,
    password_reset_expires_at: String?,
    stripe_customer_id: String?,
    subscription_status: String,
    subscription_id: String?,
    trial_ends_at: String,
    subscription_tier: String,
    last_activity_at: String?,
    phone_number: String?,
    wiz3r_ai_enabled: Long,
    timezone: String,
  ) -> T): Query<T> = SelectUserByVerificationTokenQuery(verification_token) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7),
      cursor.getString(8),
      cursor.getLong(9)!!,
      cursor.getString(10),
      cursor.getLong(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17)!!,
      cursor.getString(18),
      cursor.getString(19)!!,
      cursor.getString(20)!!,
      cursor.getString(21),
      cursor.getString(22),
      cursor.getLong(23)!!,
      cursor.getString(24)!!
    )
  }

  public fun selectUserByVerificationToken(verification_token: String?): Query<Users> =
      selectUserByVerificationToken(verification_token) { id, user_id, username, password_hash,
      email, created_at, updated_at, last_login_at, mfa_secret, mfa_enabled, backup_codes,
      email_verified, verification_token_, verification_token_expires_at, password_reset_token_hash,
      password_reset_expires_at, stripe_customer_id, subscription_status, subscription_id,
      trial_ends_at, subscription_tier, last_activity_at, phone_number, wiz3r_ai_enabled,
      timezone ->
    Users(
      id,
      user_id,
      username,
      password_hash,
      email,
      created_at,
      updated_at,
      last_login_at,
      mfa_secret,
      mfa_enabled,
      backup_codes,
      email_verified,
      verification_token_,
      verification_token_expires_at,
      password_reset_token_hash,
      password_reset_expires_at,
      stripe_customer_id,
      subscription_status,
      subscription_id,
      trial_ends_at,
      subscription_tier,
      last_activity_at,
      phone_number,
      wiz3r_ai_enabled,
      timezone
    )
  }

  public fun <T : Any> selectUserByPasswordResetTokenHash(password_reset_token_hash: String?,
      mapper: (
    id: Long,
    user_id: String,
    username: String,
    password_hash: String,
    email: String?,
    created_at: String,
    updated_at: String,
    last_login_at: String?,
    mfa_secret: String?,
    mfa_enabled: Long,
    backup_codes: String?,
    email_verified: Long,
    verification_token: String?,
    verification_token_expires_at: String?,
    password_reset_token_hash: String?,
    password_reset_expires_at: String?,
    stripe_customer_id: String?,
    subscription_status: String,
    subscription_id: String?,
    trial_ends_at: String,
    subscription_tier: String,
    last_activity_at: String?,
    phone_number: String?,
    wiz3r_ai_enabled: Long,
    timezone: String,
  ) -> T): Query<T> = SelectUserByPasswordResetTokenHashQuery(password_reset_token_hash) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7),
      cursor.getString(8),
      cursor.getLong(9)!!,
      cursor.getString(10),
      cursor.getLong(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17)!!,
      cursor.getString(18),
      cursor.getString(19)!!,
      cursor.getString(20)!!,
      cursor.getString(21),
      cursor.getString(22),
      cursor.getLong(23)!!,
      cursor.getString(24)!!
    )
  }

  public fun selectUserByPasswordResetTokenHash(password_reset_token_hash: String?): Query<Users> =
      selectUserByPasswordResetTokenHash(password_reset_token_hash) { id, user_id, username,
      password_hash, email, created_at, updated_at, last_login_at, mfa_secret, mfa_enabled,
      backup_codes, email_verified, verification_token, verification_token_expires_at,
      password_reset_token_hash_, password_reset_expires_at, stripe_customer_id,
      subscription_status, subscription_id, trial_ends_at, subscription_tier, last_activity_at,
      phone_number, wiz3r_ai_enabled, timezone ->
    Users(
      id,
      user_id,
      username,
      password_hash,
      email,
      created_at,
      updated_at,
      last_login_at,
      mfa_secret,
      mfa_enabled,
      backup_codes,
      email_verified,
      verification_token,
      verification_token_expires_at,
      password_reset_token_hash_,
      password_reset_expires_at,
      stripe_customer_id,
      subscription_status,
      subscription_id,
      trial_ends_at,
      subscription_tier,
      last_activity_at,
      phone_number,
      wiz3r_ai_enabled,
      timezone
    )
  }

  public fun <T : Any> selectTransactionsByUser(user_id: String, mapper: (
    id: Long,
    transaction_id: String,
    account_id: String,
    user_id: String,
    amount: Double,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    date: String,
    authorized_date: String?,
    authorized_datetime: String?,
    datetime: String?,
    name: String,
    merchant_name: String?,
    original_description: String?,
    category: String?,
    category_id: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    personal_finance_category_confidence: String?,
    transaction_type: String?,
    transaction_code: String?,
    pending: Long,
    pending_transaction_id: String?,
    location_json: String?,
    payment_meta_json: String?,
    account_owner: String?,
    created_at: String,
    updated_at: String,
    last_synced_at: String?,
    user_category: String?,
    embedding: ByteArray?,
    embedding_version: Long?,
    user_notes: String?,
    is_hidden: Long,
    is_expense_report: Long,
    tags: String?,
    ai_category: String?,
    confidence_score: Double?,
    original_ai_category: String?,
    corrected_at: String?,
    correction_confidence: Double?,
    ml_model_version: String?,
    category_source: String?,
    parent_transaction_id: String?,
    child_type: String?,
    is_split_parent: Long,
    is_distributed_parent: Long,
    split_order: Long?,
    distribution_start_month: String?,
    distribution_end_month: String?,
    distribution_month_index: Long?,
    distribution_total_months: Long?,
    is_verified: Long,
    verified_at: String?,
    is_excluded: Long,
    notified_at: String?,
  ) -> T): Query<T> = SelectTransactionsByUserQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun selectTransactionsByUser(user_id: String): Query<Transactions> =
      selectTransactionsByUser(user_id) { id, transaction_id, account_id, user_id_, amount,
      iso_currency_code, unofficial_currency_code, date, authorized_date, authorized_datetime,
      datetime, name, merchant_name, original_description, category, category_id,
      personal_finance_category_primary, personal_finance_category_detailed,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source, parent_transaction_id,
      child_type, is_split_parent, is_distributed_parent, split_order, distribution_start_month,
      distribution_end_month, distribution_month_index, distribution_total_months, is_verified,
      verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id,
      user_id_,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> selectTransactionsByAccount(account_id: String, mapper: (
    id: Long,
    transaction_id: String,
    account_id: String,
    user_id: String,
    amount: Double,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    date: String,
    authorized_date: String?,
    authorized_datetime: String?,
    datetime: String?,
    name: String,
    merchant_name: String?,
    original_description: String?,
    category: String?,
    category_id: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    personal_finance_category_confidence: String?,
    transaction_type: String?,
    transaction_code: String?,
    pending: Long,
    pending_transaction_id: String?,
    location_json: String?,
    payment_meta_json: String?,
    account_owner: String?,
    created_at: String,
    updated_at: String,
    last_synced_at: String?,
    user_category: String?,
    embedding: ByteArray?,
    embedding_version: Long?,
    user_notes: String?,
    is_hidden: Long,
    is_expense_report: Long,
    tags: String?,
    ai_category: String?,
    confidence_score: Double?,
    original_ai_category: String?,
    corrected_at: String?,
    correction_confidence: Double?,
    ml_model_version: String?,
    category_source: String?,
    parent_transaction_id: String?,
    child_type: String?,
    is_split_parent: Long,
    is_distributed_parent: Long,
    split_order: Long?,
    distribution_start_month: String?,
    distribution_end_month: String?,
    distribution_month_index: Long?,
    distribution_total_months: Long?,
    is_verified: Long,
    verified_at: String?,
    is_excluded: Long,
    notified_at: String?,
  ) -> T): Query<T> = SelectTransactionsByAccountQuery(account_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun selectTransactionsByAccount(account_id: String): Query<Transactions> =
      selectTransactionsByAccount(account_id) { id, transaction_id, account_id_, user_id, amount,
      iso_currency_code, unofficial_currency_code, date, authorized_date, authorized_datetime,
      datetime, name, merchant_name, original_description, category, category_id,
      personal_finance_category_primary, personal_finance_category_detailed,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source, parent_transaction_id,
      child_type, is_split_parent, is_distributed_parent, split_order, distribution_start_month,
      distribution_end_month, distribution_month_index, distribution_total_months, is_verified,
      verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id_,
      user_id,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> selectTransactionsByDateRange(
    user_id: String,
    date: String,
    date_: String,
    mapper: (
      id: Long,
      transaction_id: String,
      account_id: String,
      user_id: String,
      amount: Double,
      iso_currency_code: String?,
      unofficial_currency_code: String?,
      date: String,
      authorized_date: String?,
      authorized_datetime: String?,
      datetime: String?,
      name: String,
      merchant_name: String?,
      original_description: String?,
      category: String?,
      category_id: String?,
      personal_finance_category_primary: String?,
      personal_finance_category_detailed: String?,
      personal_finance_category_confidence: String?,
      transaction_type: String?,
      transaction_code: String?,
      pending: Long,
      pending_transaction_id: String?,
      location_json: String?,
      payment_meta_json: String?,
      account_owner: String?,
      created_at: String,
      updated_at: String,
      last_synced_at: String?,
      user_category: String?,
      embedding: ByteArray?,
      embedding_version: Long?,
      user_notes: String?,
      is_hidden: Long,
      is_expense_report: Long,
      tags: String?,
      ai_category: String?,
      confidence_score: Double?,
      original_ai_category: String?,
      corrected_at: String?,
      correction_confidence: Double?,
      ml_model_version: String?,
      category_source: String?,
      parent_transaction_id: String?,
      child_type: String?,
      is_split_parent: Long,
      is_distributed_parent: Long,
      split_order: Long?,
      distribution_start_month: String?,
      distribution_end_month: String?,
      distribution_month_index: Long?,
      distribution_total_months: Long?,
      is_verified: Long,
      verified_at: String?,
      is_excluded: Long,
      notified_at: String?,
    ) -> T,
  ): Query<T> = SelectTransactionsByDateRangeQuery(user_id, date, date_) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun selectTransactionsByDateRange(
    user_id: String,
    date: String,
    date_: String,
  ): Query<Transactions> = selectTransactionsByDateRange(user_id, date, date_) { id, transaction_id,
      account_id, user_id_, amount, iso_currency_code, unofficial_currency_code, date__,
      authorized_date, authorized_datetime, datetime, name, merchant_name, original_description,
      category, category_id, personal_finance_category_primary, personal_finance_category_detailed,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source, parent_transaction_id,
      child_type, is_split_parent, is_distributed_parent, split_order, distribution_start_month,
      distribution_end_month, distribution_month_index, distribution_total_months, is_verified,
      verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id,
      user_id_,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date__,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> selectPendingTransactions(user_id: String, mapper: (
    id: Long,
    transaction_id: String,
    account_id: String,
    user_id: String,
    amount: Double,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    date: String,
    authorized_date: String?,
    authorized_datetime: String?,
    datetime: String?,
    name: String,
    merchant_name: String?,
    original_description: String?,
    category: String?,
    category_id: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    personal_finance_category_confidence: String?,
    transaction_type: String?,
    transaction_code: String?,
    pending: Long,
    pending_transaction_id: String?,
    location_json: String?,
    payment_meta_json: String?,
    account_owner: String?,
    created_at: String,
    updated_at: String,
    last_synced_at: String?,
    user_category: String?,
    embedding: ByteArray?,
    embedding_version: Long?,
    user_notes: String?,
    is_hidden: Long,
    is_expense_report: Long,
    tags: String?,
    ai_category: String?,
    confidence_score: Double?,
    original_ai_category: String?,
    corrected_at: String?,
    correction_confidence: Double?,
    ml_model_version: String?,
    category_source: String?,
    parent_transaction_id: String?,
    child_type: String?,
    is_split_parent: Long,
    is_distributed_parent: Long,
    split_order: Long?,
    distribution_start_month: String?,
    distribution_end_month: String?,
    distribution_month_index: Long?,
    distribution_total_months: Long?,
    is_verified: Long,
    verified_at: String?,
    is_excluded: Long,
    notified_at: String?,
  ) -> T): Query<T> = SelectPendingTransactionsQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun selectPendingTransactions(user_id: String): Query<Transactions> =
      selectPendingTransactions(user_id) { id, transaction_id, account_id, user_id_, amount,
      iso_currency_code, unofficial_currency_code, date, authorized_date, authorized_datetime,
      datetime, name, merchant_name, original_description, category, category_id,
      personal_finance_category_primary, personal_finance_category_detailed,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source, parent_transaction_id,
      child_type, is_split_parent, is_distributed_parent, split_order, distribution_start_month,
      distribution_end_month, distribution_month_index, distribution_total_months, is_verified,
      verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id,
      user_id_,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> selectTransactionsByCategory(
    user_id: String,
    personal_finance_category_primary: String?,
    mapper: (
      id: Long,
      transaction_id: String,
      account_id: String,
      user_id: String,
      amount: Double,
      iso_currency_code: String?,
      unofficial_currency_code: String?,
      date: String,
      authorized_date: String?,
      authorized_datetime: String?,
      datetime: String?,
      name: String,
      merchant_name: String?,
      original_description: String?,
      category: String?,
      category_id: String?,
      personal_finance_category_primary: String?,
      personal_finance_category_detailed: String?,
      personal_finance_category_confidence: String?,
      transaction_type: String?,
      transaction_code: String?,
      pending: Long,
      pending_transaction_id: String?,
      location_json: String?,
      payment_meta_json: String?,
      account_owner: String?,
      created_at: String,
      updated_at: String,
      last_synced_at: String?,
      user_category: String?,
      embedding: ByteArray?,
      embedding_version: Long?,
      user_notes: String?,
      is_hidden: Long,
      is_expense_report: Long,
      tags: String?,
      ai_category: String?,
      confidence_score: Double?,
      original_ai_category: String?,
      corrected_at: String?,
      correction_confidence: Double?,
      ml_model_version: String?,
      category_source: String?,
      parent_transaction_id: String?,
      child_type: String?,
      is_split_parent: Long,
      is_distributed_parent: Long,
      split_order: Long?,
      distribution_start_month: String?,
      distribution_end_month: String?,
      distribution_month_index: Long?,
      distribution_total_months: Long?,
      is_verified: Long,
      verified_at: String?,
      is_excluded: Long,
      notified_at: String?,
    ) -> T,
  ): Query<T> = SelectTransactionsByCategoryQuery(user_id, personal_finance_category_primary) {
      cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun selectTransactionsByCategory(user_id: String,
      personal_finance_category_primary: String?): Query<Transactions> =
      selectTransactionsByCategory(user_id, personal_finance_category_primary) { id, transaction_id,
      account_id, user_id_, amount, iso_currency_code, unofficial_currency_code, date,
      authorized_date, authorized_datetime, datetime, name, merchant_name, original_description,
      category, category_id, personal_finance_category_primary_, personal_finance_category_detailed,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source, parent_transaction_id,
      child_type, is_split_parent, is_distributed_parent, split_order, distribution_start_month,
      distribution_end_month, distribution_month_index, distribution_total_months, is_verified,
      verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id,
      user_id_,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary_,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> selectTransactionById(transaction_id: String, mapper: (
    id: Long,
    transaction_id: String,
    account_id: String,
    user_id: String,
    amount: Double,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    date: String,
    authorized_date: String?,
    authorized_datetime: String?,
    datetime: String?,
    name: String,
    merchant_name: String?,
    original_description: String?,
    category: String?,
    category_id: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    personal_finance_category_confidence: String?,
    transaction_type: String?,
    transaction_code: String?,
    pending: Long,
    pending_transaction_id: String?,
    location_json: String?,
    payment_meta_json: String?,
    account_owner: String?,
    created_at: String,
    updated_at: String,
    last_synced_at: String?,
    user_category: String?,
    embedding: ByteArray?,
    embedding_version: Long?,
    user_notes: String?,
    is_hidden: Long,
    is_expense_report: Long,
    tags: String?,
    ai_category: String?,
    confidence_score: Double?,
    original_ai_category: String?,
    corrected_at: String?,
    correction_confidence: Double?,
    ml_model_version: String?,
    category_source: String?,
    parent_transaction_id: String?,
    child_type: String?,
    is_split_parent: Long,
    is_distributed_parent: Long,
    split_order: Long?,
    distribution_start_month: String?,
    distribution_end_month: String?,
    distribution_month_index: Long?,
    distribution_total_months: Long?,
    is_verified: Long,
    verified_at: String?,
    is_excluded: Long,
    notified_at: String?,
  ) -> T): Query<T> = SelectTransactionByIdQuery(transaction_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun selectTransactionById(transaction_id: String): Query<Transactions> =
      selectTransactionById(transaction_id) { id, transaction_id_, account_id, user_id, amount,
      iso_currency_code, unofficial_currency_code, date, authorized_date, authorized_datetime,
      datetime, name, merchant_name, original_description, category, category_id,
      personal_finance_category_primary, personal_finance_category_detailed,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source, parent_transaction_id,
      child_type, is_split_parent, is_distributed_parent, split_order, distribution_start_month,
      distribution_end_month, distribution_month_index, distribution_total_months, is_verified,
      verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id_,
      account_id,
      user_id,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> selectUserOverridesByUserId(user_id: String, mapper: (
    transaction_id: String,
    user_category: String?,
    category_source: String?,
    user_notes: String?,
    tags: String?,
  ) -> T): Query<T> = SelectUserOverridesByUserIdQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1),
      cursor.getString(2),
      cursor.getString(3),
      cursor.getString(4)
    )
  }

  public fun selectUserOverridesByUserId(user_id: String): Query<SelectUserOverridesByUserId> =
      selectUserOverridesByUserId(user_id) { transaction_id, user_category, category_source,
      user_notes, tags ->
    SelectUserOverridesByUserId(
      transaction_id,
      user_category,
      category_source,
      user_notes,
      tags
    )
  }

  public fun <T : Any> selectVerifiedTransactionsByUserId(user_id: String, mapper: (
    transaction_id: String,
    is_verified: Long,
    verified_at: String?,
  ) -> T): Query<T> = SelectVerifiedTransactionsByUserIdQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getLong(1)!!,
      cursor.getString(2)
    )
  }

  public fun selectVerifiedTransactionsByUserId(user_id: String):
      Query<SelectVerifiedTransactionsByUserId> = selectVerifiedTransactionsByUserId(user_id) {
      transaction_id, is_verified, verified_at ->
    SelectVerifiedTransactionsByUserId(
      transaction_id,
      is_verified,
      verified_at
    )
  }

  public fun <T : Any> selectTransactionsWithEmbeddings(user_id: String, mapper: (
    transaction_id: String,
    name: String,
    merchant_name: String?,
    personal_finance_category_primary: String?,
    embedding: ByteArray,
  ) -> T): Query<T> = SelectTransactionsWithEmbeddingsQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getString(3),
      cursor.getBytes(4)!!
    )
  }

  public fun selectTransactionsWithEmbeddings(user_id: String):
      Query<SelectTransactionsWithEmbeddings> = selectTransactionsWithEmbeddings(user_id) {
      transaction_id, name, merchant_name, personal_finance_category_primary, embedding ->
    SelectTransactionsWithEmbeddings(
      transaction_id,
      name,
      merchant_name,
      personal_finance_category_primary,
      embedding
    )
  }

  public fun <T : Any> previewCategoryPropagation(
    user_id: String,
    transaction_id: String,
    merchant_name: String?,
    name: String,
    mapper: (
      transaction_id: String,
      name: String,
      merchant_name: String?,
      amount: Double,
      date: String,
      personal_finance_category_detailed: String?,
    ) -> T,
  ): Query<T> = PreviewCategoryPropagationQuery(user_id, transaction_id, merchant_name, name) {
      cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getDouble(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)
    )
  }

  public fun previewCategoryPropagation(
    user_id: String,
    transaction_id: String,
    merchant_name: String?,
    name: String,
  ): Query<PreviewCategoryPropagation> = previewCategoryPropagation(user_id, transaction_id,
      merchant_name, name) { transaction_id_, name_, merchant_name_, amount, date,
      personal_finance_category_detailed ->
    PreviewCategoryPropagation(
      transaction_id_,
      name_,
      merchant_name_,
      amount,
      date,
      personal_finance_category_detailed
    )
  }

  public fun <T : Any> selectPendingHoldCandidates(user_id: String, mapper: (
    transaction_id: String,
    account_id: String,
    amount: Double,
    name: String,
    date: String,
    is_hidden: Long,
    pending: Long,
  ) -> T): Query<T> = SelectPendingHoldCandidatesQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getDouble(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getLong(5)!!,
      cursor.getLong(6)!!
    )
  }

  public fun selectPendingHoldCandidates(user_id: String): Query<SelectPendingHoldCandidates> =
      selectPendingHoldCandidates(user_id) { transaction_id, account_id, amount, name, date,
      is_hidden, pending ->
    SelectPendingHoldCandidates(
      transaction_id,
      account_id,
      amount,
      name,
      date,
      is_hidden,
      pending
    )
  }

  public fun <T : Any> selectHiddenHoldLegs(user_id: String, mapper: (
    transaction_id: String,
    account_id: String,
    amount: Double,
    name: String,
    date: String,
    is_hidden: Long,
    pending: Long,
  ) -> T): Query<T> = SelectHiddenHoldLegsQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getDouble(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getLong(5)!!,
      cursor.getLong(6)!!
    )
  }

  public fun selectHiddenHoldLegs(user_id: String): Query<SelectHiddenHoldLegs> =
      selectHiddenHoldLegs(user_id) { transaction_id, account_id, amount, name, date, is_hidden,
      pending ->
    SelectHiddenHoldLegs(
      transaction_id,
      account_id,
      amount,
      name,
      date,
      is_hidden,
      pending
    )
  }

  public fun <T : Any> selectCardPaymentCandidates(user_id: String, mapper: (
    transaction_id: String,
    account_id: String,
    amount: Double,
    date: String,
    is_excluded: Long,
    account_type: String,
  ) -> T): Query<T> = SelectCardPaymentCandidatesQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getDouble(2)!!,
      cursor.getString(3)!!,
      cursor.getLong(4)!!,
      cursor.getString(5)!!
    )
  }

  public fun selectCardPaymentCandidates(user_id: String): Query<SelectCardPaymentCandidates> =
      selectCardPaymentCandidates(user_id) { transaction_id, account_id, amount, date, is_excluded,
      account_type ->
    SelectCardPaymentCandidates(
      transaction_id,
      account_id,
      amount,
      date,
      is_excluded,
      account_type
    )
  }

  public fun <T : Any> selectMerchantCategoryPreferences(user_id: String,
      mapper: (merchant_key: String, user_category: String) -> T): Query<T> =
      SelectMerchantCategoryPreferencesQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!
    )
  }

  public fun selectMerchantCategoryPreferences(user_id: String):
      Query<SelectMerchantCategoryPreferences> = selectMerchantCategoryPreferences(user_id) {
      merchant_key, user_category ->
    SelectMerchantCategoryPreferences(
      merchant_key,
      user_category
    )
  }

  public fun <T : Any> selectUnverifiedByCategory(
    user_id: String,
    personal_finance_category_detailed: String?,
    mapper: (
      id: Long,
      transaction_id: String,
      account_id: String,
      user_id: String,
      amount: Double,
      iso_currency_code: String?,
      unofficial_currency_code: String?,
      date: String,
      authorized_date: String?,
      authorized_datetime: String?,
      datetime: String?,
      name: String,
      merchant_name: String?,
      original_description: String?,
      category: String?,
      category_id: String?,
      personal_finance_category_primary: String?,
      personal_finance_category_detailed: String?,
      personal_finance_category_confidence: String?,
      transaction_type: String?,
      transaction_code: String?,
      pending: Long,
      pending_transaction_id: String?,
      location_json: String?,
      payment_meta_json: String?,
      account_owner: String?,
      created_at: String,
      updated_at: String,
      last_synced_at: String?,
      user_category: String?,
      embedding: ByteArray?,
      embedding_version: Long?,
      user_notes: String?,
      is_hidden: Long,
      is_expense_report: Long,
      tags: String?,
      ai_category: String?,
      confidence_score: Double?,
      original_ai_category: String?,
      corrected_at: String?,
      correction_confidence: Double?,
      ml_model_version: String?,
      category_source: String?,
      parent_transaction_id: String?,
      child_type: String?,
      is_split_parent: Long,
      is_distributed_parent: Long,
      split_order: Long?,
      distribution_start_month: String?,
      distribution_end_month: String?,
      distribution_month_index: Long?,
      distribution_total_months: Long?,
      is_verified: Long,
      verified_at: String?,
      is_excluded: Long,
      notified_at: String?,
    ) -> T,
  ): Query<T> = SelectUnverifiedByCategoryQuery(user_id, personal_finance_category_detailed) {
      cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun selectUnverifiedByCategory(user_id: String,
      personal_finance_category_detailed: String?): Query<Transactions> =
      selectUnverifiedByCategory(user_id, personal_finance_category_detailed) { id, transaction_id,
      account_id, user_id_, amount, iso_currency_code, unofficial_currency_code, date,
      authorized_date, authorized_datetime, datetime, name, merchant_name, original_description,
      category, category_id, personal_finance_category_primary, personal_finance_category_detailed_,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source, parent_transaction_id,
      child_type, is_split_parent, is_distributed_parent, split_order, distribution_start_month,
      distribution_end_month, distribution_month_index, distribution_total_months, is_verified,
      verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id,
      user_id_,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed_,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> selectUncategorizedTransactions(
    user_id: String,
    `value`: Long,
    mapper: (
      id: Long,
      transaction_id: String,
      account_id: String,
      user_id: String,
      amount: Double,
      iso_currency_code: String?,
      unofficial_currency_code: String?,
      date: String,
      authorized_date: String?,
      authorized_datetime: String?,
      datetime: String?,
      name: String,
      merchant_name: String?,
      original_description: String?,
      category: String?,
      category_id: String?,
      personal_finance_category_primary: String?,
      personal_finance_category_detailed: String?,
      personal_finance_category_confidence: String?,
      transaction_type: String?,
      transaction_code: String?,
      pending: Long,
      pending_transaction_id: String?,
      location_json: String?,
      payment_meta_json: String?,
      account_owner: String?,
      created_at: String,
      updated_at: String,
      last_synced_at: String?,
      user_category: String?,
      embedding: ByteArray?,
      embedding_version: Long?,
      user_notes: String?,
      is_hidden: Long,
      is_expense_report: Long,
      tags: String?,
      ai_category: String?,
      confidence_score: Double?,
      original_ai_category: String?,
      corrected_at: String?,
      correction_confidence: Double?,
      ml_model_version: String?,
      category_source: String?,
      parent_transaction_id: String?,
      child_type: String?,
      is_split_parent: Long,
      is_distributed_parent: Long,
      split_order: Long?,
      distribution_start_month: String?,
      distribution_end_month: String?,
      distribution_month_index: Long?,
      distribution_total_months: Long?,
      is_verified: Long,
      verified_at: String?,
      is_excluded: Long,
      notified_at: String?,
    ) -> T,
  ): Query<T> = SelectUncategorizedTransactionsQuery(user_id, value) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun selectUncategorizedTransactions(user_id: String, value_: Long): Query<Transactions> =
      selectUncategorizedTransactions(user_id, value_) { id, transaction_id, account_id, user_id_,
      amount, iso_currency_code, unofficial_currency_code, date, authorized_date,
      authorized_datetime, datetime, name, merchant_name, original_description, category,
      category_id, personal_finance_category_primary, personal_finance_category_detailed,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source, parent_transaction_id,
      child_type, is_split_parent, is_distributed_parent, split_order, distribution_start_month,
      distribution_end_month, distribution_month_index, distribution_total_months, is_verified,
      verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id,
      user_id_,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> selectLowConfidencePredictions(
    user_id: String,
    confidence_score: Double?,
    `value`: Long,
    mapper: (
      id: Long,
      transaction_id: String,
      account_id: String,
      user_id: String,
      amount: Double,
      iso_currency_code: String?,
      unofficial_currency_code: String?,
      date: String,
      authorized_date: String?,
      authorized_datetime: String?,
      datetime: String?,
      name: String,
      merchant_name: String?,
      original_description: String?,
      category: String?,
      category_id: String?,
      personal_finance_category_primary: String?,
      personal_finance_category_detailed: String?,
      personal_finance_category_confidence: String?,
      transaction_type: String?,
      transaction_code: String?,
      pending: Long,
      pending_transaction_id: String?,
      location_json: String?,
      payment_meta_json: String?,
      account_owner: String?,
      created_at: String,
      updated_at: String,
      last_synced_at: String?,
      user_category: String?,
      embedding: ByteArray?,
      embedding_version: Long?,
      user_notes: String?,
      is_hidden: Long,
      is_expense_report: Long,
      tags: String?,
      ai_category: String,
      confidence_score: Double?,
      original_ai_category: String?,
      corrected_at: String?,
      correction_confidence: Double?,
      ml_model_version: String?,
      category_source: String?,
      parent_transaction_id: String?,
      child_type: String?,
      is_split_parent: Long,
      is_distributed_parent: Long,
      split_order: Long?,
      distribution_start_month: String?,
      distribution_end_month: String?,
      distribution_month_index: Long?,
      distribution_total_months: Long?,
      is_verified: Long,
      verified_at: String?,
      is_excluded: Long,
      notified_at: String?,
    ) -> T,
  ): Query<T> = SelectLowConfidencePredictionsQuery(user_id, confidence_score, value) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36)!!,
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun selectLowConfidencePredictions(
    user_id: String,
    confidence_score: Double?,
    value_: Long,
  ): Query<SelectLowConfidencePredictions> = selectLowConfidencePredictions(user_id,
      confidence_score, value_) { id, transaction_id, account_id, user_id_, amount,
      iso_currency_code, unofficial_currency_code, date, authorized_date, authorized_datetime,
      datetime, name, merchant_name, original_description, category, category_id,
      personal_finance_category_primary, personal_finance_category_detailed,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score_, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source, parent_transaction_id,
      child_type, is_split_parent, is_distributed_parent, split_order, distribution_start_month,
      distribution_end_month, distribution_month_index, distribution_total_months, is_verified,
      verified_at, is_excluded, notified_at ->
    SelectLowConfidencePredictions(
      id,
      transaction_id,
      account_id,
      user_id_,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score_,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> selectTransactionsByAICategory(
    user_id: String,
    ai_category: String?,
    mapper: (
      id: Long,
      transaction_id: String,
      account_id: String,
      user_id: String,
      amount: Double,
      iso_currency_code: String?,
      unofficial_currency_code: String?,
      date: String,
      authorized_date: String?,
      authorized_datetime: String?,
      datetime: String?,
      name: String,
      merchant_name: String?,
      original_description: String?,
      category: String?,
      category_id: String?,
      personal_finance_category_primary: String?,
      personal_finance_category_detailed: String?,
      personal_finance_category_confidence: String?,
      transaction_type: String?,
      transaction_code: String?,
      pending: Long,
      pending_transaction_id: String?,
      location_json: String?,
      payment_meta_json: String?,
      account_owner: String?,
      created_at: String,
      updated_at: String,
      last_synced_at: String?,
      user_category: String?,
      embedding: ByteArray?,
      embedding_version: Long?,
      user_notes: String?,
      is_hidden: Long,
      is_expense_report: Long,
      tags: String?,
      ai_category: String?,
      confidence_score: Double?,
      original_ai_category: String?,
      corrected_at: String?,
      correction_confidence: Double?,
      ml_model_version: String?,
      category_source: String?,
      parent_transaction_id: String?,
      child_type: String?,
      is_split_parent: Long,
      is_distributed_parent: Long,
      split_order: Long?,
      distribution_start_month: String?,
      distribution_end_month: String?,
      distribution_month_index: Long?,
      distribution_total_months: Long?,
      is_verified: Long,
      verified_at: String?,
      is_excluded: Long,
      notified_at: String?,
    ) -> T,
  ): Query<T> = SelectTransactionsByAICategoryQuery(user_id, ai_category) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun selectTransactionsByAICategory(user_id: String, ai_category: String?):
      Query<Transactions> = selectTransactionsByAICategory(user_id, ai_category) { id,
      transaction_id, account_id, user_id_, amount, iso_currency_code, unofficial_currency_code,
      date, authorized_date, authorized_datetime, datetime, name, merchant_name,
      original_description, category, category_id, personal_finance_category_primary,
      personal_finance_category_detailed, personal_finance_category_confidence, transaction_type,
      transaction_code, pending, pending_transaction_id, location_json, payment_meta_json,
      account_owner, created_at, updated_at, last_synced_at, user_category, embedding,
      embedding_version, user_notes, is_hidden, is_expense_report, tags, ai_category_,
      confidence_score, original_ai_category, corrected_at, correction_confidence, ml_model_version,
      category_source, parent_transaction_id, child_type, is_split_parent, is_distributed_parent,
      split_order, distribution_start_month, distribution_end_month, distribution_month_index,
      distribution_total_months, is_verified, verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id,
      user_id_,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category_,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> getCorrectionStats(user_id: String, mapper: (
    original_ai_category: String,
    user_category: String?,
    correction_count: Long,
    avg_original_confidence: Double?,
  ) -> T): Query<T> = GetCorrectionStatsQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1),
      cursor.getLong(2)!!,
      cursor.getDouble(3)
    )
  }

  public fun getCorrectionStats(user_id: String): Query<GetCorrectionStats> =
      getCorrectionStats(user_id) { original_ai_category, user_category, correction_count,
      avg_original_confidence ->
    GetCorrectionStats(
      original_ai_category,
      user_category,
      correction_count,
      avg_original_confidence
    )
  }

  public fun findMatchingPendingTransaction(
    accountId: String,
    amount: Double,
    postedDate: String,
  ): Query<String> = FindMatchingPendingTransactionQuery(accountId, amount, postedDate) { cursor ->
    cursor.getString(0)!!
  }

  public fun getTransactionCount(user_id: String): Query<Long> = GetTransactionCountQuery(user_id) {
      cursor ->
    cursor.getLong(0)!!
  }

  public fun <T : Any> getRecentTransactions(
    user_id: String,
    `value`: Long,
    mapper: (
      id: Long,
      transaction_id: String,
      account_id: String,
      user_id: String,
      amount: Double,
      iso_currency_code: String?,
      unofficial_currency_code: String?,
      date: String,
      authorized_date: String?,
      authorized_datetime: String?,
      datetime: String?,
      name: String,
      merchant_name: String?,
      original_description: String?,
      category: String?,
      category_id: String?,
      personal_finance_category_primary: String?,
      personal_finance_category_detailed: String?,
      personal_finance_category_confidence: String?,
      transaction_type: String?,
      transaction_code: String?,
      pending: Long,
      pending_transaction_id: String?,
      location_json: String?,
      payment_meta_json: String?,
      account_owner: String?,
      created_at: String,
      updated_at: String,
      last_synced_at: String?,
      user_category: String?,
      embedding: ByteArray?,
      embedding_version: Long?,
      user_notes: String?,
      is_hidden: Long,
      is_expense_report: Long,
      tags: String?,
      ai_category: String?,
      confidence_score: Double?,
      original_ai_category: String?,
      corrected_at: String?,
      correction_confidence: Double?,
      ml_model_version: String?,
      category_source: String?,
      parent_transaction_id: String?,
      child_type: String?,
      is_split_parent: Long,
      is_distributed_parent: Long,
      split_order: Long?,
      distribution_start_month: String?,
      distribution_end_month: String?,
      distribution_month_index: Long?,
      distribution_total_months: Long?,
      is_verified: Long,
      verified_at: String?,
      is_excluded: Long,
      notified_at: String?,
    ) -> T,
  ): Query<T> = GetRecentTransactionsQuery(user_id, value) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun getRecentTransactions(user_id: String, value_: Long): Query<Transactions> =
      getRecentTransactions(user_id, value_) { id, transaction_id, account_id, user_id_, amount,
      iso_currency_code, unofficial_currency_code, date, authorized_date, authorized_datetime,
      datetime, name, merchant_name, original_description, category, category_id,
      personal_finance_category_primary, personal_finance_category_detailed,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source, parent_transaction_id,
      child_type, is_split_parent, is_distributed_parent, split_order, distribution_start_month,
      distribution_end_month, distribution_month_index, distribution_total_months, is_verified,
      verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id,
      user_id_,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> searchTransactions(
    user_id: String,
    name: String,
    merchant_name: String,
    `value`: Long,
    mapper: (
      id: Long,
      transaction_id: String,
      account_id: String,
      user_id: String,
      amount: Double,
      iso_currency_code: String?,
      unofficial_currency_code: String?,
      date: String,
      authorized_date: String?,
      authorized_datetime: String?,
      datetime: String?,
      name: String,
      merchant_name: String?,
      original_description: String?,
      category: String?,
      category_id: String?,
      personal_finance_category_primary: String?,
      personal_finance_category_detailed: String?,
      personal_finance_category_confidence: String?,
      transaction_type: String?,
      transaction_code: String?,
      pending: Long,
      pending_transaction_id: String?,
      location_json: String?,
      payment_meta_json: String?,
      account_owner: String?,
      created_at: String,
      updated_at: String,
      last_synced_at: String?,
      user_category: String?,
      embedding: ByteArray?,
      embedding_version: Long?,
      user_notes: String?,
      is_hidden: Long,
      is_expense_report: Long,
      tags: String?,
      ai_category: String?,
      confidence_score: Double?,
      original_ai_category: String?,
      corrected_at: String?,
      correction_confidence: Double?,
      ml_model_version: String?,
      category_source: String?,
      parent_transaction_id: String?,
      child_type: String?,
      is_split_parent: Long,
      is_distributed_parent: Long,
      split_order: Long?,
      distribution_start_month: String?,
      distribution_end_month: String?,
      distribution_month_index: Long?,
      distribution_total_months: Long?,
      is_verified: Long,
      verified_at: String?,
      is_excluded: Long,
      notified_at: String?,
    ) -> T,
  ): Query<T> = SearchTransactionsQuery(user_id, name, merchant_name, value) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun searchTransactions(
    user_id: String,
    name: String,
    merchant_name: String,
    value_: Long,
  ): Query<Transactions> = searchTransactions(user_id, name, merchant_name, value_) { id,
      transaction_id, account_id, user_id_, amount, iso_currency_code, unofficial_currency_code,
      date, authorized_date, authorized_datetime, datetime, name_, merchant_name_,
      original_description, category, category_id, personal_finance_category_primary,
      personal_finance_category_detailed, personal_finance_category_confidence, transaction_type,
      transaction_code, pending, pending_transaction_id, location_json, payment_meta_json,
      account_owner, created_at, updated_at, last_synced_at, user_category, embedding,
      embedding_version, user_notes, is_hidden, is_expense_report, tags, ai_category,
      confidence_score, original_ai_category, corrected_at, correction_confidence, ml_model_version,
      category_source, parent_transaction_id, child_type, is_split_parent, is_distributed_parent,
      split_order, distribution_start_month, distribution_end_month, distribution_month_index,
      distribution_total_months, is_verified, verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id,
      user_id_,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name_,
      merchant_name_,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> selectTransactionsByMerchant(
    user_id: String,
    merchant_name: String?,
    name: String,
    mapper: (
      id: Long,
      transaction_id: String,
      account_id: String,
      user_id: String,
      amount: Double,
      iso_currency_code: String?,
      unofficial_currency_code: String?,
      date: String,
      authorized_date: String?,
      authorized_datetime: String?,
      datetime: String?,
      name: String,
      merchant_name: String?,
      original_description: String?,
      category: String?,
      category_id: String?,
      personal_finance_category_primary: String?,
      personal_finance_category_detailed: String?,
      personal_finance_category_confidence: String?,
      transaction_type: String?,
      transaction_code: String?,
      pending: Long,
      pending_transaction_id: String?,
      location_json: String?,
      payment_meta_json: String?,
      account_owner: String?,
      created_at: String,
      updated_at: String,
      last_synced_at: String?,
      user_category: String?,
      embedding: ByteArray?,
      embedding_version: Long?,
      user_notes: String?,
      is_hidden: Long,
      is_expense_report: Long,
      tags: String?,
      ai_category: String?,
      confidence_score: Double?,
      original_ai_category: String?,
      corrected_at: String?,
      correction_confidence: Double?,
      ml_model_version: String?,
      category_source: String?,
      parent_transaction_id: String?,
      child_type: String?,
      is_split_parent: Long,
      is_distributed_parent: Long,
      split_order: Long?,
      distribution_start_month: String?,
      distribution_end_month: String?,
      distribution_month_index: Long?,
      distribution_total_months: Long?,
      is_verified: Long,
      verified_at: String?,
      is_excluded: Long,
      notified_at: String?,
    ) -> T,
  ): Query<T> = SelectTransactionsByMerchantQuery(user_id, merchant_name, name) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun selectTransactionsByMerchant(
    user_id: String,
    merchant_name: String?,
    name: String,
  ): Query<Transactions> = selectTransactionsByMerchant(user_id, merchant_name, name) { id,
      transaction_id, account_id, user_id_, amount, iso_currency_code, unofficial_currency_code,
      date, authorized_date, authorized_datetime, datetime, name_, merchant_name_,
      original_description, category, category_id, personal_finance_category_primary,
      personal_finance_category_detailed, personal_finance_category_confidence, transaction_type,
      transaction_code, pending, pending_transaction_id, location_json, payment_meta_json,
      account_owner, created_at, updated_at, last_synced_at, user_category, embedding,
      embedding_version, user_notes, is_hidden, is_expense_report, tags, ai_category,
      confidence_score, original_ai_category, corrected_at, correction_confidence, ml_model_version,
      category_source, parent_transaction_id, child_type, is_split_parent, is_distributed_parent,
      split_order, distribution_start_month, distribution_end_month, distribution_month_index,
      distribution_total_months, is_verified, verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id,
      user_id_,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name_,
      merchant_name_,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> getChildTransactions(parent_transaction_id: String?, mapper: (
    id: Long,
    transaction_id: String,
    account_id: String,
    user_id: String,
    amount: Double,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    date: String,
    authorized_date: String?,
    authorized_datetime: String?,
    datetime: String?,
    name: String,
    merchant_name: String?,
    original_description: String?,
    category: String?,
    category_id: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    personal_finance_category_confidence: String?,
    transaction_type: String?,
    transaction_code: String?,
    pending: Long,
    pending_transaction_id: String?,
    location_json: String?,
    payment_meta_json: String?,
    account_owner: String?,
    created_at: String,
    updated_at: String,
    last_synced_at: String?,
    user_category: String?,
    embedding: ByteArray?,
    embedding_version: Long?,
    user_notes: String?,
    is_hidden: Long,
    is_expense_report: Long,
    tags: String?,
    ai_category: String?,
    confidence_score: Double?,
    original_ai_category: String?,
    corrected_at: String?,
    correction_confidence: Double?,
    ml_model_version: String?,
    category_source: String?,
    parent_transaction_id: String?,
    child_type: String?,
    is_split_parent: Long,
    is_distributed_parent: Long,
    split_order: Long?,
    distribution_start_month: String?,
    distribution_end_month: String?,
    distribution_month_index: Long?,
    distribution_total_months: Long?,
    is_verified: Long,
    verified_at: String?,
    is_excluded: Long,
    notified_at: String?,
  ) -> T): Query<T> = GetChildTransactionsQuery(parent_transaction_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun getChildTransactions(parent_transaction_id: String?): Query<Transactions> =
      getChildTransactions(parent_transaction_id) { id, transaction_id, account_id, user_id, amount,
      iso_currency_code, unofficial_currency_code, date, authorized_date, authorized_datetime,
      datetime, name, merchant_name, original_description, category, category_id,
      personal_finance_category_primary, personal_finance_category_detailed,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source,
      parent_transaction_id_, child_type, is_split_parent, is_distributed_parent, split_order,
      distribution_start_month, distribution_end_month, distribution_month_index,
      distribution_total_months, is_verified, verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id,
      user_id,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id_,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> getSplitChildren(parent_transaction_id: String?, mapper: (
    id: Long,
    transaction_id: String,
    account_id: String,
    user_id: String,
    amount: Double,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    date: String,
    authorized_date: String?,
    authorized_datetime: String?,
    datetime: String?,
    name: String,
    merchant_name: String?,
    original_description: String?,
    category: String?,
    category_id: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    personal_finance_category_confidence: String?,
    transaction_type: String?,
    transaction_code: String?,
    pending: Long,
    pending_transaction_id: String?,
    location_json: String?,
    payment_meta_json: String?,
    account_owner: String?,
    created_at: String,
    updated_at: String,
    last_synced_at: String?,
    user_category: String?,
    embedding: ByteArray?,
    embedding_version: Long?,
    user_notes: String?,
    is_hidden: Long,
    is_expense_report: Long,
    tags: String?,
    ai_category: String?,
    confidence_score: Double?,
    original_ai_category: String?,
    corrected_at: String?,
    correction_confidence: Double?,
    ml_model_version: String?,
    category_source: String?,
    parent_transaction_id: String?,
    child_type: String?,
    is_split_parent: Long,
    is_distributed_parent: Long,
    split_order: Long?,
    distribution_start_month: String?,
    distribution_end_month: String?,
    distribution_month_index: Long?,
    distribution_total_months: Long?,
    is_verified: Long,
    verified_at: String?,
    is_excluded: Long,
    notified_at: String?,
  ) -> T): Query<T> = GetSplitChildrenQuery(parent_transaction_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun getSplitChildren(parent_transaction_id: String?): Query<Transactions> =
      getSplitChildren(parent_transaction_id) { id, transaction_id, account_id, user_id, amount,
      iso_currency_code, unofficial_currency_code, date, authorized_date, authorized_datetime,
      datetime, name, merchant_name, original_description, category, category_id,
      personal_finance_category_primary, personal_finance_category_detailed,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source,
      parent_transaction_id_, child_type, is_split_parent, is_distributed_parent, split_order,
      distribution_start_month, distribution_end_month, distribution_month_index,
      distribution_total_months, is_verified, verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id,
      user_id,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id_,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> getDistributedChildren(parent_transaction_id: String?, mapper: (
    id: Long,
    transaction_id: String,
    account_id: String,
    user_id: String,
    amount: Double,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    date: String,
    authorized_date: String?,
    authorized_datetime: String?,
    datetime: String?,
    name: String,
    merchant_name: String?,
    original_description: String?,
    category: String?,
    category_id: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    personal_finance_category_confidence: String?,
    transaction_type: String?,
    transaction_code: String?,
    pending: Long,
    pending_transaction_id: String?,
    location_json: String?,
    payment_meta_json: String?,
    account_owner: String?,
    created_at: String,
    updated_at: String,
    last_synced_at: String?,
    user_category: String?,
    embedding: ByteArray?,
    embedding_version: Long?,
    user_notes: String?,
    is_hidden: Long,
    is_expense_report: Long,
    tags: String?,
    ai_category: String?,
    confidence_score: Double?,
    original_ai_category: String?,
    corrected_at: String?,
    correction_confidence: Double?,
    ml_model_version: String?,
    category_source: String?,
    parent_transaction_id: String?,
    child_type: String?,
    is_split_parent: Long,
    is_distributed_parent: Long,
    split_order: Long?,
    distribution_start_month: String?,
    distribution_end_month: String?,
    distribution_month_index: Long?,
    distribution_total_months: Long?,
    is_verified: Long,
    verified_at: String?,
    is_excluded: Long,
    notified_at: String?,
  ) -> T): Query<T> = GetDistributedChildrenQuery(parent_transaction_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun getDistributedChildren(parent_transaction_id: String?): Query<Transactions> =
      getDistributedChildren(parent_transaction_id) { id, transaction_id, account_id, user_id,
      amount, iso_currency_code, unofficial_currency_code, date, authorized_date,
      authorized_datetime, datetime, name, merchant_name, original_description, category,
      category_id, personal_finance_category_primary, personal_finance_category_detailed,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source,
      parent_transaction_id_, child_type, is_split_parent, is_distributed_parent, split_order,
      distribution_start_month, distribution_end_month, distribution_month_index,
      distribution_total_months, is_verified, verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id,
      user_id,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id_,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun <T : Any> getParentTransactions(user_id: String, mapper: (
    id: Long,
    transaction_id: String,
    account_id: String,
    user_id: String,
    amount: Double,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    date: String,
    authorized_date: String?,
    authorized_datetime: String?,
    datetime: String?,
    name: String,
    merchant_name: String?,
    original_description: String?,
    category: String?,
    category_id: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    personal_finance_category_confidence: String?,
    transaction_type: String?,
    transaction_code: String?,
    pending: Long,
    pending_transaction_id: String?,
    location_json: String?,
    payment_meta_json: String?,
    account_owner: String?,
    created_at: String,
    updated_at: String,
    last_synced_at: String?,
    user_category: String?,
    embedding: ByteArray?,
    embedding_version: Long?,
    user_notes: String?,
    is_hidden: Long,
    is_expense_report: Long,
    tags: String?,
    ai_category: String?,
    confidence_score: Double?,
    original_ai_category: String?,
    corrected_at: String?,
    correction_confidence: Double?,
    ml_model_version: String?,
    category_source: String?,
    parent_transaction_id: String?,
    child_type: String?,
    is_split_parent: Long,
    is_distributed_parent: Long,
    split_order: Long?,
    distribution_start_month: String?,
    distribution_end_month: String?,
    distribution_month_index: Long?,
    distribution_total_months: Long?,
    is_verified: Long,
    verified_at: String?,
    is_excluded: Long,
    notified_at: String?,
  ) -> T): Query<T> = GetParentTransactionsQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun getParentTransactions(user_id: String): Query<Transactions> =
      getParentTransactions(user_id) { id, transaction_id, account_id, user_id_, amount,
      iso_currency_code, unofficial_currency_code, date, authorized_date, authorized_datetime,
      datetime, name, merchant_name, original_description, category, category_id,
      personal_finance_category_primary, personal_finance_category_detailed,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source, parent_transaction_id,
      child_type, is_split_parent, is_distributed_parent, split_order, distribution_start_month,
      distribution_end_month, distribution_month_index, distribution_total_months, is_verified,
      verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id,
      user_id_,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun selectSplitParentsByUser(user_id: String): Query<String> =
      SelectSplitParentsByUserQuery(user_id) { cursor ->
    cursor.getString(0)!!
  }

  public fun <T : Any> selectDistributedParentsByUser(user_id: String, mapper: (
    transaction_id: String,
    distribution_start_month: String?,
    distribution_end_month: String?,
  ) -> T): Query<T> = SelectDistributedParentsByUserQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1),
      cursor.getString(2)
    )
  }

  public fun selectDistributedParentsByUser(user_id: String): Query<SelectDistributedParentsByUser>
      = selectDistributedParentsByUser(user_id) { transaction_id, distribution_start_month,
      distribution_end_month ->
    SelectDistributedParentsByUser(
      transaction_id,
      distribution_start_month,
      distribution_end_month
    )
  }

  public fun selectExcludedByUser(user_id: String): Query<String> =
      SelectExcludedByUserQuery(user_id) { cursor ->
    cursor.getString(0)!!
  }

  public fun findDuplicateTransaction(
    user_id: String,
    name: String,
    amount: Double,
    transaction_id: String,
    `value`: String,
    value_: String,
  ): Query<String> = FindDuplicateTransactionQuery(user_id, name, amount, transaction_id, value,
      value_) { cursor ->
    cursor.getString(0)!!
  }

  public fun hasChildTransactions(parent_transaction_id: String?): Query<Long> =
      HasChildTransactionsQuery(parent_transaction_id) { cursor ->
    cursor.getLong(0)!!
  }

  public fun <T : Any> selectAccountsByUser(user_id: String, mapper: (
    id: Long,
    account_id: String,
    user_id: String,
    item_id: String,
    name: String,
    official_name: String?,
    mask: String?,
    type: String,
    subtype: String?,
    balance_available: Double?,
    balance_current: Double?,
    balance_limit: Double?,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    balance_last_updated: String?,
    verification_status: String?,
    persistent_account_id: String?,
    created_at: String,
    updated_at: String,
    last_synced_at: String?,
    status: String,
    custom_name: String?,
  ) -> T): Query<T> = SelectAccountsByUserQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getDouble(9),
      cursor.getDouble(10),
      cursor.getDouble(11),
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17)!!,
      cursor.getString(18)!!,
      cursor.getString(19),
      cursor.getString(20)!!,
      cursor.getString(21)
    )
  }

  public fun selectAccountsByUser(user_id: String): Query<Accounts> =
      selectAccountsByUser(user_id) { id, account_id, user_id_, item_id, name, official_name, mask,
      type, subtype, balance_available, balance_current, balance_limit, iso_currency_code,
      unofficial_currency_code, balance_last_updated, verification_status, persistent_account_id,
      created_at, updated_at, last_synced_at, status, custom_name ->
    Accounts(
      id,
      account_id,
      user_id_,
      item_id,
      name,
      official_name,
      mask,
      type,
      subtype,
      balance_available,
      balance_current,
      balance_limit,
      iso_currency_code,
      unofficial_currency_code,
      balance_last_updated,
      verification_status,
      persistent_account_id,
      created_at,
      updated_at,
      last_synced_at,
      status,
      custom_name
    )
  }

  public fun <T : Any> selectAllAccountsByUser(user_id: String, mapper: (
    id: Long,
    account_id: String,
    user_id: String,
    item_id: String,
    name: String,
    official_name: String?,
    mask: String?,
    type: String,
    subtype: String?,
    balance_available: Double?,
    balance_current: Double?,
    balance_limit: Double?,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    balance_last_updated: String?,
    verification_status: String?,
    persistent_account_id: String?,
    created_at: String,
    updated_at: String,
    last_synced_at: String?,
    status: String,
    custom_name: String?,
  ) -> T): Query<T> = SelectAllAccountsByUserQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getDouble(9),
      cursor.getDouble(10),
      cursor.getDouble(11),
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17)!!,
      cursor.getString(18)!!,
      cursor.getString(19),
      cursor.getString(20)!!,
      cursor.getString(21)
    )
  }

  public fun selectAllAccountsByUser(user_id: String): Query<Accounts> =
      selectAllAccountsByUser(user_id) { id, account_id, user_id_, item_id, name, official_name,
      mask, type, subtype, balance_available, balance_current, balance_limit, iso_currency_code,
      unofficial_currency_code, balance_last_updated, verification_status, persistent_account_id,
      created_at, updated_at, last_synced_at, status, custom_name ->
    Accounts(
      id,
      account_id,
      user_id_,
      item_id,
      name,
      official_name,
      mask,
      type,
      subtype,
      balance_available,
      balance_current,
      balance_limit,
      iso_currency_code,
      unofficial_currency_code,
      balance_last_updated,
      verification_status,
      persistent_account_id,
      created_at,
      updated_at,
      last_synced_at,
      status,
      custom_name
    )
  }

  public fun <T : Any> selectAccountById(account_id: String, mapper: (
    id: Long,
    account_id: String,
    user_id: String,
    item_id: String,
    name: String,
    official_name: String?,
    mask: String?,
    type: String,
    subtype: String?,
    balance_available: Double?,
    balance_current: Double?,
    balance_limit: Double?,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    balance_last_updated: String?,
    verification_status: String?,
    persistent_account_id: String?,
    created_at: String,
    updated_at: String,
    last_synced_at: String?,
    status: String,
    custom_name: String?,
  ) -> T): Query<T> = SelectAccountByIdQuery(account_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getDouble(9),
      cursor.getDouble(10),
      cursor.getDouble(11),
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17)!!,
      cursor.getString(18)!!,
      cursor.getString(19),
      cursor.getString(20)!!,
      cursor.getString(21)
    )
  }

  public fun selectAccountById(account_id: String): Query<Accounts> =
      selectAccountById(account_id) { id, account_id_, user_id, item_id, name, official_name, mask,
      type, subtype, balance_available, balance_current, balance_limit, iso_currency_code,
      unofficial_currency_code, balance_last_updated, verification_status, persistent_account_id,
      created_at, updated_at, last_synced_at, status, custom_name ->
    Accounts(
      id,
      account_id_,
      user_id,
      item_id,
      name,
      official_name,
      mask,
      type,
      subtype,
      balance_available,
      balance_current,
      balance_limit,
      iso_currency_code,
      unofficial_currency_code,
      balance_last_updated,
      verification_status,
      persistent_account_id,
      created_at,
      updated_at,
      last_synced_at,
      status,
      custom_name
    )
  }

  public fun <T : Any> selectCustomNamesForUser(user_id: String, mapper: (account_id: String,
      custom_name: String) -> T): Query<T> = SelectCustomNamesForUserQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!
    )
  }

  public fun selectCustomNamesForUser(user_id: String): Query<SelectCustomNamesForUser> =
      selectCustomNamesForUser(user_id) { account_id, custom_name ->
    SelectCustomNamesForUser(
      account_id,
      custom_name
    )
  }

  public fun <T : Any> selectUnlinkedAccountsForUserAndInstitution(
    userId: String,
    institutionId: String?,
    mapper: (
      id: Long,
      account_id: String,
      user_id: String,
      item_id: String,
      name: String,
      official_name: String?,
      mask: String?,
      type: String,
      subtype: String?,
      balance_available: Double?,
      balance_current: Double?,
      balance_limit: Double?,
      iso_currency_code: String?,
      unofficial_currency_code: String?,
      balance_last_updated: String?,
      verification_status: String?,
      persistent_account_id: String?,
      created_at: String,
      updated_at: String,
      last_synced_at: String?,
      status: String,
      custom_name: String?,
    ) -> T,
  ): Query<T> = SelectUnlinkedAccountsForUserAndInstitutionQuery(userId, institutionId) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getDouble(9),
      cursor.getDouble(10),
      cursor.getDouble(11),
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17)!!,
      cursor.getString(18)!!,
      cursor.getString(19),
      cursor.getString(20)!!,
      cursor.getString(21)
    )
  }

  public fun selectUnlinkedAccountsForUserAndInstitution(userId: String, institutionId: String?):
      Query<Accounts> = selectUnlinkedAccountsForUserAndInstitution(userId, institutionId) { id,
      account_id, user_id, item_id, name, official_name, mask, type, subtype, balance_available,
      balance_current, balance_limit, iso_currency_code, unofficial_currency_code,
      balance_last_updated, verification_status, persistent_account_id, created_at, updated_at,
      last_synced_at, status, custom_name ->
    Accounts(
      id,
      account_id,
      user_id,
      item_id,
      name,
      official_name,
      mask,
      type,
      subtype,
      balance_available,
      balance_current,
      balance_limit,
      iso_currency_code,
      unofficial_currency_code,
      balance_last_updated,
      verification_status,
      persistent_account_id,
      created_at,
      updated_at,
      last_synced_at,
      status,
      custom_name
    )
  }

  public fun <T : Any> findStaleDuplicateAccountMappings(userId: String,
      mapper: (old_account_id: String, new_account_id: String) -> T): Query<T> =
      FindStaleDuplicateAccountMappingsQuery(userId) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!
    )
  }

  public fun findStaleDuplicateAccountMappings(userId: String):
      Query<FindStaleDuplicateAccountMappings> = findStaleDuplicateAccountMappings(userId) {
      old_account_id, new_account_id ->
    FindStaleDuplicateAccountMappings(
      old_account_id,
      new_account_id
    )
  }

  public fun <T : Any> selectAccountsByItemId(item_id: String, mapper: (
    id: Long,
    account_id: String,
    user_id: String,
    item_id: String,
    name: String,
    official_name: String?,
    mask: String?,
    type: String,
    subtype: String?,
    balance_available: Double?,
    balance_current: Double?,
    balance_limit: Double?,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    balance_last_updated: String?,
    verification_status: String?,
    persistent_account_id: String?,
    created_at: String,
    updated_at: String,
    last_synced_at: String?,
    status: String,
    custom_name: String?,
  ) -> T): Query<T> = SelectAccountsByItemIdQuery(item_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getDouble(9),
      cursor.getDouble(10),
      cursor.getDouble(11),
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17)!!,
      cursor.getString(18)!!,
      cursor.getString(19),
      cursor.getString(20)!!,
      cursor.getString(21)
    )
  }

  public fun selectAccountsByItemId(item_id: String): Query<Accounts> =
      selectAccountsByItemId(item_id) { id, account_id, user_id, item_id_, name, official_name,
      mask, type, subtype, balance_available, balance_current, balance_limit, iso_currency_code,
      unofficial_currency_code, balance_last_updated, verification_status, persistent_account_id,
      created_at, updated_at, last_synced_at, status, custom_name ->
    Accounts(
      id,
      account_id,
      user_id,
      item_id_,
      name,
      official_name,
      mask,
      type,
      subtype,
      balance_available,
      balance_current,
      balance_limit,
      iso_currency_code,
      unofficial_currency_code,
      balance_last_updated,
      verification_status,
      persistent_account_id,
      created_at,
      updated_at,
      last_synced_at,
      status,
      custom_name
    )
  }

  public fun <T : Any> selectTokensByUser(user_id: String, mapper: (
    id: Long,
    user_id: String,
    link_token: String?,
    link_token_expiration: String?,
    public_token: String?,
    access_token: String,
    item_id: String,
    institution_id: String?,
    institution_name: String?,
    state: String,
    security_level: String,
    created_at: String,
    updated_at: String,
    last_used_at: String?,
    error_code: String?,
    error_message: String?,
    webhook_url: String?,
    metadata: String?,
    plaid_removal_status: String?,
    transactions_cursor: String?,
    initial_sync_complete: Long,
    historical_sync_complete: Long,
    last_sync_at: String?,
    last_relinked_at: String?,
    last_transactions_refresh_at: String?,
    days_requested: Long?,
  ) -> T): Query<T> = SelectTokensByUserQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getString(3),
      cursor.getString(4),
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7),
      cursor.getString(8),
      cursor.getString(9)!!,
      cursor.getString(10)!!,
      cursor.getString(11)!!,
      cursor.getString(12)!!,
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getLong(20)!!,
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getLong(25)
    )
  }

  public fun selectTokensByUser(user_id: String): Query<Plaid_tokens> =
      selectTokensByUser(user_id) { id, user_id_, link_token, link_token_expiration, public_token,
      access_token, item_id, institution_id, institution_name, state, security_level, created_at,
      updated_at, last_used_at, error_code, error_message, webhook_url, metadata,
      plaid_removal_status, transactions_cursor, initial_sync_complete, historical_sync_complete,
      last_sync_at, last_relinked_at, last_transactions_refresh_at, days_requested ->
    Plaid_tokens(
      id,
      user_id_,
      link_token,
      link_token_expiration,
      public_token,
      access_token,
      item_id,
      institution_id,
      institution_name,
      state,
      security_level,
      created_at,
      updated_at,
      last_used_at,
      error_code,
      error_message,
      webhook_url,
      metadata,
      plaid_removal_status,
      transactions_cursor,
      initial_sync_complete,
      historical_sync_complete,
      last_sync_at,
      last_relinked_at,
      last_transactions_refresh_at,
      days_requested
    )
  }

  public fun <T : Any> selectAllActiveTokens(mapper: (
    id: Long,
    user_id: String,
    link_token: String?,
    link_token_expiration: String?,
    public_token: String?,
    access_token: String,
    item_id: String,
    institution_id: String?,
    institution_name: String?,
    state: String,
    security_level: String,
    created_at: String,
    updated_at: String,
    last_used_at: String?,
    error_code: String?,
    error_message: String?,
    webhook_url: String?,
    metadata: String?,
    plaid_removal_status: String?,
    transactions_cursor: String?,
    initial_sync_complete: Long,
    historical_sync_complete: Long,
    last_sync_at: String?,
    last_relinked_at: String?,
    last_transactions_refresh_at: String?,
    days_requested: Long?,
  ) -> T): Query<T> = Query(-2_105_705_717, arrayOf("plaid_tokens"), driver, "schema.sq",
      "selectAllActiveTokens",
      "SELECT plaid_tokens.id, plaid_tokens.user_id, plaid_tokens.link_token, plaid_tokens.link_token_expiration, plaid_tokens.public_token, plaid_tokens.access_token, plaid_tokens.item_id, plaid_tokens.institution_id, plaid_tokens.institution_name, plaid_tokens.state, plaid_tokens.security_level, plaid_tokens.created_at, plaid_tokens.updated_at, plaid_tokens.last_used_at, plaid_tokens.error_code, plaid_tokens.error_message, plaid_tokens.webhook_url, plaid_tokens.metadata, plaid_tokens.plaid_removal_status, plaid_tokens.transactions_cursor, plaid_tokens.initial_sync_complete, plaid_tokens.historical_sync_complete, plaid_tokens.last_sync_at, plaid_tokens.last_relinked_at, plaid_tokens.last_transactions_refresh_at, plaid_tokens.days_requested FROM plaid_tokens WHERE state = 'ACTIVE'") {
      cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getString(3),
      cursor.getString(4),
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7),
      cursor.getString(8),
      cursor.getString(9)!!,
      cursor.getString(10)!!,
      cursor.getString(11)!!,
      cursor.getString(12)!!,
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getLong(20)!!,
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getLong(25)
    )
  }

  public fun selectAllActiveTokens(): Query<Plaid_tokens> = selectAllActiveTokens { id, user_id,
      link_token, link_token_expiration, public_token, access_token, item_id, institution_id,
      institution_name, state, security_level, created_at, updated_at, last_used_at, error_code,
      error_message, webhook_url, metadata, plaid_removal_status, transactions_cursor,
      initial_sync_complete, historical_sync_complete, last_sync_at, last_relinked_at,
      last_transactions_refresh_at, days_requested ->
    Plaid_tokens(
      id,
      user_id,
      link_token,
      link_token_expiration,
      public_token,
      access_token,
      item_id,
      institution_id,
      institution_name,
      state,
      security_level,
      created_at,
      updated_at,
      last_used_at,
      error_code,
      error_message,
      webhook_url,
      metadata,
      plaid_removal_status,
      transactions_cursor,
      initial_sync_complete,
      historical_sync_complete,
      last_sync_at,
      last_relinked_at,
      last_transactions_refresh_at,
      days_requested
    )
  }

  public fun <T : Any> selectTokenByItemId(item_id: String, mapper: (
    id: Long,
    user_id: String,
    link_token: String?,
    link_token_expiration: String?,
    public_token: String?,
    access_token: String,
    item_id: String,
    institution_id: String?,
    institution_name: String?,
    state: String,
    security_level: String,
    created_at: String,
    updated_at: String,
    last_used_at: String?,
    error_code: String?,
    error_message: String?,
    webhook_url: String?,
    metadata: String?,
    plaid_removal_status: String?,
    transactions_cursor: String?,
    initial_sync_complete: Long,
    historical_sync_complete: Long,
    last_sync_at: String?,
    last_relinked_at: String?,
    last_transactions_refresh_at: String?,
    days_requested: Long?,
  ) -> T): Query<T> = SelectTokenByItemIdQuery(item_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getString(3),
      cursor.getString(4),
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7),
      cursor.getString(8),
      cursor.getString(9)!!,
      cursor.getString(10)!!,
      cursor.getString(11)!!,
      cursor.getString(12)!!,
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getLong(20)!!,
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getLong(25)
    )
  }

  public fun selectTokenByItemId(item_id: String): Query<Plaid_tokens> =
      selectTokenByItemId(item_id) { id, user_id, link_token, link_token_expiration, public_token,
      access_token, item_id_, institution_id, institution_name, state, security_level, created_at,
      updated_at, last_used_at, error_code, error_message, webhook_url, metadata,
      plaid_removal_status, transactions_cursor, initial_sync_complete, historical_sync_complete,
      last_sync_at, last_relinked_at, last_transactions_refresh_at, days_requested ->
    Plaid_tokens(
      id,
      user_id,
      link_token,
      link_token_expiration,
      public_token,
      access_token,
      item_id_,
      institution_id,
      institution_name,
      state,
      security_level,
      created_at,
      updated_at,
      last_used_at,
      error_code,
      error_message,
      webhook_url,
      metadata,
      plaid_removal_status,
      transactions_cursor,
      initial_sync_complete,
      historical_sync_complete,
      last_sync_at,
      last_relinked_at,
      last_transactions_refresh_at,
      days_requested
    )
  }

  public fun <T : Any> getSyncStatus(item_id: String, mapper: (
    transactions_cursor: String?,
    initial_sync_complete: Long,
    historical_sync_complete: Long,
    last_sync_at: String?,
    days_requested: Long?,
  ) -> T): Query<T> = GetSyncStatusQuery(item_id) { cursor ->
    mapper(
      cursor.getString(0),
      cursor.getLong(1)!!,
      cursor.getLong(2)!!,
      cursor.getString(3),
      cursor.getLong(4)
    )
  }

  public fun getSyncStatus(item_id: String): Query<GetSyncStatus> = getSyncStatus(item_id) {
      transactions_cursor, initial_sync_complete, historical_sync_complete, last_sync_at,
      days_requested ->
    GetSyncStatus(
      transactions_cursor,
      initial_sync_complete,
      historical_sync_complete,
      last_sync_at,
      days_requested
    )
  }

  public fun <T : Any> selectPendingPlaidRemovals(mapper: (
    id: Long,
    user_id: String,
    link_token: String?,
    link_token_expiration: String?,
    public_token: String?,
    access_token: String,
    item_id: String,
    institution_id: String?,
    institution_name: String?,
    state: String,
    security_level: String,
    created_at: String,
    updated_at: String,
    last_used_at: String?,
    error_code: String?,
    error_message: String?,
    webhook_url: String?,
    metadata: String?,
    plaid_removal_status: String?,
    transactions_cursor: String?,
    initial_sync_complete: Long,
    historical_sync_complete: Long,
    last_sync_at: String?,
    last_relinked_at: String?,
    last_transactions_refresh_at: String?,
    days_requested: Long?,
  ) -> T): Query<T> = Query(1_760_402_246, arrayOf("plaid_tokens"), driver, "schema.sq",
      "selectPendingPlaidRemovals",
      "SELECT plaid_tokens.id, plaid_tokens.user_id, plaid_tokens.link_token, plaid_tokens.link_token_expiration, plaid_tokens.public_token, plaid_tokens.access_token, plaid_tokens.item_id, plaid_tokens.institution_id, plaid_tokens.institution_name, plaid_tokens.state, plaid_tokens.security_level, plaid_tokens.created_at, plaid_tokens.updated_at, plaid_tokens.last_used_at, plaid_tokens.error_code, plaid_tokens.error_message, plaid_tokens.webhook_url, plaid_tokens.metadata, plaid_tokens.plaid_removal_status, plaid_tokens.transactions_cursor, plaid_tokens.initial_sync_complete, plaid_tokens.historical_sync_complete, plaid_tokens.last_sync_at, plaid_tokens.last_relinked_at, plaid_tokens.last_transactions_refresh_at, plaid_tokens.days_requested FROM plaid_tokens WHERE plaid_removal_status = 'PENDING'") {
      cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getString(3),
      cursor.getString(4),
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7),
      cursor.getString(8),
      cursor.getString(9)!!,
      cursor.getString(10)!!,
      cursor.getString(11)!!,
      cursor.getString(12)!!,
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getLong(20)!!,
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getLong(25)
    )
  }

  public fun selectPendingPlaidRemovals(): Query<Plaid_tokens> = selectPendingPlaidRemovals { id,
      user_id, link_token, link_token_expiration, public_token, access_token, item_id,
      institution_id, institution_name, state, security_level, created_at, updated_at, last_used_at,
      error_code, error_message, webhook_url, metadata, plaid_removal_status, transactions_cursor,
      initial_sync_complete, historical_sync_complete, last_sync_at, last_relinked_at,
      last_transactions_refresh_at, days_requested ->
    Plaid_tokens(
      id,
      user_id,
      link_token,
      link_token_expiration,
      public_token,
      access_token,
      item_id,
      institution_id,
      institution_name,
      state,
      security_level,
      created_at,
      updated_at,
      last_used_at,
      error_code,
      error_message,
      webhook_url,
      metadata,
      plaid_removal_status,
      transactions_cursor,
      initial_sync_complete,
      historical_sync_complete,
      last_sync_at,
      last_relinked_at,
      last_transactions_refresh_at,
      days_requested
    )
  }

  public fun <T : Any> selectAllPendingRemovals(mapper: (
    id: Long,
    item_id: String,
    access_token: String,
    user_id: String?,
    institution_name: String?,
    error_message: String?,
    attempts: Long,
    created_at: String,
    last_attempt_at: String,
  ) -> T): Query<T> = Query(-1_506_120_173, arrayOf("pending_plaid_removals"), driver, "schema.sq",
      "selectAllPendingRemovals",
      "SELECT pending_plaid_removals.id, pending_plaid_removals.item_id, pending_plaid_removals.access_token, pending_plaid_removals.user_id, pending_plaid_removals.institution_name, pending_plaid_removals.error_message, pending_plaid_removals.attempts, pending_plaid_removals.created_at, pending_plaid_removals.last_attempt_at FROM pending_plaid_removals ORDER BY created_at") {
      cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getString(4),
      cursor.getString(5),
      cursor.getLong(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8)!!
    )
  }

  public fun selectAllPendingRemovals(): Query<Pending_plaid_removals> = selectAllPendingRemovals {
      id, item_id, access_token, user_id, institution_name, error_message, attempts, created_at,
      last_attempt_at ->
    Pending_plaid_removals(
      id,
      item_id,
      access_token,
      user_id,
      institution_name,
      error_message,
      attempts,
      created_at,
      last_attempt_at
    )
  }

  public fun <T : Any> selectCorrectionsByUser(
    user_id: String,
    `value`: Long,
    mapper: (
      id: Long,
      transaction_id: String,
      user_id: String,
      old_category: String,
      new_category: String,
      original_confidence: Double?,
      correction_reason: String?,
      timestamp: String,
      ml_model_version: String?,
      merchant_name: String?,
      transaction_amount: Double?,
    ) -> T,
  ): Query<T> = SelectCorrectionsByUserQuery(user_id, value) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getDouble(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getDouble(10)
    )
  }

  public fun selectCorrectionsByUser(user_id: String, value_: Long): Query<User_corrections> =
      selectCorrectionsByUser(user_id, value_) { id, transaction_id, user_id_, old_category,
      new_category, original_confidence, correction_reason, timestamp, ml_model_version,
      merchant_name, transaction_amount ->
    User_corrections(
      id,
      transaction_id,
      user_id_,
      old_category,
      new_category,
      original_confidence,
      correction_reason,
      timestamp,
      ml_model_version,
      merchant_name,
      transaction_amount
    )
  }

  public fun <T : Any> selectCorrectionsByTransaction(transaction_id: String, mapper: (
    id: Long,
    transaction_id: String,
    user_id: String,
    old_category: String,
    new_category: String,
    original_confidence: Double?,
    correction_reason: String?,
    timestamp: String,
    ml_model_version: String?,
    merchant_name: String?,
    transaction_amount: Double?,
  ) -> T): Query<T> = SelectCorrectionsByTransactionQuery(transaction_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getDouble(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getDouble(10)
    )
  }

  public fun selectCorrectionsByTransaction(transaction_id: String): Query<User_corrections> =
      selectCorrectionsByTransaction(transaction_id) { id, transaction_id_, user_id, old_category,
      new_category, original_confidence, correction_reason, timestamp, ml_model_version,
      merchant_name, transaction_amount ->
    User_corrections(
      id,
      transaction_id_,
      user_id,
      old_category,
      new_category,
      original_confidence,
      correction_reason,
      timestamp,
      ml_model_version,
      merchant_name,
      transaction_amount
    )
  }

  public fun <T : Any> getCorrectionPatterns(
    user_id: String,
    `value`: Long,
    mapper: (
      old_category: String,
      new_category: String,
      pattern_count: Long,
      avg_confidence: Double?,
      merchant_name: String?,
    ) -> T,
  ): Query<T> = GetCorrectionPatternsQuery(user_id, value) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2)!!,
      cursor.getDouble(3),
      cursor.getString(4)
    )
  }

  public fun getCorrectionPatterns(user_id: String, value_: Long): Query<GetCorrectionPatterns> =
      getCorrectionPatterns(user_id, value_) { old_category, new_category, pattern_count,
      avg_confidence, merchant_name ->
    GetCorrectionPatterns(
      old_category,
      new_category,
      pattern_count,
      avg_confidence,
      merchant_name
    )
  }

  public fun <T : Any> getCorrectionsByCategory(
    user_id: String,
    old_category: String,
    `value`: Long,
    mapper: (
      old_category: String,
      new_category: String,
      merchant_name: String?,
      transaction_amount: Double?,
      original_confidence: Double?,
      timestamp: String,
    ) -> T,
  ): Query<T> = GetCorrectionsByCategoryQuery(user_id, old_category, value) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getDouble(3),
      cursor.getDouble(4),
      cursor.getString(5)!!
    )
  }

  public fun getCorrectionsByCategory(
    user_id: String,
    old_category: String,
    value_: Long,
  ): Query<GetCorrectionsByCategory> = getCorrectionsByCategory(user_id, old_category, value_) {
      old_category_, new_category, merchant_name, transaction_amount, original_confidence,
      timestamp ->
    GetCorrectionsByCategory(
      old_category_,
      new_category,
      merchant_name,
      transaction_amount,
      original_confidence,
      timestamp
    )
  }

  public fun <T : Any> getRecentCorrections(
    user_id: String,
    timestamp: String,
    mapper: (
      id: Long,
      transaction_id: String,
      user_id: String,
      old_category: String,
      new_category: String,
      original_confidence: Double?,
      correction_reason: String?,
      timestamp: String,
      ml_model_version: String?,
      merchant_name: String?,
      transaction_amount: Double?,
    ) -> T,
  ): Query<T> = GetRecentCorrectionsQuery(user_id, timestamp) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getDouble(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getDouble(10)
    )
  }

  public fun getRecentCorrections(user_id: String, timestamp: String): Query<User_corrections> =
      getRecentCorrections(user_id, timestamp) { id, transaction_id, user_id_, old_category,
      new_category, original_confidence, correction_reason, timestamp_, ml_model_version,
      merchant_name, transaction_amount ->
    User_corrections(
      id,
      transaction_id,
      user_id_,
      old_category,
      new_category,
      original_confidence,
      correction_reason,
      timestamp_,
      ml_model_version,
      merchant_name,
      transaction_amount
    )
  }

  public fun <T : Any> getCorrectionsByMerchant(
    user_id: String,
    `value`: Long,
    mapper: (
      merchant_name: String?,
      old_category: String,
      new_category: String,
      correction_count: Long,
      avg_original_confidence: Double?,
    ) -> T,
  ): Query<T> = GetCorrectionsByMerchantQuery(user_id, value) { cursor ->
    mapper(
      cursor.getString(0),
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3)!!,
      cursor.getDouble(4)
    )
  }

  public fun getCorrectionsByMerchant(user_id: String, value_: Long):
      Query<GetCorrectionsByMerchant> = getCorrectionsByMerchant(user_id, value_) { merchant_name,
      old_category, new_category, correction_count, avg_original_confidence ->
    GetCorrectionsByMerchant(
      merchant_name,
      old_category,
      new_category,
      correction_count,
      avg_original_confidence
    )
  }

  public fun <T : Any> selectEmbeddingByTransaction(transaction_id: String, mapper: (
    id: Long,
    transaction_id: String,
    user_id: String,
    embedding_vector: ByteArray?,
    embedding_dimension: Long,
    source_text: String,
    embedding_model: String,
    embedding_version: String?,
    category_hint: String?,
    similarity_threshold: Double?,
    created_at: String,
    updated_at: String,
  ) -> T): Query<T> = SelectEmbeddingByTransactionQuery(transaction_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getBytes(3),
      cursor.getLong(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7),
      cursor.getString(8),
      cursor.getDouble(9),
      cursor.getString(10)!!,
      cursor.getString(11)!!
    )
  }

  public fun selectEmbeddingByTransaction(transaction_id: String): Query<Transaction_embeddings> =
      selectEmbeddingByTransaction(transaction_id) { id, transaction_id_, user_id, embedding_vector,
      embedding_dimension, source_text, embedding_model, embedding_version, category_hint,
      similarity_threshold, created_at, updated_at ->
    Transaction_embeddings(
      id,
      transaction_id_,
      user_id,
      embedding_vector,
      embedding_dimension,
      source_text,
      embedding_model,
      embedding_version,
      category_hint,
      similarity_threshold,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> selectEmbeddingsByUser(
    user_id: String,
    `value`: Long,
    mapper: (
      id: Long,
      transaction_id: String,
      user_id: String,
      embedding_vector: ByteArray?,
      embedding_dimension: Long,
      source_text: String,
      embedding_model: String,
      embedding_version: String?,
      category_hint: String?,
      similarity_threshold: Double?,
      created_at: String,
      updated_at: String,
    ) -> T,
  ): Query<T> = SelectEmbeddingsByUserQuery(user_id, value) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getBytes(3),
      cursor.getLong(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7),
      cursor.getString(8),
      cursor.getDouble(9),
      cursor.getString(10)!!,
      cursor.getString(11)!!
    )
  }

  public fun selectEmbeddingsByUser(user_id: String, value_: Long): Query<Transaction_embeddings> =
      selectEmbeddingsByUser(user_id, value_) { id, transaction_id, user_id_, embedding_vector,
      embedding_dimension, source_text, embedding_model, embedding_version, category_hint,
      similarity_threshold, created_at, updated_at ->
    Transaction_embeddings(
      id,
      transaction_id,
      user_id_,
      embedding_vector,
      embedding_dimension,
      source_text,
      embedding_model,
      embedding_version,
      category_hint,
      similarity_threshold,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> getEmbeddingsForSimilarity(
    user_id: String,
    embedding_model: String,
    mapper: (
      transaction_id: String,
      embedding_vector: ByteArray?,
      category_hint: String?,
      similarity_threshold: Double?,
    ) -> T,
  ): Query<T> = GetEmbeddingsForSimilarityQuery(user_id, embedding_model) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getBytes(1),
      cursor.getString(2),
      cursor.getDouble(3)
    )
  }

  public fun getEmbeddingsForSimilarity(user_id: String, embedding_model: String):
      Query<GetEmbeddingsForSimilarity> = getEmbeddingsForSimilarity(user_id, embedding_model) {
      transaction_id, embedding_vector, category_hint, similarity_threshold ->
    GetEmbeddingsForSimilarity(
      transaction_id,
      embedding_vector,
      category_hint,
      similarity_threshold
    )
  }

  public fun <T : Any> getEmbeddingsByCategory(
    user_id: String,
    category_hint: String?,
    `value`: Long,
    mapper: (
      transaction_id: String,
      embedding_vector: ByteArray?,
      source_text: String,
      category_hint: String?,
    ) -> T,
  ): Query<T> = GetEmbeddingsByCategoryQuery(user_id, category_hint, value) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getBytes(1),
      cursor.getString(2)!!,
      cursor.getString(3)
    )
  }

  public fun getEmbeddingsByCategory(
    user_id: String,
    category_hint: String?,
    value_: Long,
  ): Query<GetEmbeddingsByCategory> = getEmbeddingsByCategory(user_id, category_hint, value_) {
      transaction_id, embedding_vector, source_text, category_hint_ ->
    GetEmbeddingsByCategory(
      transaction_id,
      embedding_vector,
      source_text,
      category_hint_
    )
  }

  public fun <T : Any> getUncategorizedEmbeddings(
    user_id: String,
    `value`: Long,
    mapper: (
      transaction_id: String,
      embedding_vector: ByteArray?,
      source_text: String,
      similarity_threshold: Double?,
    ) -> T,
  ): Query<T> = GetUncategorizedEmbeddingsQuery(user_id, value) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getBytes(1),
      cursor.getString(2)!!,
      cursor.getDouble(3)
    )
  }

  public fun getUncategorizedEmbeddings(user_id: String, value_: Long):
      Query<GetUncategorizedEmbeddings> = getUncategorizedEmbeddings(user_id, value_) {
      transaction_id, embedding_vector, source_text, similarity_threshold ->
    GetUncategorizedEmbeddings(
      transaction_id,
      embedding_vector,
      source_text,
      similarity_threshold
    )
  }

  public fun <T : Any> getTopLevelCategories(user_id: String, mapper: (
    id: Long,
    user_id: String,
    name: String,
    parent_id: Long?,
    color: String?,
    icon: String?,
    is_system: Long,
    created_at: String,
  ) -> T): Query<T> = GetTopLevelCategoriesQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3),
      cursor.getString(4),
      cursor.getString(5),
      cursor.getLong(6)!!,
      cursor.getString(7)!!
    )
  }

  public fun getTopLevelCategories(user_id: String): Query<Transaction_categories> =
      getTopLevelCategories(user_id) { id, user_id_, name, parent_id, color, icon, is_system,
      created_at ->
    Transaction_categories(
      id,
      user_id_,
      name,
      parent_id,
      color,
      icon,
      is_system,
      created_at
    )
  }

  public fun <T : Any> getSubCategories(
    user_id: String,
    parent_id: Long?,
    mapper: (
      id: Long,
      user_id: String,
      name: String,
      parent_id: Long?,
      color: String?,
      icon: String?,
      is_system: Long,
      created_at: String,
    ) -> T,
  ): Query<T> = GetSubCategoriesQuery(user_id, parent_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3),
      cursor.getString(4),
      cursor.getString(5),
      cursor.getLong(6)!!,
      cursor.getString(7)!!
    )
  }

  public fun getSubCategories(user_id: String, parent_id: Long?): Query<Transaction_categories> =
      getSubCategories(user_id, parent_id) { id, user_id_, name, parent_id_, color, icon, is_system,
      created_at ->
    Transaction_categories(
      id,
      user_id_,
      name,
      parent_id_,
      color,
      icon,
      is_system,
      created_at
    )
  }

  public fun <T : Any> getCategoryById(id: Long, mapper: (
    id: Long,
    user_id: String,
    name: String,
    parent_id: Long?,
    color: String?,
    icon: String?,
    is_system: Long,
    created_at: String,
  ) -> T): Query<T> = GetCategoryByIdQuery(id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3),
      cursor.getString(4),
      cursor.getString(5),
      cursor.getLong(6)!!,
      cursor.getString(7)!!
    )
  }

  public fun getCategoryById(id: Long): Query<Transaction_categories> = getCategoryById(id) { id_,
      user_id, name, parent_id, color, icon, is_system, created_at ->
    Transaction_categories(
      id_,
      user_id,
      name,
      parent_id,
      color,
      icon,
      is_system,
      created_at
    )
  }

  public fun <T : Any> getAllCategories(user_id: String, mapper: (
    id: Long,
    user_id: String,
    name: String,
    parent_id: Long?,
    color: String?,
    icon: String?,
    is_system: Long,
    created_at: String,
  ) -> T): Query<T> = GetAllCategoriesQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3),
      cursor.getString(4),
      cursor.getString(5),
      cursor.getLong(6)!!,
      cursor.getString(7)!!
    )
  }

  public fun getAllCategories(user_id: String): Query<Transaction_categories> =
      getAllCategories(user_id) { id, user_id_, name, parent_id, color, icon, is_system,
      created_at ->
    Transaction_categories(
      id,
      user_id_,
      name,
      parent_id,
      color,
      icon,
      is_system,
      created_at
    )
  }

  public fun <T : Any> searchCategories(
    user_id: String,
    name: String,
    `value`: Long,
    mapper: (
      id: Long,
      user_id: String,
      name: String,
      parent_id: Long?,
      color: String?,
      icon: String?,
      is_system: Long,
      created_at: String,
    ) -> T,
  ): Query<T> = SearchCategoriesQuery(user_id, name, value) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3),
      cursor.getString(4),
      cursor.getString(5),
      cursor.getLong(6)!!,
      cursor.getString(7)!!
    )
  }

  public fun searchCategories(
    user_id: String,
    name: String,
    value_: Long,
  ): Query<Transaction_categories> = searchCategories(user_id, name, value_) { id, user_id_, name_,
      parent_id, color, icon, is_system, created_at ->
    Transaction_categories(
      id,
      user_id_,
      name_,
      parent_id,
      color,
      icon,
      is_system,
      created_at
    )
  }

  public fun <T : Any> getCategoryByName(
    user_id: String,
    name: String,
    mapper: (
      id: Long,
      user_id: String,
      name: String,
      parent_id: Long?,
      color: String?,
      icon: String?,
      is_system: Long,
      created_at: String,
    ) -> T,
  ): Query<T> = GetCategoryByNameQuery(user_id, name) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3),
      cursor.getString(4),
      cursor.getString(5),
      cursor.getLong(6)!!,
      cursor.getString(7)!!
    )
  }

  public fun getCategoryByName(user_id: String, name: String): Query<Transaction_categories> =
      getCategoryByName(user_id, name) { id, user_id_, name_, parent_id, color, icon, is_system,
      created_at ->
    Transaction_categories(
      id,
      user_id_,
      name_,
      parent_id,
      color,
      icon,
      is_system,
      created_at
    )
  }

  public fun <T : Any> selectActiveGoalsByUser(user_id: String, mapper: (
    id: String,
    user_id: String,
    name: String,
    description: String?,
    target_amount: Double,
    current_amount: Double?,
    deadline: String,
    category: String,
    priority: String?,
    created_date: String,
    is_active: Long,
    icon_emoji: String?,
    created_at: String,
    updated_at: String,
    last_modified: String,
    account_type: String?,
    debt_interest_rate: Double?,
    debt_remaining_balance: Double?,
    debt_remaining_months: Long?,
    debt_payment_frequency: String?,
    debt_monthly_equivalent_payment: Double?,
  ) -> T): Query<T> = SelectActiveGoalsByUserQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getDouble(4)!!,
      cursor.getDouble(5),
      cursor.getString(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9)!!,
      cursor.getLong(10)!!,
      cursor.getString(11),
      cursor.getString(12)!!,
      cursor.getString(13)!!,
      cursor.getString(14)!!,
      cursor.getString(15),
      cursor.getDouble(16),
      cursor.getDouble(17),
      cursor.getLong(18),
      cursor.getString(19),
      cursor.getDouble(20)
    )
  }

  public fun selectActiveGoalsByUser(user_id: String): Query<Savings_goals> =
      selectActiveGoalsByUser(user_id) { id, user_id_, name, description, target_amount,
      current_amount, deadline, category, priority, created_date, is_active, icon_emoji, created_at,
      updated_at, last_modified, account_type, debt_interest_rate, debt_remaining_balance,
      debt_remaining_months, debt_payment_frequency, debt_monthly_equivalent_payment ->
    Savings_goals(
      id,
      user_id_,
      name,
      description,
      target_amount,
      current_amount,
      deadline,
      category,
      priority,
      created_date,
      is_active,
      icon_emoji,
      created_at,
      updated_at,
      last_modified,
      account_type,
      debt_interest_rate,
      debt_remaining_balance,
      debt_remaining_months,
      debt_payment_frequency,
      debt_monthly_equivalent_payment
    )
  }

  public fun <T : Any> selectAllGoalsByUser(user_id: String, mapper: (
    id: String,
    user_id: String,
    name: String,
    description: String?,
    target_amount: Double,
    current_amount: Double?,
    deadline: String,
    category: String,
    priority: String?,
    created_date: String,
    is_active: Long,
    icon_emoji: String?,
    created_at: String,
    updated_at: String,
    last_modified: String,
    account_type: String?,
    debt_interest_rate: Double?,
    debt_remaining_balance: Double?,
    debt_remaining_months: Long?,
    debt_payment_frequency: String?,
    debt_monthly_equivalent_payment: Double?,
  ) -> T): Query<T> = SelectAllGoalsByUserQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getDouble(4)!!,
      cursor.getDouble(5),
      cursor.getString(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9)!!,
      cursor.getLong(10)!!,
      cursor.getString(11),
      cursor.getString(12)!!,
      cursor.getString(13)!!,
      cursor.getString(14)!!,
      cursor.getString(15),
      cursor.getDouble(16),
      cursor.getDouble(17),
      cursor.getLong(18),
      cursor.getString(19),
      cursor.getDouble(20)
    )
  }

  public fun selectAllGoalsByUser(user_id: String): Query<Savings_goals> =
      selectAllGoalsByUser(user_id) { id, user_id_, name, description, target_amount,
      current_amount, deadline, category, priority, created_date, is_active, icon_emoji, created_at,
      updated_at, last_modified, account_type, debt_interest_rate, debt_remaining_balance,
      debt_remaining_months, debt_payment_frequency, debt_monthly_equivalent_payment ->
    Savings_goals(
      id,
      user_id_,
      name,
      description,
      target_amount,
      current_amount,
      deadline,
      category,
      priority,
      created_date,
      is_active,
      icon_emoji,
      created_at,
      updated_at,
      last_modified,
      account_type,
      debt_interest_rate,
      debt_remaining_balance,
      debt_remaining_months,
      debt_payment_frequency,
      debt_monthly_equivalent_payment
    )
  }

  public fun <T : Any> selectGoalById(id: String, mapper: (
    id: String,
    user_id: String,
    name: String,
    description: String?,
    target_amount: Double,
    current_amount: Double?,
    deadline: String,
    category: String,
    priority: String?,
    created_date: String,
    is_active: Long,
    icon_emoji: String?,
    created_at: String,
    updated_at: String,
    last_modified: String,
    account_type: String?,
    debt_interest_rate: Double?,
    debt_remaining_balance: Double?,
    debt_remaining_months: Long?,
    debt_payment_frequency: String?,
    debt_monthly_equivalent_payment: Double?,
  ) -> T): Query<T> = SelectGoalByIdQuery(id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getDouble(4)!!,
      cursor.getDouble(5),
      cursor.getString(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9)!!,
      cursor.getLong(10)!!,
      cursor.getString(11),
      cursor.getString(12)!!,
      cursor.getString(13)!!,
      cursor.getString(14)!!,
      cursor.getString(15),
      cursor.getDouble(16),
      cursor.getDouble(17),
      cursor.getLong(18),
      cursor.getString(19),
      cursor.getDouble(20)
    )
  }

  public fun selectGoalById(id: String): Query<Savings_goals> = selectGoalById(id) { id_, user_id,
      name, description, target_amount, current_amount, deadline, category, priority, created_date,
      is_active, icon_emoji, created_at, updated_at, last_modified, account_type,
      debt_interest_rate, debt_remaining_balance, debt_remaining_months, debt_payment_frequency,
      debt_monthly_equivalent_payment ->
    Savings_goals(
      id_,
      user_id,
      name,
      description,
      target_amount,
      current_amount,
      deadline,
      category,
      priority,
      created_date,
      is_active,
      icon_emoji,
      created_at,
      updated_at,
      last_modified,
      account_type,
      debt_interest_rate,
      debt_remaining_balance,
      debt_remaining_months,
      debt_payment_frequency,
      debt_monthly_equivalent_payment
    )
  }

  public fun <T : Any> selectGoalsByCategory(
    user_id: String,
    category: String,
    mapper: (
      id: String,
      user_id: String,
      name: String,
      description: String?,
      target_amount: Double,
      current_amount: Double?,
      deadline: String,
      category: String,
      priority: String?,
      created_date: String,
      is_active: Long,
      icon_emoji: String?,
      created_at: String,
      updated_at: String,
      last_modified: String,
      account_type: String?,
      debt_interest_rate: Double?,
      debt_remaining_balance: Double?,
      debt_remaining_months: Long?,
      debt_payment_frequency: String?,
      debt_monthly_equivalent_payment: Double?,
    ) -> T,
  ): Query<T> = SelectGoalsByCategoryQuery(user_id, category) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getDouble(4)!!,
      cursor.getDouble(5),
      cursor.getString(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9)!!,
      cursor.getLong(10)!!,
      cursor.getString(11),
      cursor.getString(12)!!,
      cursor.getString(13)!!,
      cursor.getString(14)!!,
      cursor.getString(15),
      cursor.getDouble(16),
      cursor.getDouble(17),
      cursor.getLong(18),
      cursor.getString(19),
      cursor.getDouble(20)
    )
  }

  public fun selectGoalsByCategory(user_id: String, category: String): Query<Savings_goals> =
      selectGoalsByCategory(user_id, category) { id, user_id_, name, description, target_amount,
      current_amount, deadline, category_, priority, created_date, is_active, icon_emoji,
      created_at, updated_at, last_modified, account_type, debt_interest_rate,
      debt_remaining_balance, debt_remaining_months, debt_payment_frequency,
      debt_monthly_equivalent_payment ->
    Savings_goals(
      id,
      user_id_,
      name,
      description,
      target_amount,
      current_amount,
      deadline,
      category_,
      priority,
      created_date,
      is_active,
      icon_emoji,
      created_at,
      updated_at,
      last_modified,
      account_type,
      debt_interest_rate,
      debt_remaining_balance,
      debt_remaining_months,
      debt_payment_frequency,
      debt_monthly_equivalent_payment
    )
  }

  public fun <T : Any> selectGoalsNearingDeadline(user_id: String, mapper: (
    id: String,
    user_id: String,
    name: String,
    description: String?,
    target_amount: Double,
    current_amount: Double?,
    deadline: String,
    category: String,
    priority: String?,
    created_date: String,
    is_active: Long,
    icon_emoji: String?,
    created_at: String,
    updated_at: String,
    last_modified: String,
    account_type: String?,
    debt_interest_rate: Double?,
    debt_remaining_balance: Double?,
    debt_remaining_months: Long?,
    debt_payment_frequency: String?,
    debt_monthly_equivalent_payment: Double?,
  ) -> T): Query<T> = SelectGoalsNearingDeadlineQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getDouble(4)!!,
      cursor.getDouble(5),
      cursor.getString(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9)!!,
      cursor.getLong(10)!!,
      cursor.getString(11),
      cursor.getString(12)!!,
      cursor.getString(13)!!,
      cursor.getString(14)!!,
      cursor.getString(15),
      cursor.getDouble(16),
      cursor.getDouble(17),
      cursor.getLong(18),
      cursor.getString(19),
      cursor.getDouble(20)
    )
  }

  public fun selectGoalsNearingDeadline(user_id: String): Query<Savings_goals> =
      selectGoalsNearingDeadline(user_id) { id, user_id_, name, description, target_amount,
      current_amount, deadline, category, priority, created_date, is_active, icon_emoji, created_at,
      updated_at, last_modified, account_type, debt_interest_rate, debt_remaining_balance,
      debt_remaining_months, debt_payment_frequency, debt_monthly_equivalent_payment ->
    Savings_goals(
      id,
      user_id_,
      name,
      description,
      target_amount,
      current_amount,
      deadline,
      category,
      priority,
      created_date,
      is_active,
      icon_emoji,
      created_at,
      updated_at,
      last_modified,
      account_type,
      debt_interest_rate,
      debt_remaining_balance,
      debt_remaining_months,
      debt_payment_frequency,
      debt_monthly_equivalent_payment
    )
  }

  public fun <T : Any> selectRecommendationsByGoal(goal_id: String, mapper: (
    id: Long,
    goal_id: String,
    user_id: String,
    spending_category: String,
    current_monthly_spending: Double,
    recommended_monthly_spending: Double,
    suggested_reduction: Double,
    rationale: String,
    potential_savings: Double,
    confidence_score: Double,
    priority: Long,
    created_at: String,
    is_applied: Long,
    applied_at: String?,
  ) -> T): Query<T> = SelectRecommendationsByGoalQuery(goal_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getDouble(5)!!,
      cursor.getDouble(6)!!,
      cursor.getString(7)!!,
      cursor.getDouble(8)!!,
      cursor.getDouble(9)!!,
      cursor.getLong(10)!!,
      cursor.getString(11)!!,
      cursor.getLong(12)!!,
      cursor.getString(13)
    )
  }

  public fun selectRecommendationsByGoal(goal_id: String): Query<Savings_recommendations> =
      selectRecommendationsByGoal(goal_id) { id, goal_id_, user_id, spending_category,
      current_monthly_spending, recommended_monthly_spending, suggested_reduction, rationale,
      potential_savings, confidence_score, priority, created_at, is_applied, applied_at ->
    Savings_recommendations(
      id,
      goal_id_,
      user_id,
      spending_category,
      current_monthly_spending,
      recommended_monthly_spending,
      suggested_reduction,
      rationale,
      potential_savings,
      confidence_score,
      priority,
      created_at,
      is_applied,
      applied_at
    )
  }

  public fun <T : Any> selectUnappliedRecommendations(goal_id: String, mapper: (
    id: Long,
    goal_id: String,
    user_id: String,
    spending_category: String,
    current_monthly_spending: Double,
    recommended_monthly_spending: Double,
    suggested_reduction: Double,
    rationale: String,
    potential_savings: Double,
    confidence_score: Double,
    priority: Long,
    created_at: String,
    is_applied: Long,
    applied_at: String?,
  ) -> T): Query<T> = SelectUnappliedRecommendationsQuery(goal_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getDouble(5)!!,
      cursor.getDouble(6)!!,
      cursor.getString(7)!!,
      cursor.getDouble(8)!!,
      cursor.getDouble(9)!!,
      cursor.getLong(10)!!,
      cursor.getString(11)!!,
      cursor.getLong(12)!!,
      cursor.getString(13)
    )
  }

  public fun selectUnappliedRecommendations(goal_id: String): Query<Savings_recommendations> =
      selectUnappliedRecommendations(goal_id) { id, goal_id_, user_id, spending_category,
      current_monthly_spending, recommended_monthly_spending, suggested_reduction, rationale,
      potential_savings, confidence_score, priority, created_at, is_applied, applied_at ->
    Savings_recommendations(
      id,
      goal_id_,
      user_id,
      spending_category,
      current_monthly_spending,
      recommended_monthly_spending,
      suggested_reduction,
      rationale,
      potential_savings,
      confidence_score,
      priority,
      created_at,
      is_applied,
      applied_at
    )
  }

  public fun <T : Any> selectProgressHistoryByGoal(goal_id: String, mapper: (
    id: Long,
    goal_id: String,
    user_id: String,
    contribution_amount: Double,
    contribution_date: String,
    notes: String?,
    created_at: String,
  ) -> T): Query<T> = SelectProgressHistoryByGoalQuery(goal_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getDouble(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6)!!
    )
  }

  public fun selectProgressHistoryByGoal(goal_id: String): Query<Goal_progress_history> =
      selectProgressHistoryByGoal(goal_id) { id, goal_id_, user_id, contribution_amount,
      contribution_date, notes, created_at ->
    Goal_progress_history(
      id,
      goal_id_,
      user_id,
      contribution_amount,
      contribution_date,
      notes,
      created_at
    )
  }

  public fun getTotalContributions(goal_id: String): Query<Double> =
      GetTotalContributionsQuery(goal_id) { cursor ->
    cursor.getDouble(0)!!
  }

  public fun <T : Any> getMonthlyContributions(goal_id: String, mapper: (
    month: String,
    total_contribution: Double?,
    contribution_count: Long,
  ) -> T): Query<T> = GetMonthlyContributionsQuery(goal_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getDouble(1),
      cursor.getLong(2)!!
    )
  }

  public fun getMonthlyContributions(goal_id: String): Query<GetMonthlyContributions> =
      getMonthlyContributions(goal_id) { month, total_contribution, contribution_count ->
    GetMonthlyContributions(
      month,
      total_contribution,
      contribution_count
    )
  }

  public fun getLastInsertRowId(): ExecutableQuery<Long> = Query(-1_415_412_406, driver,
      "schema.sq", "getLastInsertRowId", "SELECT last_insert_rowid()") { cursor ->
    cursor.getLong(0)!!
  }

  public fun <T : Any> selectRulesByUser(user_id: String, mapper: (
    id: Long,
    user_id: String,
    goal_id: String,
    merchant_name: String?,
    account_id: String?,
    description_pattern: String?,
    priority: Long?,
    is_default: Long?,
    rule_mode: String?,
    created_at: String,
    updated_at: String,
  ) -> T): Query<T> = SelectRulesByUserQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getString(4),
      cursor.getString(5),
      cursor.getLong(6),
      cursor.getLong(7),
      cursor.getString(8),
      cursor.getString(9)!!,
      cursor.getString(10)!!
    )
  }

  public fun selectRulesByUser(user_id: String): Query<Goal_assignment_rules> =
      selectRulesByUser(user_id) { id, user_id_, goal_id, merchant_name, account_id,
      description_pattern, priority, is_default, rule_mode, created_at, updated_at ->
    Goal_assignment_rules(
      id,
      user_id_,
      goal_id,
      merchant_name,
      account_id,
      description_pattern,
      priority,
      is_default,
      rule_mode,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> selectRulesByGoal(goal_id: String, mapper: (
    id: Long,
    user_id: String,
    goal_id: String,
    merchant_name: String?,
    account_id: String?,
    description_pattern: String?,
    priority: Long?,
    is_default: Long?,
    rule_mode: String?,
    created_at: String,
    updated_at: String,
  ) -> T): Query<T> = SelectRulesByGoalQuery(goal_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getString(4),
      cursor.getString(5),
      cursor.getLong(6),
      cursor.getLong(7),
      cursor.getString(8),
      cursor.getString(9)!!,
      cursor.getString(10)!!
    )
  }

  public fun selectRulesByGoal(goal_id: String): Query<Goal_assignment_rules> =
      selectRulesByGoal(goal_id) { id, user_id, goal_id_, merchant_name, account_id,
      description_pattern, priority, is_default, rule_mode, created_at, updated_at ->
    Goal_assignment_rules(
      id,
      user_id,
      goal_id_,
      merchant_name,
      account_id,
      description_pattern,
      priority,
      is_default,
      rule_mode,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> selectRulesByMerchant(
    user_id: String,
    merchant_name: String?,
    mapper: (
      id: Long,
      user_id: String,
      goal_id: String,
      merchant_name: String?,
      account_id: String?,
      description_pattern: String?,
      priority: Long?,
      is_default: Long?,
      rule_mode: String?,
      created_at: String,
      updated_at: String,
    ) -> T,
  ): Query<T> = SelectRulesByMerchantQuery(user_id, merchant_name) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getString(4),
      cursor.getString(5),
      cursor.getLong(6),
      cursor.getLong(7),
      cursor.getString(8),
      cursor.getString(9)!!,
      cursor.getString(10)!!
    )
  }

  public fun selectRulesByMerchant(user_id: String, merchant_name: String?):
      Query<Goal_assignment_rules> = selectRulesByMerchant(user_id, merchant_name) { id, user_id_,
      goal_id, merchant_name_, account_id, description_pattern, priority, is_default, rule_mode,
      created_at, updated_at ->
    Goal_assignment_rules(
      id,
      user_id_,
      goal_id,
      merchant_name_,
      account_id,
      description_pattern,
      priority,
      is_default,
      rule_mode,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> selectRulesByAccount(
    user_id: String,
    account_id: String?,
    mapper: (
      id: Long,
      user_id: String,
      goal_id: String,
      merchant_name: String?,
      account_id: String?,
      description_pattern: String?,
      priority: Long?,
      is_default: Long?,
      rule_mode: String?,
      created_at: String,
      updated_at: String,
    ) -> T,
  ): Query<T> = SelectRulesByAccountQuery(user_id, account_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getString(4),
      cursor.getString(5),
      cursor.getLong(6),
      cursor.getLong(7),
      cursor.getString(8),
      cursor.getString(9)!!,
      cursor.getString(10)!!
    )
  }

  public fun selectRulesByAccount(user_id: String, account_id: String?):
      Query<Goal_assignment_rules> = selectRulesByAccount(user_id, account_id) { id, user_id_,
      goal_id, merchant_name, account_id_, description_pattern, priority, is_default, rule_mode,
      created_at, updated_at ->
    Goal_assignment_rules(
      id,
      user_id_,
      goal_id,
      merchant_name,
      account_id_,
      description_pattern,
      priority,
      is_default,
      rule_mode,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> selectRulesByMerchantOrAccount(
    user_id: String,
    merchant_name: String?,
    account_id: String?,
    mapper: (
      id: Long,
      user_id: String,
      goal_id: String,
      merchant_name: String?,
      account_id: String?,
      description_pattern: String?,
      priority: Long?,
      is_default: Long?,
      rule_mode: String?,
      created_at: String,
      updated_at: String,
    ) -> T,
  ): Query<T> = SelectRulesByMerchantOrAccountQuery(user_id, merchant_name, account_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getString(4),
      cursor.getString(5),
      cursor.getLong(6),
      cursor.getLong(7),
      cursor.getString(8),
      cursor.getString(9)!!,
      cursor.getString(10)!!
    )
  }

  public fun selectRulesByMerchantOrAccount(
    user_id: String,
    merchant_name: String?,
    account_id: String?,
  ): Query<Goal_assignment_rules> = selectRulesByMerchantOrAccount(user_id, merchant_name,
      account_id) { id, user_id_, goal_id, merchant_name_, account_id_, description_pattern,
      priority, is_default, rule_mode, created_at, updated_at ->
    Goal_assignment_rules(
      id,
      user_id_,
      goal_id,
      merchant_name_,
      account_id_,
      description_pattern,
      priority,
      is_default,
      rule_mode,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> selectAssignmentByTransaction(transaction_id: String, mapper: (
    id: Long,
    transaction_id: String,
    goal_id: String,
    user_id: String,
    amount_allocated: Double,
    assignment_source: String?,
    rule_id: Long?,
    created_at: String,
  ) -> T): Query<T> = SelectAssignmentByTransactionQuery(transaction_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getLong(6),
      cursor.getString(7)!!
    )
  }

  public fun selectAssignmentByTransaction(transaction_id: String):
      Query<Transaction_goal_assignments> = selectAssignmentByTransaction(transaction_id) { id,
      transaction_id_, goal_id, user_id, amount_allocated, assignment_source, rule_id, created_at ->
    Transaction_goal_assignments(
      id,
      transaction_id_,
      goal_id,
      user_id,
      amount_allocated,
      assignment_source,
      rule_id,
      created_at
    )
  }

  public fun <T : Any> selectAssignmentsByGoal(goal_id: String, mapper: (
    id: Long,
    transaction_id: String,
    goal_id: String,
    user_id: String,
    amount_allocated: Double,
    assignment_source: String?,
    rule_id: Long?,
    created_at: String,
  ) -> T): Query<T> = SelectAssignmentsByGoalQuery(goal_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getLong(6),
      cursor.getString(7)!!
    )
  }

  public fun selectAssignmentsByGoal(goal_id: String): Query<Transaction_goal_assignments> =
      selectAssignmentsByGoal(goal_id) { id, transaction_id, goal_id_, user_id, amount_allocated,
      assignment_source, rule_id, created_at ->
    Transaction_goal_assignments(
      id,
      transaction_id,
      goal_id_,
      user_id,
      amount_allocated,
      assignment_source,
      rule_id,
      created_at
    )
  }

  public fun getTotalAssignedToGoal(goal_id: String): Query<Double> =
      GetTotalAssignedToGoalQuery(goal_id) { cursor ->
    cursor.getDouble(0)!!
  }

  public fun <T : Any> selectGoalAllocationsForUserInRange(
    user_id: String,
    date: String,
    date_: String,
    mapper: (
      amount_allocated: Double,
      goal_id: String,
      date: String,
      is_split_parent: Long,
      is_distributed_parent: Long,
      is_excluded: Long,
    ) -> T,
  ): Query<T> = SelectGoalAllocationsForUserInRangeQuery(user_id, date, date_) { cursor ->
    mapper(
      cursor.getDouble(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3)!!,
      cursor.getLong(4)!!,
      cursor.getLong(5)!!
    )
  }

  public fun selectGoalAllocationsForUserInRange(
    user_id: String,
    date: String,
    date_: String,
  ): Query<SelectGoalAllocationsForUserInRange> = selectGoalAllocationsForUserInRange(user_id, date,
      date_) { amount_allocated, goal_id, date__, is_split_parent, is_distributed_parent,
      is_excluded ->
    SelectGoalAllocationsForUserInRange(
      amount_allocated,
      goal_id,
      date__,
      is_split_parent,
      is_distributed_parent,
      is_excluded
    )
  }

  public fun <T : Any> selectAssignmentForChildrenOf(parent_transaction_id: String?, mapper: (
    id: Long,
    transaction_id: String,
    goal_id: String,
    user_id: String,
    amount_allocated: Double,
    assignment_source: String?,
    rule_id: Long?,
    created_at: String,
  ) -> T): Query<T> = SelectAssignmentForChildrenOfQuery(parent_transaction_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getLong(6),
      cursor.getString(7)!!
    )
  }

  public fun selectAssignmentForChildrenOf(parent_transaction_id: String?):
      Query<Transaction_goal_assignments> = selectAssignmentForChildrenOf(parent_transaction_id) {
      id, transaction_id, goal_id, user_id, amount_allocated, assignment_source, rule_id,
      created_at ->
    Transaction_goal_assignments(
      id,
      transaction_id,
      goal_id,
      user_id,
      amount_allocated,
      assignment_source,
      rule_id,
      created_at
    )
  }

  public fun selectGoalIdsAssignedInAccount(account_id: String, user_id: String): Query<String> =
      SelectGoalIdsAssignedInAccountQuery(account_id, user_id) { cursor ->
    cursor.getString(0)!!
  }

  public fun <T : Any> selectAllGoalAssignmentsByUser(user_id: String, mapper: (
    id: Long,
    transaction_id: String,
    goal_id: String,
    user_id: String,
    amount_allocated: Double,
    assignment_source: String?,
    rule_id: Long?,
    created_at: String,
  ) -> T): Query<T> = SelectAllGoalAssignmentsByUserQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getLong(6),
      cursor.getString(7)!!
    )
  }

  public fun selectAllGoalAssignmentsByUser(user_id: String): Query<Transaction_goal_assignments> =
      selectAllGoalAssignmentsByUser(user_id) { id, transaction_id, goal_id, user_id_,
      amount_allocated, assignment_source, rule_id, created_at ->
    Transaction_goal_assignments(
      id,
      transaction_id,
      goal_id,
      user_id_,
      amount_allocated,
      assignment_source,
      rule_id,
      created_at
    )
  }

  public fun <T : Any> selectAssignmentWithGoal(transaction_id: String, mapper: (
    id: Long,
    transaction_id: String,
    goal_id: String,
    user_id: String,
    amount_allocated: Double,
    assignment_source: String?,
    rule_id: Long?,
    created_at: String,
    goal_name: String,
    goal_icon: String?,
  ) -> T): Query<T> = SelectAssignmentWithGoalQuery(transaction_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getLong(6),
      cursor.getString(7)!!,
      cursor.getString(8)!!,
      cursor.getString(9)
    )
  }

  public fun selectAssignmentWithGoal(transaction_id: String): Query<SelectAssignmentWithGoal> =
      selectAssignmentWithGoal(transaction_id) { id, transaction_id_, goal_id, user_id,
      amount_allocated, assignment_source, rule_id, created_at, goal_name, goal_icon ->
    SelectAssignmentWithGoal(
      id,
      transaction_id_,
      goal_id,
      user_id,
      amount_allocated,
      assignment_source,
      rule_id,
      created_at,
      goal_name,
      goal_icon
    )
  }

  public fun <T : Any> selectAllAssignmentsWithGoalsByUser(user_id: String, mapper: (
    transaction_id: String,
    goal_id: String,
    amount_allocated: Double,
    goal_name: String,
    goal_icon: String?,
  ) -> T): Query<T> = SelectAllAssignmentsWithGoalsByUserQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getDouble(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)
    )
  }

  public fun selectAllAssignmentsWithGoalsByUser(user_id: String):
      Query<SelectAllAssignmentsWithGoalsByUser> = selectAllAssignmentsWithGoalsByUser(user_id) {
      transaction_id, goal_id, amount_allocated, goal_name, goal_icon ->
    SelectAllAssignmentsWithGoalsByUser(
      transaction_id,
      goal_id,
      amount_allocated,
      goal_name,
      goal_icon
    )
  }

  public fun <T : Any> selectGoalAssignmentPreferences(user_id: String, mapper: (
    merchant_key: String,
    goal_id: String,
    amount_allocated: Double,
    transaction_amount: Double,
  ) -> T): Query<T> = SelectGoalAssignmentPreferencesQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getDouble(2)!!,
      cursor.getDouble(3)!!
    )
  }

  public fun selectGoalAssignmentPreferences(user_id: String):
      Query<SelectGoalAssignmentPreferences> = selectGoalAssignmentPreferences(user_id) {
      merchant_key, goal_id, amount_allocated, transaction_amount ->
    SelectGoalAssignmentPreferences(
      merchant_key,
      goal_id,
      amount_allocated,
      transaction_amount
    )
  }

  public fun <T : Any> selectBudgetPlansByYear(
    user_id: String,
    year: Long,
    mapper: (
      id: String,
      user_id: String,
      year: Long,
      month: Long,
      category_type: String,
      category_name: String,
      planned_amount: Double,
      created_at: String,
      updated_at: String,
    ) -> T,
  ): Query<T> = SelectBudgetPlansByYearQuery(user_id, year) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2)!!,
      cursor.getLong(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getDouble(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8)!!
    )
  }

  public fun selectBudgetPlansByYear(user_id: String, year: Long): Query<Budget_plans> =
      selectBudgetPlansByYear(user_id, year) { id, user_id_, year_, month, category_type,
      category_name, planned_amount, created_at, updated_at ->
    Budget_plans(
      id,
      user_id_,
      year_,
      month,
      category_type,
      category_name,
      planned_amount,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> selectBudgetPlansByMonth(
    user_id: String,
    year: Long,
    month: Long,
    mapper: (
      id: String,
      user_id: String,
      year: Long,
      month: Long,
      category_type: String,
      category_name: String,
      planned_amount: Double,
      created_at: String,
      updated_at: String,
    ) -> T,
  ): Query<T> = SelectBudgetPlansByMonthQuery(user_id, year, month) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2)!!,
      cursor.getLong(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getDouble(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8)!!
    )
  }

  public fun selectBudgetPlansByMonth(
    user_id: String,
    year: Long,
    month: Long,
  ): Query<Budget_plans> = selectBudgetPlansByMonth(user_id, year, month) { id, user_id_, year_,
      month_, category_type, category_name, planned_amount, created_at, updated_at ->
    Budget_plans(
      id,
      user_id_,
      year_,
      month_,
      category_type,
      category_name,
      planned_amount,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> selectBudgetPlansByType(
    user_id: String,
    year: Long,
    category_type: String,
    mapper: (
      id: String,
      user_id: String,
      year: Long,
      month: Long,
      category_type: String,
      category_name: String,
      planned_amount: Double,
      created_at: String,
      updated_at: String,
    ) -> T,
  ): Query<T> = SelectBudgetPlansByTypeQuery(user_id, year, category_type) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2)!!,
      cursor.getLong(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getDouble(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8)!!
    )
  }

  public fun selectBudgetPlansByType(
    user_id: String,
    year: Long,
    category_type: String,
  ): Query<Budget_plans> = selectBudgetPlansByType(user_id, year, category_type) { id, user_id_,
      year_, month, category_type_, category_name, planned_amount, created_at, updated_at ->
    Budget_plans(
      id,
      user_id_,
      year_,
      month,
      category_type_,
      category_name,
      planned_amount,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> getYearlyTotalsByType(
    user_id: String,
    year: Long,
    mapper: (
      category_type: String,
      category_name: String,
      total_amount: Double?,
    ) -> T,
  ): Query<T> = GetYearlyTotalsByTypeQuery(user_id, year) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getDouble(2)
    )
  }

  public fun getYearlyTotalsByType(user_id: String, year: Long): Query<GetYearlyTotalsByType> =
      getYearlyTotalsByType(user_id, year) { category_type, category_name, total_amount ->
    GetYearlyTotalsByType(
      category_type,
      category_name,
      total_amount
    )
  }

  public fun <T : Any> getMonthlyTotals(
    user_id: String,
    year: Long,
    mapper: (
      month: Long,
      category_type: String,
      total_amount: Double?,
    ) -> T,
  ): Query<T> = GetMonthlyTotalsQuery(user_id, year) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getDouble(2)
    )
  }

  public fun getMonthlyTotals(user_id: String, year: Long): Query<GetMonthlyTotals> =
      getMonthlyTotals(user_id, year) { month, category_type, total_amount ->
    GetMonthlyTotals(
      month,
      category_type,
      total_amount
    )
  }

  public fun <T : Any> selectBudgetPlansForCopy(
    user_id: String,
    year: Long,
    mapper: (
      category_type: String,
      category_name: String,
      month: Long,
      planned_amount: Double,
    ) -> T,
  ): Query<T> = SelectBudgetPlansForCopyQuery(user_id, year) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2)!!,
      cursor.getDouble(3)!!
    )
  }

  public fun selectBudgetPlansForCopy(user_id: String, year: Long): Query<SelectBudgetPlansForCopy>
      = selectBudgetPlansForCopy(user_id, year) { category_type, category_name, month,
      planned_amount ->
    SelectBudgetPlansForCopy(
      category_type,
      category_name,
      month,
      planned_amount
    )
  }

  public fun <T : Any> selectAdviceCardsByCategory(category: String, mapper: (
    id: String,
    category: String,
    title: String,
    content: String,
    icon: String?,
    priority: Long?,
    is_active: Long?,
    country: String?,
  ) -> T): Query<T> = SelectAdviceCardsByCategoryQuery(category) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getLong(5),
      cursor.getLong(6),
      cursor.getString(7)
    )
  }

  public fun selectAdviceCardsByCategory(category: String): Query<Advice_cards> =
      selectAdviceCardsByCategory(category) { id, category_, title, content, icon, priority,
      is_active, country ->
    Advice_cards(
      id,
      category_,
      title,
      content,
      icon,
      priority,
      is_active,
      country
    )
  }

  public fun <T : Any> selectAllActiveAdviceCards(mapper: (
    id: String,
    category: String,
    title: String,
    content: String,
    icon: String?,
    priority: Long?,
    is_active: Long?,
    country: String?,
  ) -> T): Query<T> = Query(-1_830_379_910, arrayOf("advice_cards"), driver, "schema.sq",
      "selectAllActiveAdviceCards", """
  |SELECT advice_cards.id, advice_cards.category, advice_cards.title, advice_cards.content, advice_cards.icon, advice_cards.priority, advice_cards.is_active, advice_cards.country FROM advice_cards
  |WHERE is_active = 1
  |ORDER BY category, priority ASC
  """.trimMargin()) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getLong(5),
      cursor.getLong(6),
      cursor.getString(7)
    )
  }

  public fun selectAllActiveAdviceCards(): Query<Advice_cards> = selectAllActiveAdviceCards { id,
      category, title, content, icon, priority, is_active, country ->
    Advice_cards(
      id,
      category,
      title,
      content,
      icon,
      priority,
      is_active,
      country
    )
  }

  public fun <T : Any> selectPinnedCards(user_id: String, mapper: (
    id: String,
    category: String,
    title: String,
    content: String,
    icon: String?,
    priority: Long?,
    is_active: Long?,
    country: String?,
  ) -> T): Query<T> = SelectPinnedCardsQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getLong(5),
      cursor.getLong(6),
      cursor.getString(7)
    )
  }

  public fun selectPinnedCards(user_id: String): Query<Advice_cards> = selectPinnedCards(user_id) {
      id, category, title, content, icon, priority, is_active, country ->
    Advice_cards(
      id,
      category,
      title,
      content,
      icon,
      priority,
      is_active,
      country
    )
  }

  public fun <T : Any> selectDismissedCards(user_id: String, mapper: (
    id: String,
    category: String,
    title: String,
    content: String,
    icon: String?,
    priority: Long?,
    is_active: Long?,
    country: String?,
  ) -> T): Query<T> = SelectDismissedCardsQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getLong(5),
      cursor.getLong(6),
      cursor.getString(7)
    )
  }

  public fun selectDismissedCards(user_id: String): Query<Advice_cards> =
      selectDismissedCards(user_id) { id, category, title, content, icon, priority, is_active,
      country ->
    Advice_cards(
      id,
      category,
      title,
      content,
      icon,
      priority,
      is_active,
      country
    )
  }

  public fun <T : Any> selectAvailableCards(
    user_id: String,
    country: String?,
    mapper: (
      id: String,
      category: String,
      title: String,
      content: String,
      icon: String?,
      priority: Long?,
      is_active: Long?,
      country: String?,
    ) -> T,
  ): Query<T> = SelectAvailableCardsQuery(user_id, country) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getLong(5),
      cursor.getLong(6),
      cursor.getString(7)
    )
  }

  public fun selectAvailableCards(user_id: String, country: String?): Query<Advice_cards> =
      selectAvailableCards(user_id, country) { id, category, title, content, icon, priority,
      is_active, country_ ->
    Advice_cards(
      id,
      category,
      title,
      content,
      icon,
      priority,
      is_active,
      country_
    )
  }

  public fun getUserCountry(user_id: String): Query<String> = GetUserCountryQuery(user_id) {
      cursor ->
    cursor.getString(0)!!
  }

  public fun <T : Any> getUserCardStatus(
    user_id: String,
    card_id: String,
    mapper: (
      id: String,
      user_id: String,
      card_id: String,
      is_pinned: Long?,
      is_dismissed: Long?,
      viewed_at: String?,
      created_at: String,
    ) -> T,
  ): Query<T> = GetUserCardStatusQuery(user_id, card_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3),
      cursor.getLong(4),
      cursor.getString(5),
      cursor.getString(6)!!
    )
  }

  public fun getUserCardStatus(user_id: String, card_id: String): Query<User_advice_cards> =
      getUserCardStatus(user_id, card_id) { id, user_id_, card_id_, is_pinned, is_dismissed,
      viewed_at, created_at ->
    User_advice_cards(
      id,
      user_id_,
      card_id_,
      is_pinned,
      is_dismissed,
      viewed_at,
      created_at
    )
  }

  public fun getUserSetting(user_id: String, setting_key: String): Query<String> =
      GetUserSettingQuery(user_id, setting_key) { cursor ->
    cursor.getString(0)!!
  }

  public fun <T : Any> getAllUserSettings(user_id: String, mapper: (setting_key: String,
      setting_value: String) -> T): Query<T> = GetAllUserSettingsQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!
    )
  }

  public fun getAllUserSettings(user_id: String): Query<GetAllUserSettings> =
      getAllUserSettings(user_id) { setting_key, setting_value ->
    GetAllUserSettings(
      setting_key,
      setting_value
    )
  }

  public fun getDismissedPersonalizedCards(user_id: String): Query<String> =
      GetDismissedPersonalizedCardsQuery(user_id) { cursor ->
    cursor.getString(0)!!
  }

  public fun isPersonalizedCardDismissed(user_id: String, card_id: String): Query<Long> =
      IsPersonalizedCardDismissedQuery(user_id, card_id) { cursor ->
    cursor.getLong(0)!!
  }

  public fun <T : Any> getPersonalizedCardView(
    user_id: String,
    card_id: String,
    mapper: (
      id: Long,
      user_id: String,
      card_id: String,
      shown_at: String,
      shown_count: Long,
    ) -> T,
  ): Query<T> = GetPersonalizedCardViewQuery(user_id, card_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getLong(4)!!
    )
  }

  public fun getPersonalizedCardView(user_id: String, card_id: String):
      Query<Personalized_card_views> = getPersonalizedCardView(user_id, card_id) { id, user_id_,
      card_id_, shown_at, shown_count ->
    Personalized_card_views(
      id,
      user_id_,
      card_id_,
      shown_at,
      shown_count
    )
  }

  public fun <T : Any> getAllPersonalizedCardViews(user_id: String, mapper: (
    card_id: String,
    shown_at: String,
    shown_count: Long,
  ) -> T): Query<T> = GetAllPersonalizedCardViewsQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getLong(2)!!
    )
  }

  public fun getAllPersonalizedCardViews(user_id: String): Query<GetAllPersonalizedCardViews> =
      getAllPersonalizedCardViews(user_id) { card_id, shown_at, shown_count ->
    GetAllPersonalizedCardViews(
      card_id,
      shown_at,
      shown_count
    )
  }

  public fun <T : Any> getCustomCategoriesForUser(user_id: String, mapper: (
    id: Long,
    user_id: String,
    primary_name: String,
    secondary_name: String,
    is_income: Long,
    icon: String?,
    created_at: String,
  ) -> T): Query<T> = GetCustomCategoriesForUserQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getLong(4)!!,
      cursor.getString(5),
      cursor.getString(6)!!
    )
  }

  public fun getCustomCategoriesForUser(user_id: String): Query<GetCustomCategoriesForUser> =
      getCustomCategoriesForUser(user_id) { id, user_id_, primary_name, secondary_name, is_income,
      icon, created_at ->
    GetCustomCategoriesForUser(
      id,
      user_id_,
      primary_name,
      secondary_name,
      is_income,
      icon,
      created_at
    )
  }

  public fun <T : Any> getCustomCategoryById(
    id: Long,
    user_id: String,
    mapper: (
      id: Long,
      user_id: String,
      primary_name: String,
      secondary_name: String,
      is_income: Long,
      icon: String?,
      created_at: String,
    ) -> T,
  ): Query<T> = GetCustomCategoryByIdQuery(id, user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getLong(4)!!,
      cursor.getString(5),
      cursor.getString(6)!!
    )
  }

  public fun getCustomCategoryById(id: Long, user_id: String): Query<GetCustomCategoryById> =
      getCustomCategoryById(id, user_id) { id_, user_id_, primary_name, secondary_name, is_income,
      icon, created_at ->
    GetCustomCategoryById(
      id_,
      user_id_,
      primary_name,
      secondary_name,
      is_income,
      icon,
      created_at
    )
  }

  public fun countTransactionsForUserCategory(user_id: String, user_category: String?): Query<Long>
      = CountTransactionsForUserCategoryQuery(user_id, user_category) { cursor ->
    cursor.getLong(0)!!
  }

  public fun <T : Any> listTransactionsForUserCategory(
    user_id: String,
    user_category: String?,
    `value`: Long,
    mapper: (
      transaction_id: String,
      merchant: String,
      amount: Double,
      date: String,
    ) -> T,
  ): Query<T> = ListTransactionsForUserCategoryQuery(user_id, user_category, value) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getDouble(2)!!,
      cursor.getString(3)!!
    )
  }

  public fun listTransactionsForUserCategory(
    user_id: String,
    user_category: String?,
    value_: Long,
  ): Query<ListTransactionsForUserCategory> = listTransactionsForUserCategory(user_id,
      user_category, value_) { transaction_id, merchant, amount, date ->
    ListTransactionsForUserCategory(
      transaction_id,
      merchant,
      amount,
      date
    )
  }

  public fun <T : Any> listFullTransactionsForUserCategory(
    user_id: String,
    user_category: String?,
    mapper: (
      transaction_id: String,
      name: String,
      merchant_name: String?,
      amount: Double,
      original_description: String?,
    ) -> T,
  ): Query<T> = ListFullTransactionsForUserCategoryQuery(user_id, user_category) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getDouble(3)!!,
      cursor.getString(4)
    )
  }

  public fun listFullTransactionsForUserCategory(user_id: String, user_category: String?):
      Query<ListFullTransactionsForUserCategory> = listFullTransactionsForUserCategory(user_id,
      user_category) { transaction_id, name, merchant_name, amount, original_description ->
    ListFullTransactionsForUserCategory(
      transaction_id,
      name,
      merchant_name,
      amount,
      original_description
    )
  }

  public fun <T : Any> selectSubscriptionMerchants(user_id: String, mapper: (
    id: Long,
    user_id: String,
    merchant_key: String,
    typical_amount: Double?,
    status: String,
    confirmed_at: String?,
    created_at: String,
  ) -> T): Query<T> = SelectSubscriptionMerchantsQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getDouble(3),
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6)!!
    )
  }

  public fun selectSubscriptionMerchants(user_id: String): Query<Subscription_merchants> =
      selectSubscriptionMerchants(user_id) { id, user_id_, merchant_key, typical_amount, status,
      confirmed_at, created_at ->
    Subscription_merchants(
      id,
      user_id_,
      merchant_key,
      typical_amount,
      status,
      confirmed_at,
      created_at
    )
  }

  public fun <T : Any> selectConfirmedSubscriptions(user_id: String, mapper: (
    id: Long,
    user_id: String,
    merchant_key: String,
    typical_amount: Double?,
    status: String,
    confirmed_at: String?,
    created_at: String,
  ) -> T): Query<T> = SelectConfirmedSubscriptionsQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getDouble(3),
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6)!!
    )
  }

  public fun selectConfirmedSubscriptions(user_id: String): Query<Subscription_merchants> =
      selectConfirmedSubscriptions(user_id) { id, user_id_, merchant_key, typical_amount, status,
      confirmed_at, created_at ->
    Subscription_merchants(
      id,
      user_id_,
      merchant_key,
      typical_amount,
      status,
      confirmed_at,
      created_at
    )
  }

  public fun <T : Any> getTemplateBySignature(
    user_id: String,
    institution_signature: String,
    mapper: (
      id: Long,
      user_id: String,
      institution_signature: String,
      institution_alias: String?,
      file_type: String,
      column_mapping: String?,
      date_format: String?,
      delimiter: String?,
      template_data: String?,
      is_valid: Long,
      created_at: String,
      updated_at: String,
    ) -> T,
  ): Query<T> = GetTemplateBySignatureQuery(user_id, institution_signature) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7),
      cursor.getString(8),
      cursor.getLong(9)!!,
      cursor.getString(10)!!,
      cursor.getString(11)!!
    )
  }

  public fun getTemplateBySignature(user_id: String, institution_signature: String):
      Query<Statement_templates> = getTemplateBySignature(user_id, institution_signature) { id,
      user_id_, institution_signature_, institution_alias, file_type, column_mapping, date_format,
      delimiter, template_data, is_valid, created_at, updated_at ->
    Statement_templates(
      id,
      user_id_,
      institution_signature_,
      institution_alias,
      file_type,
      column_mapping,
      date_format,
      delimiter,
      template_data,
      is_valid,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> getTemplatesForUser(user_id: String, mapper: (
    id: Long,
    user_id: String,
    institution_signature: String,
    institution_alias: String?,
    file_type: String,
    column_mapping: String?,
    date_format: String?,
    delimiter: String?,
    template_data: String?,
    is_valid: Long,
    created_at: String,
    updated_at: String,
  ) -> T): Query<T> = GetTemplatesForUserQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7),
      cursor.getString(8),
      cursor.getLong(9)!!,
      cursor.getString(10)!!,
      cursor.getString(11)!!
    )
  }

  public fun getTemplatesForUser(user_id: String): Query<Statement_templates> =
      getTemplatesForUser(user_id) { id, user_id_, institution_signature, institution_alias,
      file_type, column_mapping, date_format, delimiter, template_data, is_valid, created_at,
      updated_at ->
    Statement_templates(
      id,
      user_id_,
      institution_signature,
      institution_alias,
      file_type,
      column_mapping,
      date_format,
      delimiter,
      template_data,
      is_valid,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> getManualAccountsForUser(user_id: String, mapper: (
    id: Long,
    account_id: String,
    user_id: String,
    institution_name: String,
    account_name: String,
    account_type: String,
    balance: Double,
    template_id: Long?,
    created_at: String,
  ) -> T): Query<T> = GetManualAccountsForUserQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getDouble(6)!!,
      cursor.getLong(7),
      cursor.getString(8)!!
    )
  }

  public fun getManualAccountsForUser(user_id: String): Query<Manual_accounts> =
      getManualAccountsForUser(user_id) { id, account_id, user_id_, institution_name, account_name,
      account_type, balance, template_id, created_at ->
    Manual_accounts(
      id,
      account_id,
      user_id_,
      institution_name,
      account_name,
      account_type,
      balance,
      template_id,
      created_at
    )
  }

  public fun <T : Any> getManualAccount(
    account_id: String,
    user_id: String,
    mapper: (
      id: Long,
      account_id: String,
      user_id: String,
      institution_name: String,
      account_name: String,
      account_type: String,
      balance: Double,
      template_id: Long?,
      created_at: String,
    ) -> T,
  ): Query<T> = GetManualAccountQuery(account_id, user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getDouble(6)!!,
      cursor.getLong(7),
      cursor.getString(8)!!
    )
  }

  public fun getManualAccount(account_id: String, user_id: String): Query<Manual_accounts> =
      getManualAccount(account_id, user_id) { id, account_id_, user_id_, institution_name,
      account_name, account_type, balance, template_id, created_at ->
    Manual_accounts(
      id,
      account_id_,
      user_id_,
      institution_name,
      account_name,
      account_type,
      balance,
      template_id,
      created_at
    )
  }

  public fun <T : Any> getManualAccountByAccountId(account_id: String, mapper: (
    id: Long,
    account_id: String,
    user_id: String,
    institution_name: String,
    account_name: String,
    account_type: String,
    balance: Double,
    template_id: Long?,
    created_at: String,
  ) -> T): Query<T> = GetManualAccountByAccountIdQuery(account_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getDouble(6)!!,
      cursor.getLong(7),
      cursor.getString(8)!!
    )
  }

  public fun getManualAccountByAccountId(account_id: String): Query<Manual_accounts> =
      getManualAccountByAccountId(account_id) { id, account_id_, user_id, institution_name,
      account_name, account_type, balance, template_id, created_at ->
    Manual_accounts(
      id,
      account_id_,
      user_id,
      institution_name,
      account_name,
      account_type,
      balance,
      template_id,
      created_at
    )
  }

  public fun <T : Any> getTransactionsForAccount(
    account_id: String,
    user_id: String,
    mapper: (
      id: Long,
      transaction_id: String,
      account_id: String,
      user_id: String,
      amount: Double,
      iso_currency_code: String?,
      unofficial_currency_code: String?,
      date: String,
      authorized_date: String?,
      authorized_datetime: String?,
      datetime: String?,
      name: String,
      merchant_name: String?,
      original_description: String?,
      category: String?,
      category_id: String?,
      personal_finance_category_primary: String?,
      personal_finance_category_detailed: String?,
      personal_finance_category_confidence: String?,
      transaction_type: String?,
      transaction_code: String?,
      pending: Long,
      pending_transaction_id: String?,
      location_json: String?,
      payment_meta_json: String?,
      account_owner: String?,
      created_at: String,
      updated_at: String,
      last_synced_at: String?,
      user_category: String?,
      embedding: ByteArray?,
      embedding_version: Long?,
      user_notes: String?,
      is_hidden: Long,
      is_expense_report: Long,
      tags: String?,
      ai_category: String?,
      confidence_score: Double?,
      original_ai_category: String?,
      corrected_at: String?,
      correction_confidence: Double?,
      ml_model_version: String?,
      category_source: String?,
      parent_transaction_id: String?,
      child_type: String?,
      is_split_parent: Long,
      is_distributed_parent: Long,
      split_order: Long?,
      distribution_start_month: String?,
      distribution_end_month: String?,
      distribution_month_index: Long?,
      distribution_total_months: Long?,
      is_verified: Long,
      verified_at: String?,
      is_excluded: Long,
      notified_at: String?,
    ) -> T,
  ): Query<T> = GetTransactionsForAccountQuery(account_id, user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun getTransactionsForAccount(account_id: String, user_id: String): Query<Transactions> =
      getTransactionsForAccount(account_id, user_id) { id, transaction_id, account_id_, user_id_,
      amount, iso_currency_code, unofficial_currency_code, date, authorized_date,
      authorized_datetime, datetime, name, merchant_name, original_description, category,
      category_id, personal_finance_category_primary, personal_finance_category_detailed,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source, parent_transaction_id,
      child_type, is_split_parent, is_distributed_parent, split_order, distribution_start_month,
      distribution_end_month, distribution_month_index, distribution_total_months, is_verified,
      verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id_,
      user_id_,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun lastInsertRowId(): ExecutableQuery<Long> = Query(-1_578_240_884, driver, "schema.sq",
      "lastInsertRowId", "SELECT last_insert_rowid()") { cursor ->
    cursor.getLong(0)!!
  }

  public fun <T : Any> getNotificationToken(
    token: String,
    expires_at: String,
    mapper: (
      id: Long,
      user_id: String,
      token: String,
      transaction_ids: String,
      phone_number: String?,
      action: String?,
      created_at: String,
      expires_at: String,
      acted_at: String?,
    ) -> T,
  ): Query<T> = GetNotificationTokenQuery(token, expires_at) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getString(5),
      cursor.getString(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8)
    )
  }

  public fun getNotificationToken(token: String, expires_at: String): Query<Notification_tokens> =
      getNotificationToken(token, expires_at) { id, user_id, token_, transaction_ids, phone_number,
      action, created_at, expires_at_, acted_at ->
    Notification_tokens(
      id,
      user_id,
      token_,
      transaction_ids,
      phone_number,
      action,
      created_at,
      expires_at_,
      acted_at
    )
  }

  public fun <T : Any> getNotificationTokenByPhone(
    phone_number: String?,
    expires_at: String,
    mapper: (
      id: Long,
      user_id: String,
      token: String,
      transaction_ids: String,
      phone_number: String?,
      action: String?,
      created_at: String,
      expires_at: String,
      acted_at: String?,
    ) -> T,
  ): Query<T> = GetNotificationTokenByPhoneQuery(phone_number, expires_at) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getString(5),
      cursor.getString(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8)
    )
  }

  public fun getNotificationTokenByPhone(phone_number: String?, expires_at: String):
      Query<Notification_tokens> = getNotificationTokenByPhone(phone_number, expires_at) { id,
      user_id, token, transaction_ids, phone_number_, action, created_at, expires_at_, acted_at ->
    Notification_tokens(
      id,
      user_id,
      token,
      transaction_ids,
      phone_number_,
      action,
      created_at,
      expires_at_,
      acted_at
    )
  }

  public fun <T : Any> getNotificationTokenById(token: String, mapper: (
    id: Long,
    user_id: String,
    token: String,
    transaction_ids: String,
    phone_number: String?,
    action: String?,
    created_at: String,
    expires_at: String,
    acted_at: String?,
  ) -> T): Query<T> = GetNotificationTokenByIdQuery(token) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getString(5),
      cursor.getString(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8)
    )
  }

  public fun getNotificationTokenById(token: String): Query<Notification_tokens> =
      getNotificationTokenById(token) { id, user_id, token_, transaction_ids, phone_number, action,
      created_at, expires_at, acted_at ->
    Notification_tokens(
      id,
      user_id,
      token_,
      transaction_ids,
      phone_number,
      action,
      created_at,
      expires_at,
      acted_at
    )
  }

  public fun <T : Any> getUnnotifiedTransactions(user_id: String, mapper: (
    id: Long,
    transaction_id: String,
    account_id: String,
    user_id: String,
    amount: Double,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    date: String,
    authorized_date: String?,
    authorized_datetime: String?,
    datetime: String?,
    name: String,
    merchant_name: String?,
    original_description: String?,
    category: String?,
    category_id: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    personal_finance_category_confidence: String?,
    transaction_type: String?,
    transaction_code: String?,
    pending: Long,
    pending_transaction_id: String?,
    location_json: String?,
    payment_meta_json: String?,
    account_owner: String?,
    created_at: String,
    updated_at: String,
    last_synced_at: String?,
    user_category: String?,
    embedding: ByteArray?,
    embedding_version: Long?,
    user_notes: String?,
    is_hidden: Long,
    is_expense_report: Long,
    tags: String?,
    ai_category: String?,
    confidence_score: Double?,
    original_ai_category: String?,
    corrected_at: String?,
    correction_confidence: Double?,
    ml_model_version: String?,
    category_source: String?,
    parent_transaction_id: String?,
    child_type: String?,
    is_split_parent: Long,
    is_distributed_parent: Long,
    split_order: Long?,
    distribution_start_month: String?,
    distribution_end_month: String?,
    distribution_month_index: Long?,
    distribution_total_months: Long?,
    is_verified: Long,
    verified_at: String?,
    is_excluded: Long,
    notified_at: String?,
  ) -> T): Query<T> = GetUnnotifiedTransactionsQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7)!!,
      cursor.getString(8),
      cursor.getString(9),
      cursor.getString(10),
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getString(15),
      cursor.getString(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getString(20),
      cursor.getLong(21)!!,
      cursor.getString(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)!!,
      cursor.getString(27)!!,
      cursor.getString(28),
      cursor.getString(29),
      cursor.getBytes(30),
      cursor.getLong(31),
      cursor.getString(32),
      cursor.getLong(33)!!,
      cursor.getLong(34)!!,
      cursor.getString(35),
      cursor.getString(36),
      cursor.getDouble(37),
      cursor.getString(38),
      cursor.getString(39),
      cursor.getDouble(40),
      cursor.getString(41),
      cursor.getString(42),
      cursor.getString(43),
      cursor.getString(44),
      cursor.getLong(45)!!,
      cursor.getLong(46)!!,
      cursor.getLong(47),
      cursor.getString(48),
      cursor.getString(49),
      cursor.getLong(50),
      cursor.getLong(51),
      cursor.getLong(52)!!,
      cursor.getString(53),
      cursor.getLong(54)!!,
      cursor.getString(55)
    )
  }

  public fun getUnnotifiedTransactions(user_id: String): Query<Transactions> =
      getUnnotifiedTransactions(user_id) { id, transaction_id, account_id, user_id_, amount,
      iso_currency_code, unofficial_currency_code, date, authorized_date, authorized_datetime,
      datetime, name, merchant_name, original_description, category, category_id,
      personal_finance_category_primary, personal_finance_category_detailed,
      personal_finance_category_confidence, transaction_type, transaction_code, pending,
      pending_transaction_id, location_json, payment_meta_json, account_owner, created_at,
      updated_at, last_synced_at, user_category, embedding, embedding_version, user_notes,
      is_hidden, is_expense_report, tags, ai_category, confidence_score, original_ai_category,
      corrected_at, correction_confidence, ml_model_version, category_source, parent_transaction_id,
      child_type, is_split_parent, is_distributed_parent, split_order, distribution_start_month,
      distribution_end_month, distribution_month_index, distribution_total_months, is_verified,
      verified_at, is_excluded, notified_at ->
    Transactions(
      id,
      transaction_id,
      account_id,
      user_id_,
      amount,
      iso_currency_code,
      unofficial_currency_code,
      date,
      authorized_date,
      authorized_datetime,
      datetime,
      name,
      merchant_name,
      original_description,
      category,
      category_id,
      personal_finance_category_primary,
      personal_finance_category_detailed,
      personal_finance_category_confidence,
      transaction_type,
      transaction_code,
      pending,
      pending_transaction_id,
      location_json,
      payment_meta_json,
      account_owner,
      created_at,
      updated_at,
      last_synced_at,
      user_category,
      embedding,
      embedding_version,
      user_notes,
      is_hidden,
      is_expense_report,
      tags,
      ai_category,
      confidence_score,
      original_ai_category,
      corrected_at,
      correction_confidence,
      ml_model_version,
      category_source,
      parent_transaction_id,
      child_type,
      is_split_parent,
      is_distributed_parent,
      split_order,
      distribution_start_month,
      distribution_end_month,
      distribution_month_index,
      distribution_total_months,
      is_verified,
      verified_at,
      is_excluded,
      notified_at
    )
  }

  public fun getRecentTransactionIdsForUser(user_id: String, `value`: Long): Query<String> =
      GetRecentTransactionIdsForUserQuery(user_id, value) { cursor ->
    cursor.getString(0)!!
  }

  public fun <T : Any> getLastActivity(user_id: String, mapper: (last_activity_at: String?) -> T):
      Query<T> = GetLastActivityQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)
    )
  }

  public fun getLastActivity(user_id: String): Query<GetLastActivity> = getLastActivity(user_id) {
      last_activity_at ->
    GetLastActivity(
      last_activity_at
    )
  }

  public fun <T : Any> getProUsersWithNotifications(mapper: (
    user_id: String,
    email: String?,
    username: String,
    phone_number: String?,
    last_activity_at: String?,
    setting_value: String,
  ) -> T): Query<T> = Query(-315_898_101, arrayOf("users", "user_settings"), driver, "schema.sq",
      "getProUsersWithNotifications", """
  |SELECT u.user_id, u.email, u.username, u.phone_number, u.last_activity_at, us.setting_value
  |FROM users u
  |JOIN user_settings us ON u.user_id = us.user_id AND us.setting_key = 'notification_preferences'
  |WHERE u.subscription_tier = 'pro'
  """.trimMargin()) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1),
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getString(4),
      cursor.getString(5)!!
    )
  }

  public fun getProUsersWithNotifications(): Query<GetProUsersWithNotifications> =
      getProUsersWithNotifications { user_id, email, username, phone_number, last_activity_at,
      setting_value ->
    GetProUsersWithNotifications(
      user_id,
      email,
      username,
      phone_number,
      last_activity_at,
      setting_value
    )
  }

  public fun <T : Any> selectContextsByUser(user_id: String, mapper: (
    id: String,
    user_id: String,
    name: String,
    color: String,
    created_at: String,
    updated_at: String,
  ) -> T): Query<T> = SelectContextsByUserQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!
    )
  }

  public fun selectContextsByUser(user_id: String): Query<Contexts> =
      selectContextsByUser(user_id) { id, user_id_, name, color, created_at, updated_at ->
    Contexts(
      id,
      user_id_,
      name,
      color,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> selectContextById(id: String, mapper: (
    id: String,
    user_id: String,
    name: String,
    color: String,
    created_at: String,
    updated_at: String,
  ) -> T): Query<T> = SelectContextByIdQuery(id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!
    )
  }

  public fun selectContextById(id: String): Query<Contexts> = selectContextById(id) { id_, user_id,
      name, color, created_at, updated_at ->
    Contexts(
      id_,
      user_id,
      name,
      color,
      created_at,
      updated_at
    )
  }

  public fun countTransactionsForContext(context_id: String): Query<Long> =
      CountTransactionsForContextQuery(context_id) { cursor ->
    cursor.getLong(0)!!
  }

  public fun selectContextIdsForTransaction(transaction_id: String): Query<String> =
      SelectContextIdsForTransactionQuery(transaction_id) { cursor ->
    cursor.getString(0)!!
  }

  public fun <T : Any> selectContextIdsForUserTransactions(user_id: String,
      mapper: (transaction_id: String, context_id: String) -> T): Query<T> =
      SelectContextIdsForUserTransactionsQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!
    )
  }

  public fun selectContextIdsForUserTransactions(user_id: String):
      Query<SelectContextIdsForUserTransactions> = selectContextIdsForUserTransactions(user_id) {
      transaction_id, context_id ->
    SelectContextIdsForUserTransactions(
      transaction_id,
      context_id
    )
  }

  public fun <T : Any> selectContextsForTransaction(transaction_id: String, mapper: (
    id: String,
    user_id: String,
    name: String,
    color: String,
    created_at: String,
    updated_at: String,
  ) -> T): Query<T> = SelectContextsForTransactionQuery(transaction_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!
    )
  }

  public fun selectContextsForTransaction(transaction_id: String): Query<Contexts> =
      selectContextsForTransaction(transaction_id) { id, user_id, name, color, created_at,
      updated_at ->
    Contexts(
      id,
      user_id,
      name,
      color,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> getLatestWiz3RCardGeneration(
    user_id: String,
    card_id: String,
    mapper: (
      id: Long,
      user_id: String,
      card_id: String,
      generated_at: String,
      context_json: String,
      response_json: String,
    ) -> T,
  ): Query<T> = GetLatestWiz3RCardGenerationQuery(user_id, card_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!
    )
  }

  public fun getLatestWiz3RCardGeneration(user_id: String, card_id: String):
      Query<Wiz3r_card_generations> = getLatestWiz3RCardGeneration(user_id, card_id) { id, user_id_,
      card_id_, generated_at, context_json, response_json ->
    Wiz3r_card_generations(
      id,
      user_id_,
      card_id_,
      generated_at,
      context_json,
      response_json
    )
  }

  public fun <T : Any> getLatestWiz3RCardGenerationSince(
    user_id: String,
    card_id: String,
    generated_at: String,
    mapper: (
      id: Long,
      user_id: String,
      card_id: String,
      generated_at: String,
      context_json: String,
      response_json: String,
    ) -> T,
  ): Query<T> = GetLatestWiz3RCardGenerationSinceQuery(user_id, card_id, generated_at) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!
    )
  }

  public fun getLatestWiz3RCardGenerationSince(
    user_id: String,
    card_id: String,
    generated_at: String,
  ): Query<Wiz3r_card_generations> = getLatestWiz3RCardGenerationSince(user_id, card_id,
      generated_at) { id, user_id_, card_id_, generated_at_, context_json, response_json ->
    Wiz3r_card_generations(
      id,
      user_id_,
      card_id_,
      generated_at_,
      context_json,
      response_json
    )
  }

  public fun <T : Any> getLatestWiz3RCardGenerationForUser(user_id: String, mapper: (
    id: Long,
    user_id: String,
    card_id: String,
    generated_at: String,
    context_json: String,
    response_json: String,
  ) -> T): Query<T> = GetLatestWiz3RCardGenerationForUserQuery(user_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!
    )
  }

  public fun getLatestWiz3RCardGenerationForUser(user_id: String): Query<Wiz3r_card_generations> =
      getLatestWiz3RCardGenerationForUser(user_id) { id, user_id_, card_id, generated_at,
      context_json, response_json ->
    Wiz3r_card_generations(
      id,
      user_id_,
      card_id,
      generated_at,
      context_json,
      response_json
    )
  }

  public fun <T : Any> getLatestWiz3RCardGenerationForUserWithPrefix(
    user_id: String,
    `value`: String,
    mapper: (
      id: Long,
      user_id: String,
      card_id: String,
      generated_at: String,
      context_json: String,
      response_json: String,
    ) -> T,
  ): Query<T> = GetLatestWiz3RCardGenerationForUserWithPrefixQuery(user_id, value) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!
    )
  }

  public fun getLatestWiz3RCardGenerationForUserWithPrefix(user_id: String, value_: String):
      Query<Wiz3r_card_generations> = getLatestWiz3RCardGenerationForUserWithPrefix(user_id,
      value_) { id, user_id_, card_id, generated_at, context_json, response_json ->
    Wiz3r_card_generations(
      id,
      user_id_,
      card_id,
      generated_at,
      context_json,
      response_json
    )
  }

  public fun <T : Any> getRecentWiz3RCardGenerations(
    user_id: String,
    `value`: Long,
    mapper: (
      id: Long,
      user_id: String,
      card_id: String,
      generated_at: String,
      context_json: String,
      response_json: String,
    ) -> T,
  ): Query<T> = GetRecentWiz3RCardGenerationsQuery(user_id, value) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!
    )
  }

  public fun getRecentWiz3RCardGenerations(user_id: String, value_: Long):
      Query<Wiz3r_card_generations> = getRecentWiz3RCardGenerations(user_id, value_) { id, user_id_,
      card_id, generated_at, context_json, response_json ->
    Wiz3r_card_generations(
      id,
      user_id_,
      card_id,
      generated_at,
      context_json,
      response_json
    )
  }

  public fun <T : Any> getMaxTransactionUpdatedAtForUser(user_id: String,
      mapper: (MAX: String?) -> T): Query<T> = GetMaxTransactionUpdatedAtForUserQuery(user_id) {
      cursor ->
    mapper(
      cursor.getString(0)
    )
  }

  public fun getMaxTransactionUpdatedAtForUser(user_id: String):
      Query<GetMaxTransactionUpdatedAtForUser> = getMaxTransactionUpdatedAtForUser(user_id) { MAX ->
    GetMaxTransactionUpdatedAtForUser(
      MAX
    )
  }

  public fun <T : Any> getWiz3RChatThread(
    user_id: String,
    card_id: String,
    mapper: (
      thread_id: String,
      user_id: String,
      card_id: String,
      created_at: String,
      last_message_at: String,
    ) -> T,
  ): Query<T> = GetWiz3RChatThreadQuery(user_id, card_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!
    )
  }

  public fun getWiz3RChatThread(user_id: String, card_id: String): Query<Wiz3r_chat_threads> =
      getWiz3RChatThread(user_id, card_id) { thread_id, user_id_, card_id_, created_at,
      last_message_at ->
    Wiz3r_chat_threads(
      thread_id,
      user_id_,
      card_id_,
      created_at,
      last_message_at
    )
  }

  public fun <T : Any> selectWiz3RChatMessages(thread_id: String, mapper: (
    id: Long,
    thread_id: String,
    user_id: String,
    role: String,
    content: String,
    chart_json: String?,
    created_at: String,
  ) -> T): Query<T> = SelectWiz3RChatMessagesQuery(thread_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6)!!
    )
  }

  public fun selectWiz3RChatMessages(thread_id: String): Query<Wiz3r_chat_messages> =
      selectWiz3RChatMessages(thread_id) { id, thread_id_, user_id, role, content, chart_json,
      created_at ->
    Wiz3r_chat_messages(
      id,
      thread_id_,
      user_id,
      role,
      content,
      chart_json,
      created_at
    )
  }

  public fun <T : Any> selectWiz3RChatMessagesRecent(
    thread_id: String,
    `value`: Long,
    mapper: (
      id: Long,
      thread_id: String,
      user_id: String,
      role: String,
      content: String,
      chart_json: String?,
      created_at: String,
    ) -> T,
  ): Query<T> = SelectWiz3RChatMessagesRecentQuery(thread_id, value) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5),
      cursor.getString(6)!!
    )
  }

  public fun selectWiz3RChatMessagesRecent(thread_id: String, value_: Long):
      Query<Wiz3r_chat_messages> = selectWiz3RChatMessagesRecent(thread_id, value_) { id,
      thread_id_, user_id, role, content, chart_json, created_at ->
    Wiz3r_chat_messages(
      id,
      thread_id_,
      user_id,
      role,
      content,
      chart_json,
      created_at
    )
  }

  public fun countWiz3RChatUserMessagesSince(user_id: String, created_at: String): Query<Long> =
      CountWiz3RChatUserMessagesSinceQuery(user_id, created_at) { cursor ->
    cursor.getLong(0)!!
  }

  public fun <T : Any> selectBudgetClassification(
    user_id: String,
    category_code: String,
    mapper: (
      user_id: String,
      category_code: String,
      bucket: String,
      created_at: String,
      updated_at: String,
    ) -> T,
  ): Query<T> = SelectBudgetClassificationQuery(user_id, category_code) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!
    )
  }

  public fun selectBudgetClassification(user_id: String, category_code: String):
      Query<Budget_category_classifications> = selectBudgetClassification(user_id, category_code) {
      user_id_, category_code_, bucket, created_at, updated_at ->
    Budget_category_classifications(
      user_id_,
      category_code_,
      bucket,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> selectAllBudgetClassifications(user_id: String, mapper: (
    user_id: String,
    category_code: String,
    bucket: String,
    created_at: String,
    updated_at: String,
  ) -> T): Query<T> = SelectAllBudgetClassificationsQuery(user_id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!
    )
  }

  public fun selectAllBudgetClassifications(user_id: String): Query<Budget_category_classifications>
      = selectAllBudgetClassifications(user_id) { user_id_, category_code, bucket, created_at,
      updated_at ->
    Budget_category_classifications(
      user_id_,
      category_code,
      bucket,
      created_at,
      updated_at
    )
  }

  public fun <T : Any> selectRecentBudgetHealthSnapshots(
    user_id: String,
    `value`: Long,
    mapper: (
      user_id: String,
      year_month: String,
      needs_pct: Double,
      wants_pct: Double,
      savings_pct: Double,
      health_label: String,
      created_at: String,
    ) -> T,
  ): Query<T> = SelectRecentBudgetHealthSnapshotsQuery(user_id, value) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getDouble(2)!!,
      cursor.getDouble(3)!!,
      cursor.getDouble(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!
    )
  }

  public fun selectRecentBudgetHealthSnapshots(user_id: String, value_: Long):
      Query<Budget_health_snapshots> = selectRecentBudgetHealthSnapshots(user_id, value_) {
      user_id_, year_month, needs_pct, wants_pct, savings_pct, health_label, created_at ->
    Budget_health_snapshots(
      user_id_,
      year_month,
      needs_pct,
      wants_pct,
      savings_pct,
      health_label,
      created_at
    )
  }

  public suspend fun insertUser(
    user_id: String,
    username: String,
    password_hash: String,
    email: String?,
  ) {
    driver.execute(1_926_333_118, """
        |INSERT INTO users(user_id, username, password_hash, email, trial_ends_at)
        |VALUES (?, ?, ?, ?, strftime('%Y-%m-%dT%H:%M:%SZ', 'now', '+14 days'))
        """.trimMargin(), 4) {
          bindString(0, user_id)
          bindString(1, username)
          bindString(2, password_hash)
          bindString(3, email)
        }.await()
    notifyQueries(1_926_333_118) { emit ->
      emit("users")
    }
  }

  public suspend fun updateUserLastLogin(user_id: String) {
    driver.execute(356_798_789, """
        |UPDATE users
        |SET last_login_at = CURRENT_TIMESTAMP,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE user_id = ?
        """.trimMargin(), 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(356_798_789) { emit ->
      emit("users")
    }
  }

  public suspend fun setMfaSecret(mfa_secret: String?, user_id: String) {
    driver.execute(1_399_630_480,
        """UPDATE users SET mfa_secret = ?, updated_at = CURRENT_TIMESTAMP WHERE user_id = ?""", 2)
        {
          bindString(0, mfa_secret)
          bindString(1, user_id)
        }.await()
    notifyQueries(1_399_630_480) { emit ->
      emit("users")
    }
  }

  public suspend fun enableMfa(user_id: String) {
    driver.execute(1_616_057_739,
        """UPDATE users SET mfa_enabled = 1, updated_at = CURRENT_TIMESTAMP WHERE user_id = ?""", 1)
        {
          bindString(0, user_id)
        }.await()
    notifyQueries(1_616_057_739) { emit ->
      emit("users")
    }
  }

  public suspend fun disableMfa(user_id: String) {
    driver.execute(-658_790_790,
        """UPDATE users SET mfa_enabled = 0, mfa_secret = NULL, backup_codes = NULL, updated_at = CURRENT_TIMESTAMP WHERE user_id = ?""",
        1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(-658_790_790) { emit ->
      emit("users")
    }
  }

  public suspend fun setBackupCodes(backup_codes: String?, user_id: String) {
    driver.execute(1_196_373_340,
        """UPDATE users SET backup_codes = ?, updated_at = CURRENT_TIMESTAMP WHERE user_id = ?""",
        2) {
          bindString(0, backup_codes)
          bindString(1, user_id)
        }.await()
    notifyQueries(1_196_373_340) { emit ->
      emit("users")
    }
  }

  public suspend fun updateSubscriptionStatus(
    subscription_status: String,
    subscription_id: String?,
    user_id: String,
  ) {
    driver.execute(1_675_431_730,
        """UPDATE users SET subscription_status = ?, subscription_id = ?, updated_at = CURRENT_TIMESTAMP WHERE user_id = ?""",
        3) {
          bindString(0, subscription_status)
          bindString(1, subscription_id)
          bindString(2, user_id)
        }.await()
    notifyQueries(1_675_431_730) { emit ->
      emit("users")
    }
  }

  public suspend fun setStripeCustomerId(stripe_customer_id: String?, user_id: String) {
    driver.execute(557_002_478,
        """UPDATE users SET stripe_customer_id = ?, updated_at = CURRENT_TIMESTAMP WHERE user_id = ?""",
        2) {
          bindString(0, stripe_customer_id)
          bindString(1, user_id)
        }.await()
    notifyQueries(557_002_478) { emit ->
      emit("users")
    }
  }

  public suspend fun updateSubscriptionTier(subscription_tier: String, user_id: String) {
    driver.execute(-695_443_166,
        """UPDATE users SET subscription_tier = ?, updated_at = CURRENT_TIMESTAMP WHERE user_id = ?""",
        2) {
          bindString(0, subscription_tier)
          bindString(1, user_id)
        }.await()
    notifyQueries(-695_443_166) { emit ->
      emit("users")
    }
  }

  public suspend fun setAiEnabled(wiz3r_ai_enabled: Long, user_id: String) {
    driver.execute(-58_297_903,
        """UPDATE users SET wiz3r_ai_enabled = ?, updated_at = CURRENT_TIMESTAMP WHERE user_id = ?""",
        2) {
          bindLong(0, wiz3r_ai_enabled)
          bindString(1, user_id)
        }.await()
    notifyQueries(-58_297_903) { emit ->
      emit("users")
    }
  }

  public suspend fun setAiEnabledByEmail(wiz3r_ai_enabled: Long, email: String?) {
    driver.execute(null,
        """UPDATE users SET wiz3r_ai_enabled = ?, updated_at = CURRENT_TIMESTAMP WHERE email ${ if (email == null) "IS" else "=" } ?""",
        2) {
          bindLong(0, wiz3r_ai_enabled)
          bindString(1, email)
        }.await()
    notifyQueries(-1_439_492_492) { emit ->
      emit("users")
    }
  }

  public suspend fun updateUserTimezone(timezone: String, user_id: String) {
    driver.execute(1_750_232_807,
        """UPDATE users SET timezone = ?, updated_at = CURRENT_TIMESTAMP WHERE user_id = ?""", 2) {
          bindString(0, timezone)
          bindString(1, user_id)
        }.await()
    notifyQueries(1_750_232_807) { emit ->
      emit("users")
    }
  }

  public suspend fun deleteUserData_wiz3r_card_generations(user_id: String) {
    driver.execute(1_642_156_489, """DELETE FROM wiz3r_card_generations WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(1_642_156_489) { emit ->
      emit("wiz3r_card_generations")
    }
  }

  public suspend fun deleteUserData_personalized_card_views(user_id: String) {
    driver.execute(1_448_339_337, """DELETE FROM personalized_card_views WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(1_448_339_337) { emit ->
      emit("personalized_card_views")
    }
  }

  public suspend fun deleteUserData_dismissed_personalized_cards(user_id: String) {
    driver.execute(1_381_063_471, """DELETE FROM dismissed_personalized_cards WHERE user_id = ?""",
        1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(1_381_063_471) { emit ->
      emit("dismissed_personalized_cards")
    }
  }

  public suspend fun deleteUserData_user_settings(user_id: String) {
    driver.execute(-133_065_966, """DELETE FROM user_settings WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(-133_065_966) { emit ->
      emit("user_settings")
    }
  }

  public suspend fun deleteUserData_user_advice_cards(user_id: String) {
    driver.execute(-1_332_480_821, """DELETE FROM user_advice_cards WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(-1_332_480_821) { emit ->
      emit("user_advice_cards")
    }
  }

  public suspend fun deleteUserData_budget_plans(user_id: String) {
    driver.execute(-1_404_691_307, """DELETE FROM budget_plans WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(-1_404_691_307) { emit ->
      emit("budget_plans")
    }
  }

  public suspend fun deleteUserData_transaction_goal_assignments(user_id: String) {
    driver.execute(-1_085_300_480, """DELETE FROM transaction_goal_assignments WHERE user_id = ?""",
        1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(-1_085_300_480) { emit ->
      emit("transaction_goal_assignments")
    }
  }

  public suspend fun deleteUserData_goal_assignment_rules(user_id: String) {
    driver.execute(-1_389_821_268, """DELETE FROM goal_assignment_rules WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(-1_389_821_268) { emit ->
      emit("goal_assignment_rules")
      emit("transaction_goal_assignments")
    }
  }

  public suspend fun deleteUserData_goal_progress_history(user_id: String) {
    driver.execute(-1_802_719_703, """DELETE FROM goal_progress_history WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(-1_802_719_703) { emit ->
      emit("goal_progress_history")
    }
  }

  public suspend fun deleteUserData_savings_recommendations(user_id: String) {
    driver.execute(-937_351_281, """DELETE FROM savings_recommendations WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(-937_351_281) { emit ->
      emit("savings_recommendations")
    }
  }

  public suspend fun deleteUserData_savings_goals(user_id: String) {
    driver.execute(1_856_635_317, """DELETE FROM savings_goals WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(1_856_635_317) { emit ->
      emit("goal_assignment_rules")
      emit("goal_progress_history")
      emit("savings_goals")
      emit("savings_recommendations")
      emit("transaction_goal_assignments")
    }
  }

  public suspend fun deleteUserData_user_corrections(user_id: String) {
    driver.execute(1_674_042_246, """DELETE FROM user_corrections WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(1_674_042_246) { emit ->
      emit("user_corrections")
    }
  }

  public suspend fun deleteUserData_transaction_embeddings(user_id: String) {
    driver.execute(-1_139_655_326, """DELETE FROM transaction_embeddings WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(-1_139_655_326) { emit ->
      emit("transaction_embeddings")
    }
  }

  public suspend fun deleteUserData_transaction_categories(user_id: String) {
    driver.execute(870_113_154, """DELETE FROM transaction_categories WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(870_113_154) { emit ->
      emit("transaction_categories")
    }
  }

  public suspend fun deleteUserData_transactions(user_id: String) {
    driver.execute(1_901_920_506, """DELETE FROM transactions WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(1_901_920_506) { emit ->
      emit("transaction_embeddings")
      emit("transaction_goal_assignments")
      emit("transactions")
      emit("user_corrections")
    }
  }

  public suspend fun deleteUserData_accounts(user_id: String) {
    driver.execute(256_953_867, """DELETE FROM accounts WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(256_953_867) { emit ->
      emit("accounts")
      emit("transactions")
    }
  }

  public suspend fun deleteUserData_plaid_tokens(user_id: String) {
    driver.execute(-1_935_497_058, """DELETE FROM plaid_tokens WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(-1_935_497_058) { emit ->
      emit("accounts")
      emit("plaid_tokens")
    }
  }

  public suspend fun deleteUser(user_id: String) {
    driver.execute(-1_570_210_640, """DELETE FROM users WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(-1_570_210_640) { emit ->
      emit("accounts")
      emit("budget_category_classifications")
      emit("budget_health_snapshots")
      emit("budget_plans")
      emit("contexts")
      emit("dismissed_personalized_cards")
      emit("goal_assignment_rules")
      emit("goal_progress_history")
      emit("manual_accounts")
      emit("personalized_card_views")
      emit("plaid_tokens")
      emit("savings_goals")
      emit("savings_recommendations")
      emit("statement_templates")
      emit("subscription_merchants")
      emit("transaction_categories")
      emit("transaction_embeddings")
      emit("transaction_goal_assignments")
      emit("transactions")
      emit("user_advice_cards")
      emit("user_corrections")
      emit("user_settings")
      emit("users")
      emit("wiz3r_card_generations")
      emit("wiz3r_chat_messages")
      emit("wiz3r_chat_threads")
    }
  }

  public suspend fun updatePassword(password_hash: String, user_id: String) {
    driver.execute(306_841_118,
        """UPDATE users SET password_hash = ?, updated_at = CURRENT_TIMESTAMP WHERE user_id = ?""",
        2) {
          bindString(0, password_hash)
          bindString(1, user_id)
        }.await()
    notifyQueries(306_841_118) { emit ->
      emit("users")
    }
  }

  public suspend fun setVerificationToken(
    verification_token: String?,
    verification_token_expires_at: String?,
    user_id: String,
  ) {
    driver.execute(847_383_670,
        """UPDATE users SET verification_token = ?, verification_token_expires_at = ? WHERE user_id = ?""",
        3) {
          bindString(0, verification_token)
          bindString(1, verification_token_expires_at)
          bindString(2, user_id)
        }.await()
    notifyQueries(847_383_670) { emit ->
      emit("users")
    }
  }

  public suspend fun verifyEmail(verification_token: String?) {
    driver.execute(null,
        """UPDATE users SET email_verified = 1, verification_token = NULL, verification_token_expires_at = NULL WHERE verification_token ${ if (verification_token == null) "IS" else "=" } ? AND verification_token_expires_at > CURRENT_TIMESTAMP""",
        1) {
          bindString(0, verification_token)
        }.await()
    notifyQueries(2_021_680_457) { emit ->
      emit("users")
    }
  }

  public suspend fun markUserEmailVerifiedById(user_id: String) {
    driver.execute(1_473_540_932,
        """UPDATE users SET email_verified = 1, verification_token = NULL, verification_token_expires_at = NULL WHERE user_id = ?""",
        1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(1_473_540_932) { emit ->
      emit("users")
    }
  }

  public suspend fun setPasswordResetToken(
    password_reset_token_hash: String?,
    password_reset_expires_at: String?,
    user_id: String,
  ) {
    driver.execute(-1_569_820_147,
        """UPDATE users SET password_reset_token_hash = ?, password_reset_expires_at = ? WHERE user_id = ?""",
        3) {
          bindString(0, password_reset_token_hash)
          bindString(1, password_reset_expires_at)
          bindString(2, user_id)
        }.await()
    notifyQueries(-1_569_820_147) { emit ->
      emit("users")
    }
  }

  public suspend fun clearPasswordResetToken(user_id: String) {
    driver.execute(486_847_928,
        """UPDATE users SET password_reset_token_hash = NULL, password_reset_expires_at = NULL WHERE user_id = ?""",
        1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(486_847_928) { emit ->
      emit("users")
    }
  }

  public suspend fun insertTransaction(
    transaction_id: String,
    account_id: String,
    user_id: String,
    amount: Double,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    date: String,
    authorized_date: String?,
    authorized_datetime: String?,
    datetime: String?,
    name: String,
    merchant_name: String?,
    original_description: String?,
    category: String?,
    category_id: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    personal_finance_category_confidence: String?,
    transaction_type: String?,
    transaction_code: String?,
    pending: Long,
    pending_transaction_id: String?,
    location_json: String?,
    payment_meta_json: String?,
    account_owner: String?,
    last_synced_at: String?,
    ai_category: String?,
    confidence_score: Double?,
    ml_model_version: String?,
    category_source: String?,
  ) {
    driver.execute(1_456_411_787, """
        |INSERT INTO transactions(
        |    transaction_id, account_id, user_id, amount, iso_currency_code, unofficial_currency_code,
        |    date, authorized_date, authorized_datetime, datetime, name, merchant_name, original_description,
        |    category, category_id, personal_finance_category_primary, personal_finance_category_detailed,
        |    personal_finance_category_confidence, transaction_type, transaction_code, pending,
        |    pending_transaction_id, location_json, payment_meta_json, account_owner, last_synced_at,
        |    ai_category, confidence_score, ml_model_version, category_source
        |) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 30) {
          bindString(0, transaction_id)
          bindString(1, account_id)
          bindString(2, user_id)
          bindDouble(3, amount)
          bindString(4, iso_currency_code)
          bindString(5, unofficial_currency_code)
          bindString(6, date)
          bindString(7, authorized_date)
          bindString(8, authorized_datetime)
          bindString(9, datetime)
          bindString(10, name)
          bindString(11, merchant_name)
          bindString(12, original_description)
          bindString(13, category)
          bindString(14, category_id)
          bindString(15, personal_finance_category_primary)
          bindString(16, personal_finance_category_detailed)
          bindString(17, personal_finance_category_confidence)
          bindString(18, transaction_type)
          bindString(19, transaction_code)
          bindLong(20, pending)
          bindString(21, pending_transaction_id)
          bindString(22, location_json)
          bindString(23, payment_meta_json)
          bindString(24, account_owner)
          bindString(25, last_synced_at)
          bindString(26, ai_category)
          bindDouble(27, confidence_score)
          bindString(28, ml_model_version)
          bindString(29, category_source)
        }.await()
    notifyQueries(1_456_411_787) { emit ->
      emit("transactions")
    }
  }

  public suspend fun updateTransaction(
    amount: Double,
    name: String,
    merchant_name: String?,
    date: String,
    pending: Long,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    location_json: String?,
    payment_meta_json: String?,
    last_synced_at: String?,
    transaction_id: String,
  ) {
    driver.execute(-2_144_062_341, """
        |UPDATE transactions SET
        |    amount = ?, name = ?, merchant_name = ?, date = ?, pending = ?,
        |    personal_finance_category_primary = ?, personal_finance_category_detailed = ?,
        |    location_json = ?, payment_meta_json = ?, updated_at = CURRENT_TIMESTAMP,
        |    last_synced_at = ?
        |WHERE transaction_id = ?
        """.trimMargin(), 11) {
          bindDouble(0, amount)
          bindString(1, name)
          bindString(2, merchant_name)
          bindString(3, date)
          bindLong(4, pending)
          bindString(5, personal_finance_category_primary)
          bindString(6, personal_finance_category_detailed)
          bindString(7, location_json)
          bindString(8, payment_meta_json)
          bindString(9, last_synced_at)
          bindString(10, transaction_id)
        }.await()
    notifyQueries(-2_144_062_341) { emit ->
      emit("transactions")
    }
  }

  public suspend fun upsertTransaction(
    transaction_id: String,
    account_id: String,
    user_id: String,
    amount: Double,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    date: String,
    authorized_date: String?,
    authorized_datetime: String?,
    datetime: String?,
    name: String,
    merchant_name: String?,
    original_description: String?,
    category: String?,
    category_id: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    personal_finance_category_confidence: String?,
    transaction_type: String?,
    transaction_code: String?,
    pending: Long,
    pending_transaction_id: String?,
    location_json: String?,
    payment_meta_json: String?,
    account_owner: String?,
    last_synced_at: String?,
  ) {
    driver.execute(-504_809_387, """
        |INSERT INTO transactions(
        |    transaction_id, account_id, user_id, amount, iso_currency_code, unofficial_currency_code,
        |    date, authorized_date, authorized_datetime, datetime, name, merchant_name, original_description,
        |    category, category_id, personal_finance_category_primary, personal_finance_category_detailed,
        |    personal_finance_category_confidence, transaction_type, transaction_code, pending,
        |    pending_transaction_id, location_json, payment_meta_json, account_owner, last_synced_at
        |) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        |ON CONFLICT(transaction_id) DO UPDATE SET
        |    account_id = excluded.account_id,
        |    amount = excluded.amount,
        |    iso_currency_code = excluded.iso_currency_code,
        |    unofficial_currency_code = excluded.unofficial_currency_code,
        |    date = excluded.date,
        |    authorized_date = excluded.authorized_date,
        |    authorized_datetime = excluded.authorized_datetime,
        |    datetime = excluded.datetime,
        |    name = excluded.name,
        |    merchant_name = excluded.merchant_name,
        |    original_description = excluded.original_description,
        |    category = excluded.category,
        |    category_id = excluded.category_id,
        |    personal_finance_category_primary = excluded.personal_finance_category_primary,
        |    personal_finance_category_detailed = excluded.personal_finance_category_detailed,
        |    personal_finance_category_confidence = excluded.personal_finance_category_confidence,
        |    transaction_type = excluded.transaction_type,
        |    transaction_code = excluded.transaction_code,
        |    pending = excluded.pending,
        |    pending_transaction_id = excluded.pending_transaction_id,
        |    location_json = excluded.location_json,
        |    payment_meta_json = excluded.payment_meta_json,
        |    account_owner = excluded.account_owner,
        |    last_synced_at = excluded.last_synced_at,
        |    updated_at = CURRENT_TIMESTAMP
        """.trimMargin(), 26) {
          bindString(0, transaction_id)
          bindString(1, account_id)
          bindString(2, user_id)
          bindDouble(3, amount)
          bindString(4, iso_currency_code)
          bindString(5, unofficial_currency_code)
          bindString(6, date)
          bindString(7, authorized_date)
          bindString(8, authorized_datetime)
          bindString(9, datetime)
          bindString(10, name)
          bindString(11, merchant_name)
          bindString(12, original_description)
          bindString(13, category)
          bindString(14, category_id)
          bindString(15, personal_finance_category_primary)
          bindString(16, personal_finance_category_detailed)
          bindString(17, personal_finance_category_confidence)
          bindString(18, transaction_type)
          bindString(19, transaction_code)
          bindLong(20, pending)
          bindString(21, pending_transaction_id)
          bindString(22, location_json)
          bindString(23, payment_meta_json)
          bindString(24, account_owner)
          bindString(25, last_synced_at)
        }.await()
    notifyQueries(-504_809_387) { emit ->
      emit("transactions")
    }
  }

  public suspend fun updateTransactionMLEmbedding(
    embedding: ByteArray?,
    embedding_version: Long?,
    transaction_id: String,
  ) {
    driver.execute(1_364_427_037, """
        |UPDATE transactions SET
        |    embedding = ?, embedding_version = ?, updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ?
        """.trimMargin(), 3) {
          bindBytes(0, embedding)
          bindLong(1, embedding_version)
          bindString(2, transaction_id)
        }.await()
    notifyQueries(1_364_427_037) { emit ->
      emit("transactions")
    }
  }

  public suspend fun updateTransactionUserData(
    user_category: String?,
    user_notes: String?,
    tags: String?,
    is_hidden: Long,
    is_expense_report: Long,
    transaction_id: String,
  ) {
    driver.execute(596_795_312, """
        |UPDATE transactions SET
        |    user_category = ?, user_notes = ?, tags = ?, is_hidden = ?, is_expense_report = ?,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ?
        """.trimMargin(), 6) {
          bindString(0, user_category)
          bindString(1, user_notes)
          bindString(2, tags)
          bindLong(3, is_hidden)
          bindLong(4, is_expense_report)
          bindString(5, transaction_id)
        }.await()
    notifyQueries(596_795_312) { emit ->
      emit("transactions")
    }
  }

  public suspend fun setUserCategoryOverride(
    user_category: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    transaction_id: String,
  ) {
    driver.execute(-1_465_325_059, """
        |UPDATE transactions SET
        |    user_category = ?,
        |    personal_finance_category_primary = ?,
        |    personal_finance_category_detailed = ?,
        |    category_source = 'user',
        |    confidence_score = 100,
        |    is_verified = 1,
        |    verified_at = CURRENT_TIMESTAMP,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ?
        """.trimMargin(), 4) {
          bindString(0, user_category)
          bindString(1, personal_finance_category_primary)
          bindString(2, personal_finance_category_detailed)
          bindString(3, transaction_id)
        }.await()
    notifyQueries(-1_465_325_059) { emit ->
      emit("transactions")
    }
  }

  public suspend fun propagateUserCategoryByMerchant(
    user_category: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    user_id: String,
    merchant_name: String?,
    name: String,
    personal_finance_category_detailed_: String?,
  ) {
    driver.execute(null, """
        |UPDATE transactions SET
        |    user_category = ?,
        |    personal_finance_category_primary = ?,
        |    personal_finance_category_detailed = ?,
        |    category_source = 'user',
        |    confidence_score = 100,
        |    is_verified = 1,
        |    verified_at = CURRENT_TIMESTAMP,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE user_id = ?
        |    AND (merchant_name ${ if (merchant_name == null) "IS" else "=" } ? OR (merchant_name IS NULL AND name = ?))
        |    AND personal_finance_category_detailed ${ if (personal_finance_category_detailed_ == null) "IS" else "=" } ?
        """.trimMargin(), 7) {
          bindString(0, user_category)
          bindString(1, personal_finance_category_primary)
          bindString(2, personal_finance_category_detailed)
          bindString(3, user_id)
          bindString(4, merchant_name)
          bindString(5, name)
          bindString(6, personal_finance_category_detailed_)
        }.await()
    notifyQueries(-1_740_906_441) { emit ->
      emit("transactions")
    }
  }

  public suspend fun markAsExcluded(transaction_id: String, user_id: String) {
    driver.execute(-440_268_189, """
        |UPDATE transactions SET is_excluded = 1, updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ? AND user_id = ?
        """.trimMargin(), 2) {
          bindString(0, transaction_id)
          bindString(1, user_id)
        }.await()
    notifyQueries(-440_268_189) { emit ->
      emit("transactions")
    }
  }

  public suspend fun unmarkExcluded(transaction_id: String, user_id: String) {
    driver.execute(970_695_786, """
        |UPDATE transactions SET is_excluded = 0, updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ? AND user_id = ?
        """.trimMargin(), 2) {
          bindString(0, transaction_id)
          bindString(1, user_id)
        }.await()
    notifyQueries(970_695_786) { emit ->
      emit("transactions")
    }
  }

  public suspend fun markAsHidden(transaction_id: String, user_id: String) {
    driver.execute(-348_513_149, """
        |UPDATE transactions SET is_hidden = 1, updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ? AND user_id = ?
        """.trimMargin(), 2) {
          bindString(0, transaction_id)
          bindString(1, user_id)
        }.await()
    notifyQueries(-348_513_149) { emit ->
      emit("transactions")
    }
  }

  public suspend fun unmarkHidden(transaction_id: String, user_id: String) {
    driver.execute(1_664_126_026, """
        |UPDATE transactions SET is_hidden = 0, updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ? AND user_id = ?
        """.trimMargin(), 2) {
          bindString(0, transaction_id)
          bindString(1, user_id)
        }.await()
    notifyQueries(1_664_126_026) { emit ->
      emit("transactions")
    }
  }

  public suspend fun applyMerchantCategoryIfUnset(
    primary: String?,
    detailed: String?,
    user_category: String?,
    user_id: String,
    merchant_key: String?,
  ) {
    driver.execute(null, """
        |UPDATE transactions SET
        |    personal_finance_category_primary = ?,
        |    personal_finance_category_detailed = ?,
        |    user_category = ?,
        |    category_source = 'user',
        |    is_verified = 1,
        |    confidence_score = 100,
        |    verified_at = CURRENT_TIMESTAMP
        |WHERE user_id = ?
        |  AND (
        |    merchant_name ${ if (merchant_key == null) "IS" else "=" } ?
        |    OR (merchant_name IS NULL AND name ${ if (merchant_key == null) "IS" else "=" } ?)
        |    OR LOWER(name) LIKE '%' || LOWER(?) || '%'
        |    OR LOWER(merchant_name) LIKE '%' || LOWER(?) || '%'
        |  )
        |  AND category_source != 'user'
        """.trimMargin(), 8) {
          bindString(0, primary)
          bindString(1, detailed)
          bindString(2, user_category)
          bindString(3, user_id)
          bindString(4, merchant_key)
          bindString(5, merchant_key)
          bindString(6, merchant_key)
          bindString(7, merchant_key)
        }.await()
    notifyQueries(-713_049_838) { emit ->
      emit("transactions")
    }
  }

  public suspend fun resetUserCategoryOverride(transaction_id: String) {
    driver.execute(-209_338_134, """
        |UPDATE transactions SET
        |    user_category = NULL,
        |    category_source = COALESCE(
        |        CASE WHEN ai_category IS NOT NULL THEN 'ai' ELSE NULL END,
        |        'plaid'
        |    ),
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ?
        """.trimMargin(), 1) {
          bindString(0, transaction_id)
        }.await()
    notifyQueries(-209_338_134) { emit ->
      emit("transactions")
    }
  }

  public suspend fun updateTransactionAICategory(
    ai_category: String?,
    confidence_score: Double?,
    ml_model_version: String?,
    category_source: String?,
    transaction_id: String,
  ) {
    driver.execute(577_476_609, """
        |UPDATE transactions SET
        |    ai_category = ?, confidence_score = ?, ml_model_version = ?, category_source = ?,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ?
        """.trimMargin(), 5) {
          bindString(0, ai_category)
          bindDouble(1, confidence_score)
          bindString(2, ml_model_version)
          bindString(3, category_source)
          bindString(4, transaction_id)
        }.await()
    notifyQueries(577_476_609) { emit ->
      emit("transactions")
    }
  }

  public suspend fun recordCategoryCorrection(user_category: String?, transaction_id: String) {
    driver.execute(1_179_547_719, """
        |UPDATE transactions SET
        |    original_ai_category = ai_category,
        |    correction_confidence = confidence_score,
        |    corrected_at = CURRENT_TIMESTAMP,
        |    user_category = ?,
        |    category_source = 'user',
        |    confidence_score = 100,
        |    is_verified = 1,
        |    verified_at = CURRENT_TIMESTAMP,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ? AND ai_category IS NOT NULL
        """.trimMargin(), 2) {
          bindString(0, user_category)
          bindString(1, transaction_id)
        }.await()
    notifyQueries(1_179_547_719) { emit ->
      emit("transactions")
    }
  }

  public suspend fun verifyTransactionCategory(transaction_id: String) {
    driver.execute(-1_849_074_455, """
        |UPDATE transactions SET
        |    is_verified = 1,
        |    confidence_score = 100,
        |    verified_at = CURRENT_TIMESTAMP,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ?
        """.trimMargin(), 1) {
          bindString(0, transaction_id)
        }.await()
    notifyQueries(-1_849_074_455) { emit ->
      emit("transactions")
    }
  }

  public suspend fun propagateVerificationByMerchant(
    user_id: String,
    merchant_name: String?,
    name: String,
    personal_finance_category_detailed: String?,
  ) {
    driver.execute(null, """
        |UPDATE transactions SET
        |    is_verified = 1,
        |    confidence_score = 100,
        |    verified_at = CURRENT_TIMESTAMP,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE user_id = ?
        |    AND is_verified = 0
        |    AND (merchant_name ${ if (merchant_name == null) "IS" else "=" } ? OR (merchant_name IS NULL AND name = ?))
        |    AND personal_finance_category_detailed ${ if (personal_finance_category_detailed == null) "IS" else "=" } ?
        """.trimMargin(), 4) {
          bindString(0, user_id)
          bindString(1, merchant_name)
          bindString(2, name)
          bindString(3, personal_finance_category_detailed)
        }.await()
    notifyQueries(1_577_749_993) { emit ->
      emit("transactions")
    }
  }

  public suspend fun verifyTransactionsByIds(transaction_id: Collection<String>) {
    val transaction_idIndexes = createArguments(count = transaction_id.size)
    driver.execute(null, """
        |UPDATE transactions SET
        |    is_verified = 1,
        |    confidence_score = 100,
        |    verified_at = CURRENT_TIMESTAMP,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id IN $transaction_idIndexes
        """.trimMargin(), transaction_id.size) {
          transaction_id.forEachIndexed { index, transaction_id_ ->
            bindString(index, transaction_id_)
          }
        }.await()
    notifyQueries(199_635_129) { emit ->
      emit("transactions")
    }
  }

  public suspend fun clearVerification(transaction_id: String) {
    driver.execute(-1_878_986_962, """
        |UPDATE transactions SET
        |    is_verified = 0,
        |    verified_at = NULL,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ?
        """.trimMargin(), 1) {
          bindString(0, transaction_id)
        }.await()
    notifyQueries(-1_878_986_962) { emit ->
      emit("transactions")
    }
  }

  public suspend fun deleteTransaction(transaction_id: String) {
    driver.execute(-906_336_679, """DELETE FROM transactions WHERE transaction_id = ?""", 1) {
          bindString(0, transaction_id)
        }.await()
    notifyQueries(-906_336_679) { emit ->
      emit("transaction_embeddings")
      emit("transaction_goal_assignments")
      emit("transactions")
      emit("user_corrections")
    }
  }

  public suspend fun deleteStalePendingTransactions() {
    driver.execute(1_675_832_472, """
        |DELETE FROM transactions
        |WHERE pending = 1
        |  AND transaction_id IN (
        |    SELECT pending_transaction_id FROM transactions
        |    WHERE pending = 0 AND pending_transaction_id IS NOT NULL
        |  )
        """.trimMargin(), 0).await()
    notifyQueries(1_675_832_472) { emit ->
      emit("transaction_embeddings")
      emit("transaction_goal_assignments")
      emit("transactions")
      emit("user_corrections")
    }
  }

  public suspend fun transferUserMods(old_id: String, new_id: String) {
    driver.execute(366_462_529, """
        |UPDATE transactions SET
        |    is_excluded = (SELECT is_excluded FROM transactions t2 WHERE t2.transaction_id = ?),
        |    is_verified = (SELECT is_verified FROM transactions t2 WHERE t2.transaction_id = ?),
        |    verified_at = (SELECT verified_at FROM transactions t2 WHERE t2.transaction_id = ?),
        |    user_category = (SELECT user_category FROM transactions t2 WHERE t2.transaction_id = ?),
        |    category_source = (SELECT category_source FROM transactions t2 WHERE t2.transaction_id = ?),
        |    user_notes = (SELECT user_notes FROM transactions t2 WHERE t2.transaction_id = ?),
        |    tags = (SELECT tags FROM transactions t2 WHERE t2.transaction_id = ?),
        |    is_split_parent = (SELECT is_split_parent FROM transactions t2 WHERE t2.transaction_id = ?),
        |    is_distributed_parent = (SELECT is_distributed_parent FROM transactions t2 WHERE t2.transaction_id = ?),
        |    distribution_start_month = (SELECT distribution_start_month FROM transactions t2 WHERE t2.transaction_id = ?),
        |    distribution_end_month = (SELECT distribution_end_month FROM transactions t2 WHERE t2.transaction_id = ?),
        |    -- notified_at MUST come across. Without it, every time Plaid re-issues a
        |    -- transaction_id the merged row looks un-notified again and the scheduler
        |    -- re-sends the same transactions — users were getting the identical
        |    -- "13 new transactions" SMS every cycle, for days.
        |    notified_at = (SELECT notified_at FROM transactions t2 WHERE t2.transaction_id = ?),
        |    -- Same reasoning for is_hidden: a re-issued id would un-hide an
        |    -- auth-hold leg the user had already had hidden.
        |    is_hidden = (SELECT is_hidden FROM transactions t2 WHERE t2.transaction_id = ?),
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ?
        """.trimMargin(), 14) {
          bindString(0, old_id)
          bindString(1, old_id)
          bindString(2, old_id)
          bindString(3, old_id)
          bindString(4, old_id)
          bindString(5, old_id)
          bindString(6, old_id)
          bindString(7, old_id)
          bindString(8, old_id)
          bindString(9, old_id)
          bindString(10, old_id)
          bindString(11, old_id)
          bindString(12, old_id)
          bindString(13, new_id)
        }.await()
    notifyQueries(366_462_529) { emit ->
      emit("transactions")
    }
  }

  public suspend fun transferGoalAssignments(new_id: String, old_id: String) {
    driver.execute(-1_415_080_850, """
        |UPDATE OR REPLACE transaction_goal_assignments SET transaction_id = ?
        |WHERE transaction_id = ?
        """.trimMargin(), 2) {
          bindString(0, new_id)
          bindString(1, old_id)
        }.await()
    notifyQueries(-1_415_080_850) { emit ->
      emit("transaction_goal_assignments")
    }
  }

  public suspend fun transferChildTransactions(new_id: String?, old_id: String?) {
    driver.execute(null, """
        |UPDATE transactions SET parent_transaction_id = ?
        |WHERE parent_transaction_id ${ if (old_id == null) "IS" else "=" } ?
        """.trimMargin(), 2) {
          bindString(0, new_id)
          bindString(1, old_id)
        }.await()
    notifyQueries(-824_076_276) { emit ->
      emit("transactions")
    }
  }

  public suspend fun insertSplitChild(
    transaction_id: String,
    account_id: String,
    user_id: String,
    amount: Double,
    iso_currency_code: String?,
    date: String,
    name: String,
    merchant_name: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    parent_transaction_id: String?,
    split_order: Long?,
  ) {
    driver.execute(-1_635_049_771, """
        |INSERT INTO transactions(
        |    transaction_id, account_id, user_id, amount, iso_currency_code,
        |    date, name, merchant_name, personal_finance_category_primary,
        |    personal_finance_category_detailed, parent_transaction_id, child_type,
        |    split_order, category_source
        |) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'SPLIT', ?, 'split')
        """.trimMargin(), 12) {
          bindString(0, transaction_id)
          bindString(1, account_id)
          bindString(2, user_id)
          bindDouble(3, amount)
          bindString(4, iso_currency_code)
          bindString(5, date)
          bindString(6, name)
          bindString(7, merchant_name)
          bindString(8, personal_finance_category_primary)
          bindString(9, personal_finance_category_detailed)
          bindString(10, parent_transaction_id)
          bindLong(11, split_order)
        }.await()
    notifyQueries(-1_635_049_771) { emit ->
      emit("transactions")
    }
  }

  public suspend fun insertDistributedChild(
    transaction_id: String,
    account_id: String,
    user_id: String,
    amount: Double,
    iso_currency_code: String?,
    date: String,
    name: String,
    merchant_name: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    parent_transaction_id: String?,
    distribution_month_index: Long?,
    distribution_total_months: Long?,
    distribution_start_month: String?,
    distribution_end_month: String?,
  ) {
    driver.execute(1_869_799_084, """
        |INSERT INTO transactions(
        |    transaction_id, account_id, user_id, amount, iso_currency_code,
        |    date, name, merchant_name, personal_finance_category_primary,
        |    personal_finance_category_detailed, parent_transaction_id, child_type,
        |    category_source, distribution_month_index, distribution_total_months,
        |    distribution_start_month, distribution_end_month
        |) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'DISTRIBUTED', 'split', ?, ?, ?, ?)
        """.trimMargin(), 15) {
          bindString(0, transaction_id)
          bindString(1, account_id)
          bindString(2, user_id)
          bindDouble(3, amount)
          bindString(4, iso_currency_code)
          bindString(5, date)
          bindString(6, name)
          bindString(7, merchant_name)
          bindString(8, personal_finance_category_primary)
          bindString(9, personal_finance_category_detailed)
          bindString(10, parent_transaction_id)
          bindLong(11, distribution_month_index)
          bindLong(12, distribution_total_months)
          bindString(13, distribution_start_month)
          bindString(14, distribution_end_month)
        }.await()
    notifyQueries(1_869_799_084) { emit ->
      emit("transactions")
    }
  }

  public suspend fun markAsSplitParent(transaction_id: String) {
    driver.execute(1_501_148_459, """
        |UPDATE transactions SET
        |    is_split_parent = 1,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ?
        """.trimMargin(), 1) {
          bindString(0, transaction_id)
        }.await()
    notifyQueries(1_501_148_459) { emit ->
      emit("transactions")
    }
  }

  public suspend fun markAsDistributedParent(
    distribution_start_month: String?,
    distribution_end_month: String?,
    transaction_id: String,
  ) {
    driver.execute(666_416_308, """
        |UPDATE transactions SET
        |    is_distributed_parent = 1,
        |    distribution_start_month = ?,
        |    distribution_end_month = ?,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ?
        """.trimMargin(), 3) {
          bindString(0, distribution_start_month)
          bindString(1, distribution_end_month)
          bindString(2, transaction_id)
        }.await()
    notifyQueries(666_416_308) { emit ->
      emit("transactions")
    }
  }

  public suspend fun clearParentStatus(transaction_id: String) {
    driver.execute(668_350_031, """
        |UPDATE transactions SET
        |    is_split_parent = 0,
        |    is_distributed_parent = 0,
        |    distribution_start_month = NULL,
        |    distribution_end_month = NULL,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ?
        """.trimMargin(), 1) {
          bindString(0, transaction_id)
        }.await()
    notifyQueries(668_350_031) { emit ->
      emit("transactions")
    }
  }

  public suspend fun deleteChildTransactions(parent_transaction_id: String?) {
    driver.execute(null,
        """DELETE FROM transactions WHERE parent_transaction_id ${ if (parent_transaction_id == null) "IS" else "=" } ?""",
        1) {
          bindString(0, parent_transaction_id)
        }.await()
    notifyQueries(-1_523_330_100) { emit ->
      emit("transaction_embeddings")
      emit("transaction_goal_assignments")
      emit("transactions")
      emit("user_corrections")
    }
  }

  public suspend fun insertAccount(
    account_id: String,
    user_id: String,
    item_id: String,
    name: String,
    official_name: String?,
    mask: String?,
    type: String,
    subtype: String?,
    balance_available: Double?,
    balance_current: Double?,
    balance_limit: Double?,
    iso_currency_code: String?,
    unofficial_currency_code: String?,
    balance_last_updated: String?,
    verification_status: String?,
    persistent_account_id: String?,
    last_synced_at: String?,
  ) {
    driver.execute(1_301_785_178, """
        |INSERT INTO accounts(
        |    account_id, user_id, item_id, name, official_name, mask, type, subtype,
        |    balance_available, balance_current, balance_limit, iso_currency_code,
        |    unofficial_currency_code, balance_last_updated, verification_status,
        |    persistent_account_id, last_synced_at
        |) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 17) {
          bindString(0, account_id)
          bindString(1, user_id)
          bindString(2, item_id)
          bindString(3, name)
          bindString(4, official_name)
          bindString(5, mask)
          bindString(6, type)
          bindString(7, subtype)
          bindDouble(8, balance_available)
          bindDouble(9, balance_current)
          bindDouble(10, balance_limit)
          bindString(11, iso_currency_code)
          bindString(12, unofficial_currency_code)
          bindString(13, balance_last_updated)
          bindString(14, verification_status)
          bindString(15, persistent_account_id)
          bindString(16, last_synced_at)
        }.await()
    notifyQueries(1_301_785_178) { emit ->
      emit("accounts")
    }
  }

  public suspend fun updateAccountBalance(
    balance_available: Double?,
    balance_current: Double?,
    balance_limit: Double?,
    balance_last_updated: String?,
    last_synced_at: String?,
    account_id: String,
  ) {
    driver.execute(360_676_658, """
        |UPDATE accounts SET
        |    balance_available = ?, balance_current = ?, balance_limit = ?,
        |    balance_last_updated = ?, updated_at = CURRENT_TIMESTAMP,
        |    last_synced_at = ?
        |WHERE account_id = ?
        """.trimMargin(), 6) {
          bindDouble(0, balance_available)
          bindDouble(1, balance_current)
          bindDouble(2, balance_limit)
          bindString(3, balance_last_updated)
          bindString(4, last_synced_at)
          bindString(5, account_id)
        }.await()
    notifyQueries(360_676_658) { emit ->
      emit("accounts")
    }
  }

  public suspend fun updateAccountBalanceAndName(
    official_name: String?,
    balance_available: Double?,
    balance_current: Double?,
    balance_limit: Double?,
    balance_last_updated: String?,
    last_synced_at: String?,
    account_id: String,
  ) {
    driver.execute(-95_100_048, """
        |UPDATE accounts SET
        |    official_name = ?,
        |    balance_available = ?, balance_current = ?, balance_limit = ?,
        |    balance_last_updated = ?, updated_at = CURRENT_TIMESTAMP,
        |    last_synced_at = ?
        |WHERE account_id = ?
        """.trimMargin(), 7) {
          bindString(0, official_name)
          bindDouble(1, balance_available)
          bindDouble(2, balance_current)
          bindDouble(3, balance_limit)
          bindString(4, balance_last_updated)
          bindString(5, last_synced_at)
          bindString(6, account_id)
        }.await()
    notifyQueries(-95_100_048) { emit ->
      emit("accounts")
    }
  }

  public suspend fun updateAccountCustomName(
    custom_name: String?,
    account_id: String,
    user_id: String,
  ) {
    driver.execute(253_993_862, """
        |UPDATE accounts SET custom_name = ?, updated_at = CURRENT_TIMESTAMP
        |WHERE account_id = ? AND user_id = ?
        """.trimMargin(), 3) {
          bindString(0, custom_name)
          bindString(1, account_id)
          bindString(2, user_id)
        }.await()
    notifyQueries(253_993_862) { emit ->
      emit("accounts")
    }
  }

  public suspend fun deleteAccount(account_id: String) {
    driver.execute(1_608_520_488, """DELETE FROM accounts WHERE account_id = ?""", 1) {
          bindString(0, account_id)
        }.await()
    notifyQueries(1_608_520_488) { emit ->
      emit("accounts")
      emit("transactions")
    }
  }

  public suspend fun deleteAccountsByItemId(item_id: String) {
    driver.execute(29_385_264, """DELETE FROM accounts WHERE item_id = ?""", 1) {
          bindString(0, item_id)
        }.await()
    notifyQueries(29_385_264) { emit ->
      emit("accounts")
      emit("transactions")
    }
  }

  public suspend fun markAccountsUnlinkedByItemId(item_id: String) {
    driver.execute(770_937_060, """
        |UPDATE accounts SET
        |    status = 'unlinked',
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE item_id = ?
        """.trimMargin(), 1) {
          bindString(0, item_id)
        }.await()
    notifyQueries(770_937_060) { emit ->
      emit("accounts")
    }
  }

  public suspend fun reparentTransactionsToAccount(newAccountId: String, oldAccountId: String) {
    driver.execute(-1_088_874_010, """
        |UPDATE transactions SET
        |    account_id = ?,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE account_id = ?
        """.trimMargin(), 2) {
          bindString(0, newAccountId)
          bindString(1, oldAccountId)
        }.await()
    notifyQueries(-1_088_874_010) { emit ->
      emit("transactions")
    }
  }

  public suspend fun reparentChildTransactionsToParent(newParentId: String?, oldParentId: String?) {
    driver.execute(null, """
        |UPDATE transactions SET
        |    parent_transaction_id = ?,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE parent_transaction_id ${ if (oldParentId == null) "IS" else "=" } ?
        """.trimMargin(), 2) {
          bindString(0, newParentId)
          bindString(1, oldParentId)
        }.await()
    notifyQueries(-1_091_777_857) { emit ->
      emit("transactions")
    }
  }

  public suspend fun copyUserEditsToTransaction(
    userCategory: String?,
    userNotes: String?,
    tags: String?,
    isVerified: Long,
    verifiedAt: String?,
    isExcluded: Long,
    isHidden: Long,
    isExpenseReport: Long,
    categorySource: String?,
    transactionId: String,
  ) {
    driver.execute(-1_196_262_956, """
        |UPDATE transactions SET
        |    user_category = ?,
        |    user_notes = ?,
        |    tags = ?,
        |    is_verified = ?,
        |    verified_at = ?,
        |    is_excluded = ?,
        |    is_hidden = ?,
        |    is_expense_report = ?,
        |    category_source = ?,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ?
        """.trimMargin(), 10) {
          bindString(0, userCategory)
          bindString(1, userNotes)
          bindString(2, tags)
          bindLong(3, isVerified)
          bindString(4, verifiedAt)
          bindLong(5, isExcluded)
          bindLong(6, isHidden)
          bindLong(7, isExpenseReport)
          bindString(8, categorySource)
          bindString(9, transactionId)
        }.await()
    notifyQueries(-1_196_262_956) { emit ->
      emit("transactions")
    }
  }

  public suspend fun deleteTransactionByIdInternal(transaction_id: String) {
    driver.execute(-662_007_384, """DELETE FROM transactions WHERE transaction_id = ?""", 1) {
          bindString(0, transaction_id)
        }.await()
    notifyQueries(-662_007_384) { emit ->
      emit("transaction_embeddings")
      emit("transaction_goal_assignments")
      emit("transactions")
      emit("user_corrections")
    }
  }

  public suspend fun deleteAccountByAccountId(account_id: String) {
    driver.execute(996_166_985, """DELETE FROM accounts WHERE account_id = ?""", 1) {
          bindString(0, account_id)
        }.await()
    notifyQueries(996_166_985) { emit ->
      emit("accounts")
      emit("transactions")
    }
  }

  public suspend fun deleteStaleDuplicateAccounts(userId: String) {
    driver.execute(-324_324_067, """
        |DELETE FROM accounts
        |WHERE user_id = ?
        |  AND id NOT IN (
        |    SELECT MAX(id) FROM accounts
        |    WHERE user_id = ?
        |    GROUP BY name, COALESCE(mask, ''), type, COALESCE(subtype, '')
        |  )
        """.trimMargin(), 2) {
          bindString(0, userId)
          bindString(1, userId)
        }.await()
    notifyQueries(-324_324_067) { emit ->
      emit("accounts")
      emit("transactions")
    }
  }

  public suspend fun remapTransactionAccountId(new_account_id: String, old_account_id: String) {
    driver.execute(905_065_209, """
        |UPDATE transactions SET account_id = ?
        |WHERE account_id = ?
        """.trimMargin(), 2) {
          bindString(0, new_account_id)
          bindString(1, old_account_id)
        }.await()
    notifyQueries(905_065_209) { emit ->
      emit("transactions")
    }
  }

  public suspend fun insertPlaidToken(
    user_id: String,
    access_token: String,
    item_id: String,
    institution_id: String?,
    institution_name: String?,
    state: String,
    security_level: String,
    webhook_url: String?,
    days_requested: Long?,
  ) {
    driver.execute(-543_859_508, """
        |INSERT INTO plaid_tokens(
        |    user_id, access_token, item_id, institution_id, institution_name,
        |    state, security_level, webhook_url, days_requested
        |) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 9) {
          bindString(0, user_id)
          bindString(1, access_token)
          bindString(2, item_id)
          bindString(3, institution_id)
          bindString(4, institution_name)
          bindString(5, state)
          bindString(6, security_level)
          bindString(7, webhook_url)
          bindLong(8, days_requested)
        }.await()
    notifyQueries(-543_859_508) { emit ->
      emit("plaid_tokens")
    }
  }

  public suspend fun updateTokenState(
    state: String,
    error_code: String?,
    error_message: String?,
    item_id: String,
  ) {
    driver.execute(1_614_734_555, """
        |UPDATE plaid_tokens SET
        |    state = ?, error_code = ?, error_message = ?, updated_at = CURRENT_TIMESTAMP
        |WHERE item_id = ?
        """.trimMargin(), 4) {
          bindString(0, state)
          bindString(1, error_code)
          bindString(2, error_message)
          bindString(3, item_id)
        }.await()
    notifyQueries(1_614_734_555) { emit ->
      emit("plaid_tokens")
    }
  }

  public suspend fun updateTokenAccess(
    access_token: String,
    institution_id: String?,
    institution_name: String?,
    state: String,
    security_level: String,
    item_id: String,
  ) {
    driver.execute(-2_013_815_206, """
        |UPDATE plaid_tokens SET
        |    access_token = ?,
        |    institution_id = ?,
        |    institution_name = ?,
        |    state = ?,
        |    security_level = ?,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE item_id = ?
        """.trimMargin(), 6) {
          bindString(0, access_token)
          bindString(1, institution_id)
          bindString(2, institution_name)
          bindString(3, state)
          bindString(4, security_level)
          bindString(5, item_id)
        }.await()
    notifyQueries(-2_013_815_206) { emit ->
      emit("plaid_tokens")
    }
  }

  public suspend fun markTokenUsed(item_id: String) {
    driver.execute(314_966_575,
        """UPDATE plaid_tokens SET last_used_at = CURRENT_TIMESTAMP WHERE item_id = ?""", 1) {
          bindString(0, item_id)
        }.await()
    notifyQueries(314_966_575) { emit ->
      emit("plaid_tokens")
    }
  }

  public suspend fun deactivateTokenByItemId(item_id: String) {
    driver.execute(-2_123_801_232, """
        |UPDATE plaid_tokens SET
        |    state = 'INACTIVE',
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE item_id = ?
        """.trimMargin(), 1) {
          bindString(0, item_id)
        }.await()
    notifyQueries(-2_123_801_232) { emit ->
      emit("plaid_tokens")
    }
  }

  public suspend fun updateSyncCursor(transactions_cursor: String?, item_id: String) {
    driver.execute(-1_124_667_244, """
        |UPDATE plaid_tokens SET
        |    transactions_cursor = ?,
        |    last_sync_at = CURRENT_TIMESTAMP,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE item_id = ?
        """.trimMargin(), 2) {
          bindString(0, transactions_cursor)
          bindString(1, item_id)
        }.await()
    notifyQueries(-1_124_667_244) { emit ->
      emit("plaid_tokens")
    }
  }

  public suspend fun markItemRelinked(item_id: String) {
    driver.execute(315_078_342, """
        |UPDATE plaid_tokens SET
        |    last_relinked_at = CURRENT_TIMESTAMP,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE item_id = ?
        """.trimMargin(), 1) {
          bindString(0, item_id)
        }.await()
    notifyQueries(315_078_342) { emit ->
      emit("plaid_tokens")
    }
  }

  public suspend fun markItemTransactionsRefreshed(item_id: String) {
    driver.execute(-1_996_364_693, """
        |UPDATE plaid_tokens SET
        |    last_transactions_refresh_at = CURRENT_TIMESTAMP,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE item_id = ?
        """.trimMargin(), 1) {
          bindString(0, item_id)
        }.await()
    notifyQueries(-1_996_364_693) { emit ->
      emit("plaid_tokens")
    }
  }

  public suspend fun markInitialSyncComplete(item_id: String) {
    driver.execute(401_464_305, """
        |UPDATE plaid_tokens SET
        |    initial_sync_complete = 1,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE item_id = ?
        """.trimMargin(), 1) {
          bindString(0, item_id)
        }.await()
    notifyQueries(401_464_305) { emit ->
      emit("plaid_tokens")
    }
  }

  public suspend fun markHistoricalSyncComplete(item_id: String) {
    driver.execute(1_834_963_621, """
        |UPDATE plaid_tokens SET
        |    historical_sync_complete = 1,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE item_id = ?
        """.trimMargin(), 1) {
          bindString(0, item_id)
        }.await()
    notifyQueries(1_834_963_621) { emit ->
      emit("plaid_tokens")
    }
  }

  public suspend fun markPlaidRemovalPending(item_id: String) {
    driver.execute(323_563_396, """
        |UPDATE plaid_tokens SET
        |    plaid_removal_status = 'PENDING',
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE item_id = ?
        """.trimMargin(), 1) {
          bindString(0, item_id)
        }.await()
    notifyQueries(323_563_396) { emit ->
      emit("plaid_tokens")
    }
  }

  public suspend fun markPlaidRemovalDone(item_id: String) {
    driver.execute(1_234_767_029, """
        |UPDATE plaid_tokens SET
        |    plaid_removal_status = 'REMOVED',
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE item_id = ?
        """.trimMargin(), 1) {
          bindString(0, item_id)
        }.await()
    notifyQueries(1_234_767_029) { emit ->
      emit("plaid_tokens")
    }
  }

  public suspend fun insertPendingPlaidRemoval(
    item_id: String,
    access_token: String,
    user_id: String?,
    institution_name: String?,
    error_message: String?,
  ) {
    driver.execute(1_490_474_480, """
        |INSERT OR REPLACE INTO pending_plaid_removals(item_id, access_token, user_id, institution_name, error_message)
        |VALUES (?, ?, ?, ?, ?)
        """.trimMargin(), 5) {
          bindString(0, item_id)
          bindString(1, access_token)
          bindString(2, user_id)
          bindString(3, institution_name)
          bindString(4, error_message)
        }.await()
    notifyQueries(1_490_474_480) { emit ->
      emit("pending_plaid_removals")
    }
  }

  public suspend fun updatePendingRemovalAttempt(error_message: String?, item_id: String) {
    driver.execute(-223_226_859, """
        |UPDATE pending_plaid_removals SET
        |    attempts = attempts + 1,
        |    last_attempt_at = CURRENT_TIMESTAMP,
        |    error_message = ?
        |WHERE item_id = ?
        """.trimMargin(), 2) {
          bindString(0, error_message)
          bindString(1, item_id)
        }.await()
    notifyQueries(-223_226_859) { emit ->
      emit("pending_plaid_removals")
    }
  }

  public suspend fun deletePendingRemoval(item_id: String) {
    driver.execute(-404_556_550, """DELETE FROM pending_plaid_removals WHERE item_id = ?""", 1) {
          bindString(0, item_id)
        }.await()
    notifyQueries(-404_556_550) { emit ->
      emit("pending_plaid_removals")
    }
  }

  public suspend fun insertUserCorrection(
    transaction_id: String,
    user_id: String,
    old_category: String,
    new_category: String,
    original_confidence: Double?,
    correction_reason: String?,
    ml_model_version: String?,
    merchant_name: String?,
    transaction_amount: Double?,
  ) {
    driver.execute(1_103_603_900, """
        |INSERT INTO user_corrections(
        |    transaction_id, user_id, old_category, new_category, original_confidence,
        |    correction_reason, ml_model_version, merchant_name, transaction_amount
        |) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 9) {
          bindString(0, transaction_id)
          bindString(1, user_id)
          bindString(2, old_category)
          bindString(3, new_category)
          bindDouble(4, original_confidence)
          bindString(5, correction_reason)
          bindString(6, ml_model_version)
          bindString(7, merchant_name)
          bindDouble(8, transaction_amount)
        }.await()
    notifyQueries(1_103_603_900) { emit ->
      emit("user_corrections")
    }
  }

  public suspend fun deleteCorrection(id: Long) {
    driver.execute(-1_852_133_245, """DELETE FROM user_corrections WHERE id = ?""", 1) {
          bindLong(0, id)
        }.await()
    notifyQueries(-1_852_133_245) { emit ->
      emit("user_corrections")
    }
  }

  public suspend fun insertTransactionEmbedding(
    transaction_id: String,
    user_id: String,
    embedding_vector: ByteArray?,
    embedding_dimension: Long,
    source_text: String,
    embedding_model: String,
    embedding_version: String?,
    category_hint: String?,
    similarity_threshold: Double?,
  ) {
    driver.execute(1_715_879_692, """
        |INSERT INTO transaction_embeddings(
        |    transaction_id, user_id, embedding_vector, embedding_dimension, source_text,
        |    embedding_model, embedding_version, category_hint, similarity_threshold
        |) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 9) {
          bindString(0, transaction_id)
          bindString(1, user_id)
          bindBytes(2, embedding_vector)
          bindLong(3, embedding_dimension)
          bindString(4, source_text)
          bindString(5, embedding_model)
          bindString(6, embedding_version)
          bindString(7, category_hint)
          bindDouble(8, similarity_threshold)
        }.await()
    notifyQueries(1_715_879_692) { emit ->
      emit("transaction_embeddings")
    }
  }

  public suspend fun updateTransactionEmbedding(
    embedding_vector: ByteArray?,
    embedding_dimension: Long,
    source_text: String,
    embedding_model: String,
    embedding_version: String?,
    transaction_id: String,
  ) {
    driver.execute(-401_732_324, """
        |UPDATE transaction_embeddings SET
        |    embedding_vector = ?, embedding_dimension = ?, source_text = ?,
        |    embedding_model = ?, embedding_version = ?, updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ?
        """.trimMargin(), 6) {
          bindBytes(0, embedding_vector)
          bindLong(1, embedding_dimension)
          bindString(2, source_text)
          bindString(3, embedding_model)
          bindString(4, embedding_version)
          bindString(5, transaction_id)
        }.await()
    notifyQueries(-401_732_324) { emit ->
      emit("transaction_embeddings")
    }
  }

  public suspend fun deleteTransactionEmbedding(transaction_id: String) {
    driver.execute(593_433_854, """DELETE FROM transaction_embeddings WHERE transaction_id = ?""",
        1) {
          bindString(0, transaction_id)
        }.await()
    notifyQueries(593_433_854) { emit ->
      emit("transaction_embeddings")
    }
  }

  public suspend fun insertSystemCategory(
    name: String,
    parent_id: Long?,
    color: String?,
    icon: String?,
  ) {
    driver.execute(2_011_477_888, """
        |INSERT INTO transaction_categories(user_id, name, parent_id, color, icon, is_system)
        |VALUES ('system', ?, ?, ?, ?, 1)
        """.trimMargin(), 4) {
          bindString(0, name)
          bindLong(1, parent_id)
          bindString(2, color)
          bindString(3, icon)
        }.await()
    notifyQueries(2_011_477_888) { emit ->
      emit("transaction_categories")
    }
  }

  public suspend fun insertUserCategory(
    user_id: String,
    name: String,
    parent_id: Long?,
    color: String?,
    icon: String?,
  ) {
    driver.execute(-472_447_524, """
        |INSERT INTO transaction_categories(user_id, name, parent_id, color, icon, is_system)
        |VALUES (?, ?, ?, ?, ?, 0)
        """.trimMargin(), 5) {
          bindString(0, user_id)
          bindString(1, name)
          bindLong(2, parent_id)
          bindString(3, color)
          bindString(4, icon)
        }.await()
    notifyQueries(-472_447_524) { emit ->
      emit("transaction_categories")
    }
  }

  public suspend fun updateCategory(
    name: String,
    parent_id: Long?,
    color: String?,
    icon: String?,
    id: Long,
    user_id: String,
  ) {
    driver.execute(-859_633_535, """
        |UPDATE transaction_categories SET
        |    name = ?, parent_id = ?, color = ?, icon = ?
        |WHERE id = ? AND user_id = ? AND is_system = 0
        """.trimMargin(), 6) {
          bindString(0, name)
          bindLong(1, parent_id)
          bindString(2, color)
          bindString(3, icon)
          bindLong(4, id)
          bindString(5, user_id)
        }.await()
    notifyQueries(-859_633_535) { emit ->
      emit("transaction_categories")
    }
  }

  public suspend fun deleteUserCategory(id: Long, user_id: String) {
    driver.execute(-703_205_938, """
        |DELETE FROM transaction_categories
        |WHERE id = ? AND user_id = ? AND is_system = 0
        """.trimMargin(), 2) {
          bindLong(0, id)
          bindString(1, user_id)
        }.await()
    notifyQueries(-703_205_938) { emit ->
      emit("transaction_categories")
    }
  }

  public suspend fun insertSavingsGoal(
    id: String,
    user_id: String,
    name: String,
    description: String?,
    target_amount: Double,
    current_amount: Double?,
    deadline: String,
    category: String,
    priority: String?,
    created_date: String,
    is_active: Long,
    icon_emoji: String?,
  ) {
    driver.execute(-1_379_132_135, """
        |INSERT INTO savings_goals(
        |    id, user_id, name, description, target_amount, current_amount, deadline,
        |    category, priority, created_date, is_active, icon_emoji, created_at, updated_at
        |) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP, CURRENT_TIMESTAMP)
        """.trimMargin(), 12) {
          bindString(0, id)
          bindString(1, user_id)
          bindString(2, name)
          bindString(3, description)
          bindDouble(4, target_amount)
          bindDouble(5, current_amount)
          bindString(6, deadline)
          bindString(7, category)
          bindString(8, priority)
          bindString(9, created_date)
          bindLong(10, is_active)
          bindString(11, icon_emoji)
        }.await()
    notifyQueries(-1_379_132_135) { emit ->
      emit("savings_goals")
    }
  }

  public suspend fun upsertSavingsGoal(
    id: String,
    user_id: String,
    name: String,
    description: String?,
    target_amount: Double,
    current_amount: Double?,
    deadline: String,
    category: String,
    priority: String?,
    created_date: String,
    is_active: Long,
    icon_emoji: String?,
  ) {
    driver.execute(954_613_987, """
        |INSERT INTO savings_goals(
        |    id, user_id, name, description, target_amount, current_amount, deadline,
        |    category, priority, created_date, is_active, icon_emoji, created_at, updated_at
        |) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP, CURRENT_TIMESTAMP)
        |ON CONFLICT(id) DO UPDATE SET
        |    user_id = excluded.user_id,
        |    name = excluded.name,
        |    description = excluded.description,
        |    target_amount = excluded.target_amount,
        |    current_amount = excluded.current_amount,
        |    deadline = excluded.deadline,
        |    category = excluded.category,
        |    priority = excluded.priority,
        |    created_date = excluded.created_date,
        |    is_active = excluded.is_active,
        |    icon_emoji = excluded.icon_emoji,
        |    updated_at = CURRENT_TIMESTAMP
        """.trimMargin(), 12) {
          bindString(0, id)
          bindString(1, user_id)
          bindString(2, name)
          bindString(3, description)
          bindDouble(4, target_amount)
          bindDouble(5, current_amount)
          bindString(6, deadline)
          bindString(7, category)
          bindString(8, priority)
          bindString(9, created_date)
          bindLong(10, is_active)
          bindString(11, icon_emoji)
        }.await()
    notifyQueries(954_613_987) { emit ->
      emit("savings_goals")
    }
  }

  public suspend fun updateGoalProgress(current_amount: Double?, id: String) {
    driver.execute(-1_638_026_141, """
        |UPDATE savings_goals SET
        |    current_amount = ?, updated_at = CURRENT_TIMESTAMP
        |WHERE id = ?
        """.trimMargin(), 2) {
          bindDouble(0, current_amount)
          bindString(1, id)
        }.await()
    notifyQueries(-1_638_026_141) { emit ->
      emit("savings_goals")
    }
  }

  public suspend fun updateGoal(
    name: String,
    description: String?,
    target_amount: Double,
    current_amount: Double?,
    deadline: String,
    category: String,
    priority: String?,
    icon_emoji: String?,
    id: String,
    user_id: String,
  ) {
    driver.execute(663_815_190, """
        |UPDATE savings_goals SET
        |    name = ?, description = ?, target_amount = ?, current_amount = ?, deadline = ?,
        |    category = ?, priority = ?, icon_emoji = ?, updated_at = CURRENT_TIMESTAMP
        |WHERE id = ? AND user_id = ?
        """.trimMargin(), 10) {
          bindString(0, name)
          bindString(1, description)
          bindDouble(2, target_amount)
          bindDouble(3, current_amount)
          bindString(4, deadline)
          bindString(5, category)
          bindString(6, priority)
          bindString(7, icon_emoji)
          bindString(8, id)
          bindString(9, user_id)
        }.await()
    notifyQueries(663_815_190) { emit ->
      emit("savings_goals")
    }
  }

  public suspend fun updateGoalAiFields(
    account_type: String?,
    debt_interest_rate: Double?,
    debt_remaining_balance: Double?,
    debt_remaining_months: Long?,
    debt_payment_frequency: String?,
    debt_monthly_equivalent_payment: Double?,
    id: String,
    user_id: String,
  ) {
    driver.execute(1_714_662_199, """
        |UPDATE savings_goals SET
        |    account_type = ?,
        |    debt_interest_rate = ?,
        |    debt_remaining_balance = ?,
        |    debt_remaining_months = ?,
        |    debt_payment_frequency = ?,
        |    debt_monthly_equivalent_payment = ?,
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE id = ? AND user_id = ?
        """.trimMargin(), 8) {
          bindString(0, account_type)
          bindDouble(1, debt_interest_rate)
          bindDouble(2, debt_remaining_balance)
          bindLong(3, debt_remaining_months)
          bindString(4, debt_payment_frequency)
          bindDouble(5, debt_monthly_equivalent_payment)
          bindString(6, id)
          bindString(7, user_id)
        }.await()
    notifyQueries(1_714_662_199) { emit ->
      emit("savings_goals")
    }
  }

  public suspend fun markGoalInactive(id: String) {
    driver.execute(-677_833_435, """UPDATE savings_goals SET is_active = 0 WHERE id = ?""", 1) {
          bindString(0, id)
        }.await()
    notifyQueries(-677_833_435) { emit ->
      emit("savings_goals")
    }
  }

  public suspend fun deleteGoal(id: String, user_id: String) {
    driver.execute(-1_570_631_688, """DELETE FROM savings_goals WHERE id = ? AND user_id = ?""", 2)
        {
          bindString(0, id)
          bindString(1, user_id)
        }.await()
    notifyQueries(-1_570_631_688) { emit ->
      emit("goal_assignment_rules")
      emit("goal_progress_history")
      emit("savings_goals")
      emit("savings_recommendations")
      emit("transaction_goal_assignments")
    }
  }

  public suspend fun insertRecommendation(
    goal_id: String,
    user_id: String,
    spending_category: String,
    current_monthly_spending: Double,
    recommended_monthly_spending: Double,
    suggested_reduction: Double,
    rationale: String,
    potential_savings: Double,
    confidence_score: Double,
    priority: Long,
  ) {
    driver.execute(-1_835_203_860, """
        |INSERT INTO savings_recommendations(
        |    goal_id, user_id, spending_category, current_monthly_spending,
        |    recommended_monthly_spending, suggested_reduction, rationale,
        |    potential_savings, confidence_score, priority
        |) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 10) {
          bindString(0, goal_id)
          bindString(1, user_id)
          bindString(2, spending_category)
          bindDouble(3, current_monthly_spending)
          bindDouble(4, recommended_monthly_spending)
          bindDouble(5, suggested_reduction)
          bindString(6, rationale)
          bindDouble(7, potential_savings)
          bindDouble(8, confidence_score)
          bindLong(9, priority)
        }.await()
    notifyQueries(-1_835_203_860) { emit ->
      emit("savings_recommendations")
    }
  }

  public suspend fun markRecommendationApplied(id: Long) {
    driver.execute(-1_795_545_635, """
        |UPDATE savings_recommendations SET
        |    is_applied = 1, applied_at = CURRENT_TIMESTAMP
        |WHERE id = ?
        """.trimMargin(), 1) {
          bindLong(0, id)
        }.await()
    notifyQueries(-1_795_545_635) { emit ->
      emit("savings_recommendations")
    }
  }

  public suspend fun deleteRecommendationsForGoal(goal_id: String) {
    driver.execute(-1_665_520_665, """DELETE FROM savings_recommendations WHERE goal_id = ?""", 1) {
          bindString(0, goal_id)
        }.await()
    notifyQueries(-1_665_520_665) { emit ->
      emit("savings_recommendations")
    }
  }

  public suspend fun insertProgressEntry(
    goal_id: String,
    user_id: String,
    contribution_amount: Double,
    contribution_date: String,
    notes: String?,
  ) {
    driver.execute(-238_865_230, """
        |INSERT INTO goal_progress_history(
        |    goal_id, user_id, contribution_amount, contribution_date, notes
        |) VALUES (?, ?, ?, ?, ?)
        """.trimMargin(), 5) {
          bindString(0, goal_id)
          bindString(1, user_id)
          bindDouble(2, contribution_amount)
          bindString(3, contribution_date)
          bindString(4, notes)
        }.await()
    notifyQueries(-238_865_230) { emit ->
      emit("goal_progress_history")
    }
  }

  public suspend fun deleteProgressHistoryForGoal(goal_id: String) {
    driver.execute(-388_704_902, """DELETE FROM goal_progress_history WHERE goal_id = ?""", 1) {
          bindString(0, goal_id)
        }.await()
    notifyQueries(-388_704_902) { emit ->
      emit("goal_progress_history")
    }
  }

  public suspend fun insertGoalAssignmentRule(
    user_id: String,
    goal_id: String,
    merchant_name: String?,
    account_id: String?,
    description_pattern: String?,
    priority: Long?,
    is_default: Long?,
    rule_mode: String?,
  ) {
    driver.execute(159_400_719, """
        |INSERT INTO goal_assignment_rules(
        |    user_id, goal_id, merchant_name, account_id, description_pattern,
        |    priority, is_default, rule_mode
        |) VALUES (?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 8) {
          bindString(0, user_id)
          bindString(1, goal_id)
          bindString(2, merchant_name)
          bindString(3, account_id)
          bindString(4, description_pattern)
          bindLong(5, priority)
          bindLong(6, is_default)
          bindString(7, rule_mode)
        }.await()
    notifyQueries(159_400_719) { emit ->
      emit("goal_assignment_rules")
    }
  }

  public suspend fun updateRuleMode(
    rule_mode: String?,
    is_default: Long?,
    priority: Long?,
    id: Long,
  ) {
    driver.execute(-146_886_078, """
        |UPDATE goal_assignment_rules SET
        |    rule_mode = ?, is_default = ?, priority = ?, updated_at = CURRENT_TIMESTAMP
        |WHERE id = ?
        """.trimMargin(), 4) {
          bindString(0, rule_mode)
          bindLong(1, is_default)
          bindLong(2, priority)
          bindLong(3, id)
        }.await()
    notifyQueries(-146_886_078) { emit ->
      emit("goal_assignment_rules")
    }
  }

  public suspend fun deleteRule(id: Long) {
    driver.execute(-1_570_297_887, """DELETE FROM goal_assignment_rules WHERE id = ?""", 1) {
          bindLong(0, id)
        }.await()
    notifyQueries(-1_570_297_887) { emit ->
      emit("goal_assignment_rules")
      emit("transaction_goal_assignments")
    }
  }

  public suspend fun deleteRulesForGoal(goal_id: String) {
    driver.execute(-312_251_798, """DELETE FROM goal_assignment_rules WHERE goal_id = ?""", 1) {
          bindString(0, goal_id)
        }.await()
    notifyQueries(-312_251_798) { emit ->
      emit("goal_assignment_rules")
      emit("transaction_goal_assignments")
    }
  }

  public suspend fun insertTransactionGoalAssignment(
    transaction_id: String,
    goal_id: String,
    user_id: String,
    amount_allocated: Double,
    assignment_source: String?,
    rule_id: Long?,
  ) {
    driver.execute(756_676_523, """
        |INSERT OR REPLACE INTO transaction_goal_assignments(
        |    transaction_id, goal_id, user_id, amount_allocated, assignment_source, rule_id
        |) VALUES (?, ?, ?, ?, ?, ?)
        """.trimMargin(), 6) {
          bindString(0, transaction_id)
          bindString(1, goal_id)
          bindString(2, user_id)
          bindDouble(3, amount_allocated)
          bindString(4, assignment_source)
          bindLong(5, rule_id)
        }.await()
    notifyQueries(756_676_523) { emit ->
      emit("transaction_goal_assignments")
    }
  }

  public suspend fun deleteAssignment(transaction_id: String, goal_id: String) {
    driver.execute(-1_981_540_366, """
        |DELETE FROM transaction_goal_assignments
        |WHERE transaction_id = ? AND goal_id = ?
        """.trimMargin(), 2) {
          bindString(0, transaction_id)
          bindString(1, goal_id)
        }.await()
    notifyQueries(-1_981_540_366) { emit ->
      emit("transaction_goal_assignments")
    }
  }

  public suspend fun deleteAssignmentsForTransaction(transaction_id: String) {
    driver.execute(597_332_854, """
        |DELETE FROM transaction_goal_assignments
        |WHERE transaction_id = ?
        """.trimMargin(), 1) {
          bindString(0, transaction_id)
        }.await()
    notifyQueries(597_332_854) { emit ->
      emit("transaction_goal_assignments")
    }
  }

  public suspend fun deleteAssignmentsForChildrenOf(parent_transaction_id: String?) {
    driver.execute(null, """
        |DELETE FROM transaction_goal_assignments
        |WHERE transaction_id IN (
        |    SELECT transaction_id FROM transactions WHERE parent_transaction_id ${ if (parent_transaction_id == null) "IS" else "=" } ?
        |)
        """.trimMargin(), 1) {
          bindString(0, parent_transaction_id)
        }.await()
    notifyQueries(768_258_206) { emit ->
      emit("transaction_goal_assignments")
    }
  }

  public suspend fun deleteAssignmentsForAccount(account_id: String, user_id: String) {
    driver.execute(-1_288_350_011, """
        |DELETE FROM transaction_goal_assignments
        |WHERE transaction_id IN (
        |    SELECT transaction_id FROM transactions WHERE account_id = ? AND user_id = ?
        |)
        """.trimMargin(), 2) {
          bindString(0, account_id)
          bindString(1, user_id)
        }.await()
    notifyQueries(-1_288_350_011) { emit ->
      emit("transaction_goal_assignments")
    }
  }

  public suspend fun deleteAssignmentsForGoal(goal_id: String) {
    driver.execute(-1_719_944_837, """
        |DELETE FROM transaction_goal_assignments
        |WHERE goal_id = ?
        """.trimMargin(), 1) {
          bindString(0, goal_id)
        }.await()
    notifyQueries(-1_719_944_837) { emit ->
      emit("transaction_goal_assignments")
    }
  }

  public suspend fun insertBudgetPlan(
    id: String,
    user_id: String,
    year: Long,
    month: Long,
    category_type: String,
    category_name: String,
    planned_amount: Double,
  ) {
    driver.execute(-1_959_143_231, """
        |INSERT INTO budget_plans(id, user_id, year, month, category_type, category_name, planned_amount)
        |VALUES (?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 7) {
          bindString(0, id)
          bindString(1, user_id)
          bindLong(2, year)
          bindLong(3, month)
          bindString(4, category_type)
          bindString(5, category_name)
          bindDouble(6, planned_amount)
        }.await()
    notifyQueries(-1_959_143_231) { emit ->
      emit("budget_plans")
    }
  }

  public suspend fun upsertBudgetPlan(
    id: String,
    user_id: String,
    year: Long,
    month: Long,
    category_type: String,
    category_name: String,
    planned_amount: Double,
  ) {
    driver.execute(-775_482_441, """
        |INSERT OR REPLACE INTO budget_plans(id, user_id, year, month, category_type, category_name, planned_amount, updated_at)
        |VALUES (?, ?, ?, ?, ?, ?, ?, CURRENT_TIMESTAMP)
        """.trimMargin(), 7) {
          bindString(0, id)
          bindString(1, user_id)
          bindLong(2, year)
          bindLong(3, month)
          bindString(4, category_type)
          bindString(5, category_name)
          bindDouble(6, planned_amount)
        }.await()
    notifyQueries(-775_482_441) { emit ->
      emit("budget_plans")
    }
  }

  public suspend fun updateBudgetPlanAmount(planned_amount: Double, id: String) {
    driver.execute(-2_102_716_791, """
        |UPDATE budget_plans SET
        |    planned_amount = ?, updated_at = CURRENT_TIMESTAMP
        |WHERE id = ?
        """.trimMargin(), 2) {
          bindDouble(0, planned_amount)
          bindString(1, id)
        }.await()
    notifyQueries(-2_102_716_791) { emit ->
      emit("budget_plans")
    }
  }

  public suspend fun deleteBudgetPlan(id: String) {
    driver.execute(597_038_387, """DELETE FROM budget_plans WHERE id = ?""", 1) {
          bindString(0, id)
        }.await()
    notifyQueries(597_038_387) { emit ->
      emit("budget_plans")
    }
  }

  public suspend fun deleteBudgetPlanByKey(
    user_id: String,
    year: Long,
    month: Long,
    category_type: String,
    category_name: String,
  ) {
    driver.execute(-623_875_819,
        """DELETE FROM budget_plans WHERE user_id = ? AND year = ? AND month = ? AND category_type = ? AND category_name = ?""",
        5) {
          bindString(0, user_id)
          bindLong(1, year)
          bindLong(2, month)
          bindString(3, category_type)
          bindString(4, category_name)
        }.await()
    notifyQueries(-623_875_819) { emit ->
      emit("budget_plans")
    }
  }

  public suspend fun deleteBudgetPlansByYear(user_id: String, year: Long) {
    driver.execute(756_271_860, """DELETE FROM budget_plans WHERE user_id = ? AND year = ?""", 2) {
          bindString(0, user_id)
          bindLong(1, year)
        }.await()
    notifyQueries(756_271_860) { emit ->
      emit("budget_plans")
    }
  }

  public suspend fun deleteBudgetPlansByCategoryYear(
    user_id: String,
    year: Long,
    category_type: String,
    category_name: String,
  ) {
    driver.execute(-590_436_846, """
        |DELETE FROM budget_plans
        |WHERE user_id = ? AND year = ? AND category_type = ? AND category_name = ?
        """.trimMargin(), 4) {
          bindString(0, user_id)
          bindLong(1, year)
          bindString(2, category_type)
          bindString(3, category_name)
        }.await()
    notifyQueries(-590_436_846) { emit ->
      emit("budget_plans")
    }
  }

  public suspend fun insertAdviceCard(
    id: String,
    category: String,
    title: String,
    content: String,
    icon: String?,
    priority: Long?,
    is_active: Long?,
    country: String?,
  ) {
    driver.execute(1_908_638_939, """
        |INSERT INTO advice_cards(id, category, title, content, icon, priority, is_active, country)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 8) {
          bindString(0, id)
          bindString(1, category)
          bindString(2, title)
          bindString(3, content)
          bindString(4, icon)
          bindLong(5, priority)
          bindLong(6, is_active)
          bindString(7, country)
        }.await()
    notifyQueries(1_908_638_939) { emit ->
      emit("advice_cards")
    }
  }

  public suspend fun updateAdviceCard(
    title: String,
    content: String,
    icon: String?,
    priority: Long?,
    is_active: Long?,
    id: String,
  ) {
    driver.execute(407_021_291, """
        |UPDATE advice_cards SET
        |    title = ?, content = ?, icon = ?, priority = ?, is_active = ?
        |WHERE id = ?
        """.trimMargin(), 6) {
          bindString(0, title)
          bindString(1, content)
          bindString(2, icon)
          bindLong(3, priority)
          bindLong(4, is_active)
          bindString(5, id)
        }.await()
    notifyQueries(407_021_291) { emit ->
      emit("advice_cards")
    }
  }

  public suspend fun updateAdviceCardCountry(country: String?, id: String) {
    driver.execute(1_956_297_259, """UPDATE advice_cards SET country = ? WHERE id = ?""", 2) {
          bindString(0, country)
          bindString(1, id)
        }.await()
    notifyQueries(1_956_297_259) { emit ->
      emit("advice_cards")
    }
  }

  public suspend fun deactivateAdviceCard(id: String) {
    driver.execute(-1_468_872_746, """UPDATE advice_cards SET is_active = 0 WHERE id = ?""", 1) {
          bindString(0, id)
        }.await()
    notifyQueries(-1_468_872_746) { emit ->
      emit("advice_cards")
    }
  }

  public suspend fun insertUserAdviceCard(
    id: String,
    user_id: String,
    card_id: String,
    is_pinned: Long?,
    is_dismissed: Long?,
    viewed_at: String?,
  ) {
    driver.execute(-1_169_376_890, """
        |INSERT INTO user_advice_cards(id, user_id, card_id, is_pinned, is_dismissed, viewed_at)
        |VALUES (?, ?, ?, ?, ?, ?)
        """.trimMargin(), 6) {
          bindString(0, id)
          bindString(1, user_id)
          bindString(2, card_id)
          bindLong(3, is_pinned)
          bindLong(4, is_dismissed)
          bindString(5, viewed_at)
        }.await()
    notifyQueries(-1_169_376_890) { emit ->
      emit("user_advice_cards")
    }
  }

  public suspend fun upsertUserAdviceCard(
    id: String,
    user_id: String,
    card_id: String,
    is_pinned: Long?,
    is_dismissed: Long?,
    viewed_at: String?,
  ) {
    driver.execute(825_723_260, """
        |INSERT OR REPLACE INTO user_advice_cards(id, user_id, card_id, is_pinned, is_dismissed, viewed_at)
        |VALUES (?, ?, ?, ?, ?, ?)
        """.trimMargin(), 6) {
          bindString(0, id)
          bindString(1, user_id)
          bindString(2, card_id)
          bindLong(3, is_pinned)
          bindLong(4, is_dismissed)
          bindString(5, viewed_at)
        }.await()
    notifyQueries(825_723_260) { emit ->
      emit("user_advice_cards")
    }
  }

  public suspend fun upsertInstitution(
    institution_id: String,
    name: String,
    country_codes: String?,
  ) {
    driver.execute(-1_754_133_713, """
        |INSERT INTO institutions(institution_id, name, country_codes)
        |VALUES (?, ?, ?)
        |ON CONFLICT(institution_id) DO UPDATE SET
        |    name = excluded.name,
        |    country_codes = COALESCE(excluded.country_codes, institutions.country_codes),
        |    updated_at = CURRENT_TIMESTAMP
        """.trimMargin(), 3) {
          bindString(0, institution_id)
          bindString(1, name)
          bindString(2, country_codes)
        }.await()
    notifyQueries(-1_754_133_713) { emit ->
      emit("institutions")
    }
  }

  public suspend fun pinAdviceCard(
    id: String,
    user_id: String,
    card_id: String,
  ) {
    driver.execute(1_643_029_699, """
        |INSERT OR REPLACE INTO user_advice_cards(id, user_id, card_id, is_pinned, is_dismissed, viewed_at)
        |VALUES (?, ?, ?, 1, 0, CURRENT_TIMESTAMP)
        """.trimMargin(), 3) {
          bindString(0, id)
          bindString(1, user_id)
          bindString(2, card_id)
        }.await()
    notifyQueries(1_643_029_699) { emit ->
      emit("user_advice_cards")
    }
  }

  public suspend fun unpinAdviceCard(user_id: String, card_id: String) {
    driver.execute(-189_782_710,
        """UPDATE user_advice_cards SET is_pinned = 0 WHERE user_id = ? AND card_id = ?""", 2) {
          bindString(0, user_id)
          bindString(1, card_id)
        }.await()
    notifyQueries(-189_782_710) { emit ->
      emit("user_advice_cards")
    }
  }

  public suspend fun dismissAdviceCard(
    id: String,
    user_id: String,
    card_id: String,
  ) {
    driver.execute(875_607_160, """
        |INSERT OR REPLACE INTO user_advice_cards(id, user_id, card_id, is_pinned, is_dismissed, viewed_at)
        |VALUES (?, ?, ?, 0, 1, CURRENT_TIMESTAMP)
        """.trimMargin(), 3) {
          bindString(0, id)
          bindString(1, user_id)
          bindString(2, card_id)
        }.await()
    notifyQueries(875_607_160) { emit ->
      emit("user_advice_cards")
    }
  }

  public suspend fun restoreDismissedCard(user_id: String, card_id: String) {
    driver.execute(-877_848_699,
        """UPDATE user_advice_cards SET is_dismissed = 0 WHERE user_id = ? AND card_id = ?""", 2) {
          bindString(0, user_id)
          bindString(1, card_id)
        }.await()
    notifyQueries(-877_848_699) { emit ->
      emit("user_advice_cards")
    }
  }

  public suspend fun markCardViewed(user_id: String, card_id: String) {
    driver.execute(1_807_555_451,
        """UPDATE user_advice_cards SET viewed_at = CURRENT_TIMESTAMP WHERE user_id = ? AND card_id = ?""",
        2) {
          bindString(0, user_id)
          bindString(1, card_id)
        }.await()
    notifyQueries(1_807_555_451) { emit ->
      emit("user_advice_cards")
    }
  }

  public suspend fun upsertUserSetting(
    user_id: String,
    setting_key: String,
    setting_value: String,
  ) {
    driver.execute(-961_981_060, """
        |INSERT OR REPLACE INTO user_settings(user_id, setting_key, setting_value, updated_at)
        |VALUES (?, ?, ?, CURRENT_TIMESTAMP)
        """.trimMargin(), 3) {
          bindString(0, user_id)
          bindString(1, setting_key)
          bindString(2, setting_value)
        }.await()
    notifyQueries(-961_981_060) { emit ->
      emit("user_settings")
    }
  }

  public suspend fun deleteUserSetting(user_id: String, setting_key: String) {
    driver.execute(-1_363_508_352,
        """DELETE FROM user_settings WHERE user_id = ? AND setting_key = ?""", 2) {
          bindString(0, user_id)
          bindString(1, setting_key)
        }.await()
    notifyQueries(-1_363_508_352) { emit ->
      emit("user_settings")
    }
  }

  public suspend fun dismissPersonalizedCard(
    user_id: String,
    card_id: String,
    expires_at: String?,
  ) {
    driver.execute(-2_020_137_136, """
        |INSERT OR REPLACE INTO dismissed_personalized_cards(user_id, card_id, dismissed_at, expires_at)
        |VALUES (?, ?, CURRENT_TIMESTAMP, ?)
        """.trimMargin(), 3) {
          bindString(0, user_id)
          bindString(1, card_id)
          bindString(2, expires_at)
        }.await()
    notifyQueries(-2_020_137_136) { emit ->
      emit("dismissed_personalized_cards")
    }
  }

  public suspend fun restorePersonalizedCard(user_id: String, card_id: String) {
    driver.execute(1_975_893_844,
        """DELETE FROM dismissed_personalized_cards WHERE user_id = ? AND card_id = ?""", 2) {
          bindString(0, user_id)
          bindString(1, card_id)
        }.await()
    notifyQueries(1_975_893_844) { emit ->
      emit("dismissed_personalized_cards")
    }
  }

  public suspend fun recordPersonalizedCardView(user_id: String, card_id: String) {
    driver.execute(708_086_864, """
        |INSERT INTO personalized_card_views(user_id, card_id, shown_at, shown_count)
        |VALUES (?, ?, CURRENT_TIMESTAMP, 1)
        |ON CONFLICT(user_id, card_id) DO UPDATE SET
        |    shown_at = CURRENT_TIMESTAMP,
        |    shown_count = shown_count + 1
        """.trimMargin(), 2) {
          bindString(0, user_id)
          bindString(1, card_id)
        }.await()
    notifyQueries(708_086_864) { emit ->
      emit("personalized_card_views")
    }
  }

  public suspend fun insertCustomCategory(
    user_id: String,
    primary_name: String,
    secondary_name: String,
    is_income: Long,
    icon: String?,
  ) {
    driver.execute(718_958_274, """
        |INSERT INTO user_custom_categories (user_id, primary_name, secondary_name, is_income, icon)
        |VALUES (?, ?, ?, ?, ?)
        """.trimMargin(), 5) {
          bindString(0, user_id)
          bindString(1, primary_name)
          bindString(2, secondary_name)
          bindLong(3, is_income)
          bindString(4, icon)
        }.await()
    notifyQueries(718_958_274) { emit ->
      emit("user_custom_categories")
    }
  }

  public suspend fun updateCustomCategory(
    primary_name: String,
    secondary_name: String,
    icon: String?,
    id: Long,
    user_id: String,
  ) {
    driver.execute(1_507_461_330, """
        |UPDATE user_custom_categories
        |SET primary_name = ?, secondary_name = ?, icon = ?
        |WHERE id = ? AND user_id = ?
        """.trimMargin(), 5) {
          bindString(0, primary_name)
          bindString(1, secondary_name)
          bindString(2, icon)
          bindLong(3, id)
          bindString(4, user_id)
        }.await()
    notifyQueries(1_507_461_330) { emit ->
      emit("user_custom_categories")
    }
  }

  public suspend fun softDeleteCustomCategory(id: Long, user_id: String) {
    driver.execute(849_933_438, """
        |UPDATE user_custom_categories
        |SET deleted_at = datetime('now')
        |WHERE id = ? AND user_id = ? AND deleted_at IS NULL
        """.trimMargin(), 2) {
          bindLong(0, id)
          bindString(1, user_id)
        }.await()
    notifyQueries(849_933_438) { emit ->
      emit("user_custom_categories")
    }
  }

  public suspend fun clearUserCategory(user_id: String, user_category: String?) {
    driver.execute(null, """
        |UPDATE transactions
        |SET user_category = NULL
        |WHERE user_id = ? AND user_category ${ if (user_category == null) "IS" else "=" } ?
        """.trimMargin(), 2) {
          bindString(0, user_id)
          bindString(1, user_category)
        }.await()
    notifyQueries(-1_112_003_844) { emit ->
      emit("transactions")
    }
  }

  public suspend fun reassignUserCategory(
    user_category: String?,
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    user_id: String,
    user_category_: String?,
  ) {
    driver.execute(null, """
        |UPDATE transactions
        |SET user_category = ?,
        |    personal_finance_category_primary = ?,
        |    personal_finance_category_detailed = ?
        |WHERE user_id = ? AND user_category ${ if (user_category_ == null) "IS" else "=" } ?
        """.trimMargin(), 5) {
          bindString(0, user_category)
          bindString(1, personal_finance_category_primary)
          bindString(2, personal_finance_category_detailed)
          bindString(3, user_id)
          bindString(4, user_category_)
        }.await()
    notifyQueries(-854_135_803) { emit ->
      emit("transactions")
    }
  }

  public suspend fun recategorizeTransactionViaAI(
    personal_finance_category_primary: String?,
    personal_finance_category_detailed: String?,
    ai_category: String?,
    transaction_id: String,
    user_id: String,
  ) {
    driver.execute(105_754_046, """
        |UPDATE transactions
        |SET user_category = NULL,
        |    personal_finance_category_primary = ?,
        |    personal_finance_category_detailed = ?,
        |    ai_category = ?,
        |    category_source = 'ai',
        |    updated_at = CURRENT_TIMESTAMP
        |WHERE transaction_id = ? AND user_id = ?
        """.trimMargin(), 5) {
          bindString(0, personal_finance_category_primary)
          bindString(1, personal_finance_category_detailed)
          bindString(2, ai_category)
          bindString(3, transaction_id)
          bindString(4, user_id)
        }.await()
    notifyQueries(105_754_046) { emit ->
      emit("transactions")
    }
  }

  public suspend fun upsertSubscriptionMerchant(
    user_id: String,
    merchant_key: String,
    typical_amount: Double?,
    status: String,
    confirmed_at: String?,
  ) {
    driver.execute(283_568_750, """
        |INSERT INTO subscription_merchants (user_id, merchant_key, typical_amount, status, confirmed_at)
        |VALUES (?, ?, ?, ?, ?)
        |ON CONFLICT(user_id, merchant_key)
        |DO UPDATE SET typical_amount = ?, status = ?, confirmed_at = ?
        """.trimMargin(), 8) {
          bindString(0, user_id)
          bindString(1, merchant_key)
          bindDouble(2, typical_amount)
          bindString(3, status)
          bindString(4, confirmed_at)
          bindDouble(5, typical_amount)
          bindString(6, status)
          bindString(7, confirmed_at)
        }.await()
    notifyQueries(283_568_750) { emit ->
      emit("subscription_merchants")
    }
  }

  public suspend fun updateSubscriptionMerchantStatus(
    status: String,
    user_id: String,
    merchant_key: String,
  ) {
    driver.execute(-466_350_310, """
        |UPDATE subscription_merchants
        |SET status = ?, confirmed_at = CASE WHEN ? = 'confirmed' THEN CURRENT_TIMESTAMP ELSE confirmed_at END
        |WHERE user_id = ? AND merchant_key = ?
        """.trimMargin(), 4) {
          bindString(0, status)
          bindString(1, status)
          bindString(2, user_id)
          bindString(3, merchant_key)
        }.await()
    notifyQueries(-466_350_310) { emit ->
      emit("subscription_merchants")
    }
  }

  public suspend fun insertTemplate(
    user_id: String,
    institution_signature: String,
    institution_alias: String?,
    file_type: String,
    column_mapping: String?,
    date_format: String?,
    delimiter: String?,
    template_data: String?,
  ) {
    driver.execute(-1_778_732_563, """
        |INSERT INTO statement_templates (user_id, institution_signature, institution_alias, file_type, column_mapping, date_format, delimiter, template_data)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 8) {
          bindString(0, user_id)
          bindString(1, institution_signature)
          bindString(2, institution_alias)
          bindString(3, file_type)
          bindString(4, column_mapping)
          bindString(5, date_format)
          bindString(6, delimiter)
          bindString(7, template_data)
        }.await()
    notifyQueries(-1_778_732_563) { emit ->
      emit("statement_templates")
    }
  }

  public suspend fun updateTemplateAlias(institution_alias: String?, id: Long) {
    driver.execute(484_181_075,
        """UPDATE statement_templates SET institution_alias = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?""",
        2) {
          bindString(0, institution_alias)
          bindLong(1, id)
        }.await()
    notifyQueries(484_181_075) { emit ->
      emit("statement_templates")
    }
  }

  public suspend fun invalidateTemplate(id: Long) {
    driver.execute(145_432_943,
        """UPDATE statement_templates SET is_valid = 0, updated_at = CURRENT_TIMESTAMP WHERE id = ?""",
        1) {
          bindLong(0, id)
        }.await()
    notifyQueries(145_432_943) { emit ->
      emit("statement_templates")
    }
  }

  public suspend fun insertManualAccount(
    account_id: String,
    user_id: String,
    institution_name: String,
    account_name: String,
    account_type: String,
    balance: Double,
    template_id: Long?,
  ) {
    driver.execute(-91_448_844, """
        |INSERT INTO manual_accounts (account_id, user_id, institution_name, account_name, account_type, balance, template_id)
        |VALUES (?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 7) {
          bindString(0, account_id)
          bindString(1, user_id)
          bindString(2, institution_name)
          bindString(3, account_name)
          bindString(4, account_type)
          bindDouble(5, balance)
          bindLong(6, template_id)
        }.await()
    notifyQueries(-91_448_844) { emit ->
      emit("manual_accounts")
    }
  }

  public suspend fun updateManualAccountBalance(balance: Double, account_id: String) {
    driver.execute(-1_221_795_752,
        """UPDATE manual_accounts SET balance = ? WHERE account_id = ?""", 2) {
          bindDouble(0, balance)
          bindString(1, account_id)
        }.await()
    notifyQueries(-1_221_795_752) { emit ->
      emit("manual_accounts")
    }
  }

  public suspend fun deleteManualAccount(account_id: String, user_id: String) {
    driver.execute(1_344_974_914,
        """DELETE FROM manual_accounts WHERE account_id = ? AND user_id = ?""", 2) {
          bindString(0, account_id)
          bindString(1, user_id)
        }.await()
    notifyQueries(1_344_974_914) { emit ->
      emit("manual_accounts")
    }
  }

  public suspend fun deleteTransactionsForAccount(account_id: String, user_id: String) {
    driver.execute(-499_396_098,
        """DELETE FROM transactions WHERE account_id = ? AND user_id = ?""", 2) {
          bindString(0, account_id)
          bindString(1, user_id)
        }.await()
    notifyQueries(-499_396_098) { emit ->
      emit("transaction_embeddings")
      emit("transaction_goal_assignments")
      emit("transactions")
      emit("user_corrections")
    }
  }

  public suspend fun deleteTransactionsByAccountId(account_id: String) {
    driver.execute(472_144_887, """DELETE FROM transactions WHERE account_id = ?""", 1) {
          bindString(0, account_id)
        }.await()
    notifyQueries(472_144_887) { emit ->
      emit("transaction_embeddings")
      emit("transaction_goal_assignments")
      emit("transactions")
      emit("user_corrections")
    }
  }

  public suspend fun insertNotificationToken(
    user_id: String,
    token: String,
    transaction_ids: String,
    phone_number: String?,
    created_at: String,
    expires_at: String,
  ) {
    driver.execute(318_810_043, """
        |INSERT INTO notification_tokens (user_id, token, transaction_ids, phone_number, created_at, expires_at)
        |VALUES (?, ?, ?, ?, ?, ?)
        """.trimMargin(), 6) {
          bindString(0, user_id)
          bindString(1, token)
          bindString(2, transaction_ids)
          bindString(3, phone_number)
          bindString(4, created_at)
          bindString(5, expires_at)
        }.await()
    notifyQueries(318_810_043) { emit ->
      emit("notification_tokens")
    }
  }

  public suspend fun markNotificationTokenActed(
    action: String?,
    acted_at: String?,
    token: String,
  ) {
    driver.execute(-339_891_894,
        """UPDATE notification_tokens SET action = ?, acted_at = ? WHERE token = ?""", 3) {
          bindString(0, action)
          bindString(1, acted_at)
          bindString(2, token)
        }.await()
    notifyQueries(-339_891_894) { emit ->
      emit("notification_tokens")
    }
  }

  public suspend fun stampTransactionsNotified(
    notified_at: String?,
    transaction_id: String,
    user_id: String,
  ) {
    driver.execute(1_152_814_742,
        """UPDATE transactions SET notified_at = ? WHERE transaction_id = ? AND user_id = ?""", 3) {
          bindString(0, notified_at)
          bindString(1, transaction_id)
          bindString(2, user_id)
        }.await()
    notifyQueries(1_152_814_742) { emit ->
      emit("transactions")
    }
  }

  public suspend fun clearNotifiedForRecent(userId: String, count: Long) {
    driver.execute(-652_242_791, """
        |UPDATE transactions
        |SET notified_at = NULL, is_verified = 0
        |WHERE transaction_id IN (
        |    SELECT transaction_id FROM transactions
        |    WHERE user_id = ? AND pending = 0
        |    ORDER BY date DESC, created_at DESC
        |    LIMIT ?
        |)
        |AND user_id = ?
        """.trimMargin(), 3) {
          bindString(0, userId)
          bindLong(1, count)
          bindString(2, userId)
        }.await()
    notifyQueries(-652_242_791) { emit ->
      emit("transactions")
    }
  }

  public suspend fun updateLastActivity(last_activity_at: String?, user_id: String) {
    driver.execute(1_217_971_656, """UPDATE users SET last_activity_at = ? WHERE user_id = ?""", 2)
        {
          bindString(0, last_activity_at)
          bindString(1, user_id)
        }.await()
    notifyQueries(1_217_971_656) { emit ->
      emit("users")
    }
  }

  public suspend fun markTransactionsVerified(
    verified_at: String?,
    transaction_id: String,
    user_id: String,
  ) {
    driver.execute(1_115_883_716,
        """UPDATE transactions SET is_verified = 1, verified_at = ? WHERE transaction_id = ? AND user_id = ?""",
        3) {
          bindString(0, verified_at)
          bindString(1, transaction_id)
          bindString(2, user_id)
        }.await()
    notifyQueries(1_115_883_716) { emit ->
      emit("transactions")
    }
  }

  public suspend fun insertContext(
    id: String,
    user_id: String,
    name: String,
    color: String,
    created_at: String,
    updated_at: String,
  ) {
    driver.execute(-864_332_324, """
        |INSERT INTO contexts (id, user_id, name, color, created_at, updated_at)
        |VALUES (?, ?, ?, ?, ?, ?)
        """.trimMargin(), 6) {
          bindString(0, id)
          bindString(1, user_id)
          bindString(2, name)
          bindString(3, color)
          bindString(4, created_at)
          bindString(5, updated_at)
        }.await()
    notifyQueries(-864_332_324) { emit ->
      emit("contexts")
    }
  }

  public suspend fun updateContext(
    name: String,
    color: String,
    updated_at: String,
    id: String,
    user_id: String,
  ) {
    driver.execute(-1_848_775_220,
        """UPDATE contexts SET name = ?, color = ?, updated_at = ? WHERE id = ? AND user_id = ?""",
        5) {
          bindString(0, name)
          bindString(1, color)
          bindString(2, updated_at)
          bindString(3, id)
          bindString(4, user_id)
        }.await()
    notifyQueries(-1_848_775_220) { emit ->
      emit("contexts")
    }
  }

  public suspend fun deleteContext(id: String, user_id: String) {
    driver.execute(-557_597_014, """DELETE FROM contexts WHERE id = ? AND user_id = ?""", 2) {
          bindString(0, id)
          bindString(1, user_id)
        }.await()
    notifyQueries(-557_597_014) { emit ->
      emit("contexts")
      emit("transaction_contexts")
    }
  }

  public suspend fun insertTransactionContext(
    transaction_id: String,
    context_id: String,
    created_at: String,
  ) {
    driver.execute(-855_271_772, """
        |INSERT OR IGNORE INTO transaction_contexts (transaction_id, context_id, created_at)
        |VALUES (?, ?, ?)
        """.trimMargin(), 3) {
          bindString(0, transaction_id)
          bindString(1, context_id)
          bindString(2, created_at)
        }.await()
    notifyQueries(-855_271_772) { emit ->
      emit("transaction_contexts")
    }
  }

  public suspend fun deleteTransactionContext(transaction_id: String, context_id: String) {
    driver.execute(1_507_803_414,
        """DELETE FROM transaction_contexts WHERE transaction_id = ? AND context_id = ?""", 2) {
          bindString(0, transaction_id)
          bindString(1, context_id)
        }.await()
    notifyQueries(1_507_803_414) { emit ->
      emit("transaction_contexts")
    }
  }

  public suspend fun deleteAllContextsForTransaction(transaction_id: String) {
    driver.execute(1_802_357_589, """DELETE FROM transaction_contexts WHERE transaction_id = ?""",
        1) {
          bindString(0, transaction_id)
        }.await()
    notifyQueries(1_802_357_589) { emit ->
      emit("transaction_contexts")
    }
  }

  public suspend fun deleteAllTransactionContextsForContext(context_id: String) {
    driver.execute(296_168_748, """DELETE FROM transaction_contexts WHERE context_id = ?""", 1) {
          bindString(0, context_id)
        }.await()
    notifyQueries(296_168_748) { emit ->
      emit("transaction_contexts")
    }
  }

  public suspend fun transferTransactionContexts(new_id: String, old_id: String) {
    driver.execute(1_465_292_381, """
        |INSERT OR IGNORE INTO transaction_contexts (transaction_id, context_id, created_at)
        |SELECT ?, context_id, created_at FROM transaction_contexts WHERE transaction_id = ?
        """.trimMargin(), 2) {
          bindString(0, new_id)
          bindString(1, old_id)
        }.await()
    notifyQueries(1_465_292_381) { emit ->
      emit("transaction_contexts")
    }
  }

  public suspend fun insertWiz3RCardGeneration(
    user_id: String,
    card_id: String,
    context_json: String,
    response_json: String,
  ) {
    driver.execute(1_528_673_596, """
        |INSERT INTO wiz3r_card_generations(user_id, card_id, generated_at, context_json, response_json)
        |VALUES (?, ?, CURRENT_TIMESTAMP, ?, ?)
        """.trimMargin(), 4) {
          bindString(0, user_id)
          bindString(1, card_id)
          bindString(2, context_json)
          bindString(3, response_json)
        }.await()
    notifyQueries(1_528_673_596) { emit ->
      emit("wiz3r_card_generations")
    }
  }

  public suspend fun invalidateWiz3RCardGenerationsForUser(user_id: String) {
    driver.execute(1_896_408_763, """DELETE FROM wiz3r_card_generations WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(1_896_408_763) { emit ->
      emit("wiz3r_card_generations")
    }
  }

  public suspend fun insertWiz3RChatThreadIfAbsent(
    thread_id: String,
    user_id: String,
    card_id: String,
  ) {
    driver.execute(-599_986_356, """
        |INSERT OR IGNORE INTO wiz3r_chat_threads(thread_id, user_id, card_id)
        |VALUES (?, ?, ?)
        """.trimMargin(), 3) {
          bindString(0, thread_id)
          bindString(1, user_id)
          bindString(2, card_id)
        }.await()
    notifyQueries(-599_986_356) { emit ->
      emit("wiz3r_chat_threads")
    }
  }

  public suspend fun touchWiz3RChatThread(thread_id: String) {
    driver.execute(1_418_029_604, """
        |UPDATE wiz3r_chat_threads
        |SET last_message_at = CURRENT_TIMESTAMP
        |WHERE thread_id = ?
        """.trimMargin(), 1) {
          bindString(0, thread_id)
        }.await()
    notifyQueries(1_418_029_604) { emit ->
      emit("wiz3r_chat_threads")
    }
  }

  public suspend fun insertWiz3RChatMessage(
    thread_id: String,
    user_id: String,
    role: String,
    content: String,
    chart_json: String?,
  ) {
    driver.execute(-300_708_581, """
        |INSERT INTO wiz3r_chat_messages(thread_id, user_id, role, content, chart_json)
        |VALUES (?, ?, ?, ?, ?)
        """.trimMargin(), 5) {
          bindString(0, thread_id)
          bindString(1, user_id)
          bindString(2, role)
          bindString(3, content)
          bindString(4, chart_json)
        }.await()
    notifyQueries(-300_708_581) { emit ->
      emit("wiz3r_chat_messages")
    }
  }

  public suspend fun deleteWiz3RChatThread(thread_id: String) {
    driver.execute(-166_049_468, """DELETE FROM wiz3r_chat_threads WHERE thread_id = ?""", 1) {
          bindString(0, thread_id)
        }.await()
    notifyQueries(-166_049_468) { emit ->
      emit("wiz3r_chat_messages")
      emit("wiz3r_chat_threads")
    }
  }

  public suspend fun deleteUserData_wiz3r_chat_threads(user_id: String) {
    driver.execute(166_576_735, """DELETE FROM wiz3r_chat_threads WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(166_576_735) { emit ->
      emit("wiz3r_chat_messages")
      emit("wiz3r_chat_threads")
    }
  }

  public suspend fun deleteUserData_wiz3r_chat_messages(user_id: String) {
    driver.execute(-1_066_809_002, """DELETE FROM wiz3r_chat_messages WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(-1_066_809_002) { emit ->
      emit("wiz3r_chat_messages")
    }
  }

  public suspend fun upsertBudgetClassification(
    user_id: String,
    category_code: String,
    bucket: String,
  ) {
    driver.execute(-1_347_558_668, """
        |INSERT INTO budget_category_classifications(user_id, category_code, bucket, updated_at)
        |VALUES (?, ?, ?, CURRENT_TIMESTAMP)
        |ON CONFLICT(user_id, category_code) DO UPDATE SET
        |    bucket = excluded.bucket,
        |    updated_at = CURRENT_TIMESTAMP
        """.trimMargin(), 3) {
          bindString(0, user_id)
          bindString(1, category_code)
          bindString(2, bucket)
        }.await()
    notifyQueries(-1_347_558_668) { emit ->
      emit("budget_category_classifications")
    }
  }

  public suspend fun deleteBudgetClassification(user_id: String, category_code: String) {
    driver.execute(1_312_176_496, """
        |DELETE FROM budget_category_classifications
        |WHERE user_id = ? AND category_code = ?
        """.trimMargin(), 2) {
          bindString(0, user_id)
          bindString(1, category_code)
        }.await()
    notifyQueries(1_312_176_496) { emit ->
      emit("budget_category_classifications")
    }
  }

  public suspend fun deleteUserData_budget_category_classifications(user_id: String) {
    driver.execute(-149_086_207,
        """DELETE FROM budget_category_classifications WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(-149_086_207) { emit ->
      emit("budget_category_classifications")
    }
  }

  public suspend fun upsertBudgetHealthSnapshot(
    user_id: String,
    year_month: String,
    needs_pct: Double,
    wants_pct: Double,
    savings_pct: Double,
    health_label: String,
  ) {
    driver.execute(828_624_718, """
        |INSERT INTO budget_health_snapshots(user_id, year_month, needs_pct, wants_pct, savings_pct, health_label)
        |VALUES (?, ?, ?, ?, ?, ?)
        |ON CONFLICT(user_id, year_month) DO UPDATE SET
        |    needs_pct = excluded.needs_pct,
        |    wants_pct = excluded.wants_pct,
        |    savings_pct = excluded.savings_pct,
        |    health_label = excluded.health_label
        """.trimMargin(), 6) {
          bindString(0, user_id)
          bindString(1, year_month)
          bindDouble(2, needs_pct)
          bindDouble(3, wants_pct)
          bindDouble(4, savings_pct)
          bindString(5, health_label)
        }.await()
    notifyQueries(828_624_718) { emit ->
      emit("budget_health_snapshots")
    }
  }

  public suspend fun deleteUserData_budget_health_snapshots(user_id: String) {
    driver.execute(-803_479_039, """DELETE FROM budget_health_snapshots WHERE user_id = ?""", 1) {
          bindString(0, user_id)
        }.await()
    notifyQueries(-803_479_039) { emit ->
      emit("budget_health_snapshots")
    }
  }

  private inner class SelectUserByUsernameQuery<out T : Any>(
    public val username: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("users", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("users", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_020_592_978,
        """SELECT users.id, users.user_id, users.username, users.password_hash, users.email, users.created_at, users.updated_at, users.last_login_at, users.mfa_secret, users.mfa_enabled, users.backup_codes, users.email_verified, users.verification_token, users.verification_token_expires_at, users.password_reset_token_hash, users.password_reset_expires_at, users.stripe_customer_id, users.subscription_status, users.subscription_id, users.trial_ends_at, users.subscription_tier, users.last_activity_at, users.phone_number, users.wiz3r_ai_enabled, users.timezone FROM users WHERE username = ?""",
        mapper, 1) {
      bindString(0, username)
    }

    override fun toString(): String = "schema.sq:selectUserByUsername"
  }

  private inner class SelectUserByIdQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("users", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("users", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_095_023_885,
        """SELECT users.id, users.user_id, users.username, users.password_hash, users.email, users.created_at, users.updated_at, users.last_login_at, users.mfa_secret, users.mfa_enabled, users.backup_codes, users.email_verified, users.verification_token, users.verification_token_expires_at, users.password_reset_token_hash, users.password_reset_expires_at, users.stripe_customer_id, users.subscription_status, users.subscription_id, users.trial_ends_at, users.subscription_tier, users.last_activity_at, users.phone_number, users.wiz3r_ai_enabled, users.timezone FROM users WHERE user_id = ?""",
        mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectUserById"
  }

  private inner class GetMfaStatusQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("users", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("users", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-135_566_498,
        """SELECT mfa_enabled, mfa_secret FROM users WHERE user_id = ?""", mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getMfaStatus"
  }

  private inner class GetBackupCodesQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("users", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("users", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_326_857_752, """SELECT backup_codes FROM users WHERE user_id = ?""",
        mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getBackupCodes"
  }

  private inner class GetSubscriptionStatusQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("users", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("users", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_343_227_019,
        """SELECT subscription_status, subscription_id, stripe_customer_id, trial_ends_at, subscription_tier FROM users WHERE user_id = ?""",
        mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getSubscriptionStatus"
  }

  private inner class GetUserByStripeCustomerIdQuery<out T : Any>(
    public val stripe_customer_id: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("users", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("users", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null,
        """SELECT users.id, users.user_id, users.username, users.password_hash, users.email, users.created_at, users.updated_at, users.last_login_at, users.mfa_secret, users.mfa_enabled, users.backup_codes, users.email_verified, users.verification_token, users.verification_token_expires_at, users.password_reset_token_hash, users.password_reset_expires_at, users.stripe_customer_id, users.subscription_status, users.subscription_id, users.trial_ends_at, users.subscription_tier, users.last_activity_at, users.phone_number, users.wiz3r_ai_enabled, users.timezone FROM users WHERE stripe_customer_id ${ if (stripe_customer_id == null) "IS" else "=" } ?""",
        mapper, 1) {
      bindString(0, stripe_customer_id)
    }

    override fun toString(): String = "schema.sq:getUserByStripeCustomerId"
  }

  private inner class GetSubscriptionTierQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("users", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("users", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_249_978_181,
        """SELECT subscription_tier FROM users WHERE user_id = ?""", mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getSubscriptionTier"
  }

  private inner class GetAiEnabledStatusQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("users", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("users", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(763_376_559, """SELECT wiz3r_ai_enabled FROM users WHERE user_id = ?""",
        mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getAiEnabledStatus"
  }

  private inner class GetUserTimezoneQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("users", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("users", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-572_876_032, """SELECT timezone FROM users WHERE user_id = ?""",
        mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getUserTimezone"
  }

  private inner class SelectUserByEmailQuery<out T : Any>(
    public val email: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("users", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("users", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null,
        """SELECT users.id, users.user_id, users.username, users.password_hash, users.email, users.created_at, users.updated_at, users.last_login_at, users.mfa_secret, users.mfa_enabled, users.backup_codes, users.email_verified, users.verification_token, users.verification_token_expires_at, users.password_reset_token_hash, users.password_reset_expires_at, users.stripe_customer_id, users.subscription_status, users.subscription_id, users.trial_ends_at, users.subscription_tier, users.last_activity_at, users.phone_number, users.wiz3r_ai_enabled, users.timezone FROM users WHERE email ${ if (email == null) "IS" else "=" } ?""",
        mapper, 1) {
      bindString(0, email)
    }

    override fun toString(): String = "schema.sq:selectUserByEmail"
  }

  private inner class SelectUserByVerificationTokenQuery<out T : Any>(
    public val verification_token: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("users", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("users", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null,
        """SELECT users.id, users.user_id, users.username, users.password_hash, users.email, users.created_at, users.updated_at, users.last_login_at, users.mfa_secret, users.mfa_enabled, users.backup_codes, users.email_verified, users.verification_token, users.verification_token_expires_at, users.password_reset_token_hash, users.password_reset_expires_at, users.stripe_customer_id, users.subscription_status, users.subscription_id, users.trial_ends_at, users.subscription_tier, users.last_activity_at, users.phone_number, users.wiz3r_ai_enabled, users.timezone FROM users WHERE verification_token ${ if (verification_token == null) "IS" else "=" } ?""",
        mapper, 1) {
      bindString(0, verification_token)
    }

    override fun toString(): String = "schema.sq:selectUserByVerificationToken"
  }

  private inner class SelectUserByPasswordResetTokenHashQuery<out T : Any>(
    public val password_reset_token_hash: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("users", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("users", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null,
        """SELECT users.id, users.user_id, users.username, users.password_hash, users.email, users.created_at, users.updated_at, users.last_login_at, users.mfa_secret, users.mfa_enabled, users.backup_codes, users.email_verified, users.verification_token, users.verification_token_expires_at, users.password_reset_token_hash, users.password_reset_expires_at, users.stripe_customer_id, users.subscription_status, users.subscription_id, users.trial_ends_at, users.subscription_tier, users.last_activity_at, users.phone_number, users.wiz3r_ai_enabled, users.timezone FROM users WHERE password_reset_token_hash ${ if (password_reset_token_hash == null) "IS" else "=" } ? AND password_reset_expires_at > CURRENT_TIMESTAMP""",
        mapper, 1) {
      bindString(0, password_reset_token_hash)
    }

    override fun toString(): String = "schema.sq:selectUserByPasswordResetTokenHash"
  }

  private inner class SelectTransactionsByUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_747_010_477, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions 
    |WHERE user_id = ? 
    |ORDER BY date DESC, datetime DESC
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectTransactionsByUser"
  }

  private inner class SelectTransactionsByAccountQuery<out T : Any>(
    public val account_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(2_040_303_371, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions 
    |WHERE account_id = ? 
    |ORDER BY date DESC, datetime DESC
    """.trimMargin(), mapper, 1) {
      bindString(0, account_id)
    }

    override fun toString(): String = "schema.sq:selectTransactionsByAccount"
  }

  private inner class SelectTransactionsByDateRangeQuery<out T : Any>(
    public val user_id: String,
    public val date: String,
    public val date_: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-502_908_019, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions
    |WHERE user_id = ? AND (date BETWEEN ? AND ? OR parent_transaction_id IS NOT NULL)
    |ORDER BY date DESC, datetime DESC
    """.trimMargin(), mapper, 3) {
      bindString(0, user_id)
      bindString(1, date)
      bindString(2, date_)
    }

    override fun toString(): String = "schema.sq:selectTransactionsByDateRange"
  }

  private inner class SelectPendingTransactionsQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_871_156_714, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions 
    |WHERE user_id = ? AND pending = 1 
    |ORDER BY date DESC
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectPendingTransactions"
  }

  private inner class SelectTransactionsByCategoryQuery<out T : Any>(
    public val user_id: String,
    public val personal_finance_category_primary: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions 
    |WHERE user_id = ? AND personal_finance_category_primary ${ if (personal_finance_category_primary == null) "IS" else "=" } ? 
    |ORDER BY date DESC
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, personal_finance_category_primary)
    }

    override fun toString(): String = "schema.sq:selectTransactionsByCategory"
  }

  private inner class SelectTransactionByIdQuery<out T : Any>(
    public val transaction_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(2_023_229_018,
        """SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions WHERE transaction_id = ?""",
        mapper, 1) {
      bindString(0, transaction_id)
    }

    override fun toString(): String = "schema.sq:selectTransactionById"
  }

  private inner class SelectUserOverridesByUserIdQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_650_748_227, """
    |SELECT transaction_id, user_category, category_source, user_notes, tags
    |FROM transactions
    |WHERE user_id = ? AND (user_category IS NOT NULL OR user_notes IS NOT NULL)
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectUserOverridesByUserId"
  }

  private inner class SelectVerifiedTransactionsByUserIdQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_845_304_976, """
    |SELECT transaction_id, is_verified, verified_at
    |FROM transactions
    |WHERE user_id = ? AND is_verified = 1
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectVerifiedTransactionsByUserId"
  }

  private inner class SelectTransactionsWithEmbeddingsQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-554_094_259, """
    |SELECT transaction_id, name, merchant_name, personal_finance_category_primary, embedding
    |FROM transactions 
    |WHERE user_id = ? AND embedding IS NOT NULL
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectTransactionsWithEmbeddings"
  }

  private inner class PreviewCategoryPropagationQuery<out T : Any>(
    public val user_id: String,
    public val transaction_id: String,
    public val merchant_name: String?,
    public val name: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT transaction_id, name, merchant_name, amount, date, personal_finance_category_detailed
    |FROM transactions
    |WHERE user_id = ?
    |    AND transaction_id != ?
    |    AND (merchant_name ${ if (merchant_name == null) "IS" else "=" } ? OR (merchant_name IS NULL AND name = ?))
    |ORDER BY date DESC, datetime DESC
    """.trimMargin(), mapper, 4) {
      bindString(0, user_id)
      bindString(1, transaction_id)
      bindString(2, merchant_name)
      bindString(3, name)
    }

    override fun toString(): String = "schema.sq:previewCategoryPropagation"
  }

  private inner class SelectPendingHoldCandidatesQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-810_680_848, """
    |SELECT transaction_id, account_id, amount, name, date, is_hidden, pending
    |FROM transactions
    |WHERE user_id = ? AND pending = 1
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectPendingHoldCandidates"
  }

  private inner class SelectHiddenHoldLegsQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-976_958_076, """
    |SELECT transaction_id, account_id, amount, name, date, is_hidden, pending
    |FROM transactions
    |WHERE user_id = ? AND is_hidden = 1
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectHiddenHoldLegs"
  }

  private inner class SelectCardPaymentCandidatesQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", "accounts", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", "accounts", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_914_213_328, """
    |SELECT t.transaction_id, t.account_id, t.amount, t.date, t.is_excluded, a.type AS account_type
    |FROM transactions t
    |JOIN accounts a ON a.account_id = t.account_id
    |WHERE t.user_id = ?
    |    AND (
    |        (t.personal_finance_category_primary = 'LOAN_PAYMENTS'
    |            AND t.personal_finance_category_detailed LIKE '%CREDIT_CARD_PAYMENT%')
    |        OR (a.type = 'credit' AND t.personal_finance_category_primary = 'TRANSFER_IN')
    |        OR (a.type IS NOT 'credit' AND t.personal_finance_category_primary = 'TRANSFER_OUT')
    |    )
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectCardPaymentCandidates"
  }

  private inner class SelectMerchantCategoryPreferencesQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(376_421_308, """
    |SELECT COALESCE(merchant_name, name) AS merchant_key,
    |       user_category
    |FROM transactions
    |WHERE user_id = ?
    |  AND (merchant_name IS NOT NULL OR name IS NOT NULL)
    |  AND user_category IS NOT NULL
    |  AND category_source = 'user'
    |ORDER BY date DESC
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectMerchantCategoryPreferences"
  }

  private inner class SelectUnverifiedByCategoryQuery<out T : Any>(
    public val user_id: String,
    public val personal_finance_category_detailed: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions
    |WHERE user_id = ?
    |    AND is_verified = 0
    |    AND personal_finance_category_detailed ${ if (personal_finance_category_detailed == null) "IS" else "=" } ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, personal_finance_category_detailed)
    }

    override fun toString(): String = "schema.sq:selectUnverifiedByCategory"
  }

  private inner class SelectUncategorizedTransactionsQuery<out T : Any>(
    public val user_id: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-460_123_343, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions 
    |WHERE user_id = ? AND ai_category IS NULL AND category_source = 'plaid'
    |ORDER BY date DESC
    |LIMIT ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindLong(1, value)
    }

    override fun toString(): String = "schema.sq:selectUncategorizedTransactions"
  }

  private inner class SelectLowConfidencePredictionsQuery<out T : Any>(
    public val user_id: String,
    public val confidence_score: Double?,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-950_486_372, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions 
    |WHERE user_id = ? AND confidence_score < ? AND ai_category IS NOT NULL
    |ORDER BY confidence_score ASC, date DESC
    |LIMIT ?
    """.trimMargin(), mapper, 3) {
      bindString(0, user_id)
      bindDouble(1, confidence_score)
      bindLong(2, value)
    }

    override fun toString(): String = "schema.sq:selectLowConfidencePredictions"
  }

  private inner class SelectTransactionsByAICategoryQuery<out T : Any>(
    public val user_id: String,
    public val ai_category: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions 
    |WHERE user_id = ? AND ai_category ${ if (ai_category == null) "IS" else "=" } ? 
    |ORDER BY date DESC
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, ai_category)
    }

    override fun toString(): String = "schema.sq:selectTransactionsByAICategory"
  }

  private inner class GetCorrectionStatsQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(871_078_981, """
    |SELECT 
    |    original_ai_category, 
    |    user_category,
    |    COUNT(*) AS correction_count,
    |    AVG(correction_confidence) AS avg_original_confidence
    |FROM transactions 
    |WHERE user_id = ? AND original_ai_category IS NOT NULL 
    |GROUP BY original_ai_category, user_category
    |ORDER BY COUNT(*) DESC
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getCorrectionStats"
  }

  private inner class FindMatchingPendingTransactionQuery<out T : Any>(
    public val accountId: String,
    public val amount: Double,
    public val postedDate: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_045_585_961, """
    |SELECT transaction_id FROM transactions
    |WHERE pending = 1
    |  AND account_id = ?
    |  AND amount = ?
    |  AND date BETWEEN date(?, '-3 days') AND date(?, '+1 day')
    |LIMIT 1
    """.trimMargin(), mapper, 4) {
      bindString(0, accountId)
      bindDouble(1, amount)
      bindString(2, postedDate)
      bindString(3, postedDate)
    }

    override fun toString(): String = "schema.sq:findMatchingPendingTransaction"
  }

  private inner class GetTransactionCountQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-251_184_915, """SELECT COUNT(*) FROM transactions WHERE user_id = ?""",
        mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getTransactionCount"
  }

  private inner class GetRecentTransactionsQuery<out T : Any>(
    public val user_id: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_631_480_940, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions 
    |WHERE user_id = ? AND date >= date('now', '-30 days')
    |ORDER BY date DESC, datetime DESC
    |LIMIT ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindLong(1, value)
    }

    override fun toString(): String = "schema.sq:getRecentTransactions"
  }

  private inner class SearchTransactionsQuery<out T : Any>(
    public val user_id: String,
    public val name: String,
    public val merchant_name: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_534_250_569, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions
    |WHERE user_id = ? AND (name LIKE ? OR merchant_name LIKE ?)
    |ORDER BY date DESC
    |LIMIT ?
    """.trimMargin(), mapper, 4) {
      bindString(0, user_id)
      bindString(1, name)
      bindString(2, merchant_name)
      bindLong(3, value)
    }

    override fun toString(): String = "schema.sq:searchTransactions"
  }

  private inner class SelectTransactionsByMerchantQuery<out T : Any>(
    public val user_id: String,
    public val merchant_name: String?,
    public val name: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions
    |WHERE user_id = ? AND (merchant_name ${ if (merchant_name == null) "IS" else "=" } ? OR (merchant_name IS NULL AND name = ?))
    """.trimMargin(), mapper, 3) {
      bindString(0, user_id)
      bindString(1, merchant_name)
      bindString(2, name)
    }

    override fun toString(): String = "schema.sq:selectTransactionsByMerchant"
  }

  private inner class GetChildTransactionsQuery<out T : Any>(
    public val parent_transaction_id: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions
    |WHERE parent_transaction_id ${ if (parent_transaction_id == null) "IS" else "=" } ?
    |ORDER BY split_order, date
    """.trimMargin(), mapper, 1) {
      bindString(0, parent_transaction_id)
    }

    override fun toString(): String = "schema.sq:getChildTransactions"
  }

  private inner class GetSplitChildrenQuery<out T : Any>(
    public val parent_transaction_id: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions
    |WHERE parent_transaction_id ${ if (parent_transaction_id == null) "IS" else "=" } ? AND child_type = 'SPLIT'
    |ORDER BY split_order
    """.trimMargin(), mapper, 1) {
      bindString(0, parent_transaction_id)
    }

    override fun toString(): String = "schema.sq:getSplitChildren"
  }

  private inner class GetDistributedChildrenQuery<out T : Any>(
    public val parent_transaction_id: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions
    |WHERE parent_transaction_id ${ if (parent_transaction_id == null) "IS" else "=" } ? AND child_type = 'DISTRIBUTED'
    |ORDER BY date
    """.trimMargin(), mapper, 1) {
      bindString(0, parent_transaction_id)
    }

    override fun toString(): String = "schema.sq:getDistributedChildren"
  }

  private inner class GetParentTransactionsQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-855_691_205, """
    |SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions
    |WHERE user_id = ? AND (is_split_parent = 1 OR is_distributed_parent = 1)
    |ORDER BY date DESC
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getParentTransactions"
  }

  private inner class SelectSplitParentsByUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-837_705_881, """
    |SELECT transaction_id FROM transactions
    |WHERE user_id = ? AND is_split_parent = 1
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectSplitParentsByUser"
  }

  private inner class SelectDistributedParentsByUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_100_130_814, """
    |SELECT transaction_id, distribution_start_month, distribution_end_month FROM transactions
    |WHERE user_id = ? AND is_distributed_parent = 1
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectDistributedParentsByUser"
  }

  private inner class SelectExcludedByUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_800_779_618, """
    |SELECT transaction_id FROM transactions
    |WHERE user_id = ? AND is_excluded = 1
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectExcludedByUser"
  }

  private inner class FindDuplicateTransactionQuery<out T : Any>(
    public val user_id: String,
    public val name: String,
    public val amount: Double,
    public val transaction_id: String,
    public val `value`: String,
    public val value_: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_173_719_878, """
    |SELECT transaction_id FROM transactions
    |WHERE user_id = ? AND name = ? AND amount = ?
    |  AND transaction_id != ?
    |  AND date BETWEEN date(?, '-1 day') AND date(?, '+1 day')
    |LIMIT 1
    """.trimMargin(), mapper, 6) {
      bindString(0, user_id)
      bindString(1, name)
      bindDouble(2, amount)
      bindString(3, transaction_id)
      bindString(4, value)
      bindString(5, value_)
    }

    override fun toString(): String = "schema.sq:findDuplicateTransaction"
  }

  private inner class HasChildTransactionsQuery<out T : Any>(
    public val parent_transaction_id: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null,
        """SELECT COUNT(*) FROM transactions WHERE parent_transaction_id ${ if (parent_transaction_id == null) "IS" else "=" } ?""",
        mapper, 1) {
      bindString(0, parent_transaction_id)
    }

    override fun toString(): String = "schema.sq:hasChildTransactions"
  }

  private inner class SelectAccountsByUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("accounts", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("accounts", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(203_985_022,
        """SELECT accounts.id, accounts.account_id, accounts.user_id, accounts.item_id, accounts.name, accounts.official_name, accounts.mask, accounts.type, accounts.subtype, accounts.balance_available, accounts.balance_current, accounts.balance_limit, accounts.iso_currency_code, accounts.unofficial_currency_code, accounts.balance_last_updated, accounts.verification_status, accounts.persistent_account_id, accounts.created_at, accounts.updated_at, accounts.last_synced_at, accounts.status, accounts.custom_name FROM accounts WHERE user_id = ? AND status = 'active' ORDER BY name""",
        mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectAccountsByUser"
  }

  private inner class SelectAllAccountsByUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("accounts", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("accounts", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_601_180_589,
        """SELECT accounts.id, accounts.account_id, accounts.user_id, accounts.item_id, accounts.name, accounts.official_name, accounts.mask, accounts.type, accounts.subtype, accounts.balance_available, accounts.balance_current, accounts.balance_limit, accounts.iso_currency_code, accounts.unofficial_currency_code, accounts.balance_last_updated, accounts.verification_status, accounts.persistent_account_id, accounts.created_at, accounts.updated_at, accounts.last_synced_at, accounts.status, accounts.custom_name FROM accounts WHERE user_id = ? ORDER BY name""",
        mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectAllAccountsByUser"
  }

  private inner class SelectAccountByIdQuery<out T : Any>(
    public val account_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("accounts", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("accounts", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-728_018_135,
        """SELECT accounts.id, accounts.account_id, accounts.user_id, accounts.item_id, accounts.name, accounts.official_name, accounts.mask, accounts.type, accounts.subtype, accounts.balance_available, accounts.balance_current, accounts.balance_limit, accounts.iso_currency_code, accounts.unofficial_currency_code, accounts.balance_last_updated, accounts.verification_status, accounts.persistent_account_id, accounts.created_at, accounts.updated_at, accounts.last_synced_at, accounts.status, accounts.custom_name FROM accounts WHERE account_id = ?""",
        mapper, 1) {
      bindString(0, account_id)
    }

    override fun toString(): String = "schema.sq:selectAccountById"
  }

  private inner class SelectCustomNamesForUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("accounts", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("accounts", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_237_620_499, """
    |SELECT account_id, custom_name FROM accounts
    |WHERE user_id = ? AND custom_name IS NOT NULL
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectCustomNamesForUser"
  }

  private inner class SelectUnlinkedAccountsForUserAndInstitutionQuery<out T : Any>(
    public val userId: String,
    public val institutionId: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("accounts", "plaid_tokens", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("accounts", "plaid_tokens", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT a.id, a.account_id, a.user_id, a.item_id, a.name, a.official_name, a.mask, a.type, a.subtype, a.balance_available, a.balance_current, a.balance_limit, a.iso_currency_code, a.unofficial_currency_code, a.balance_last_updated, a.verification_status, a.persistent_account_id, a.created_at, a.updated_at, a.last_synced_at, a.status, a.custom_name FROM accounts a
    |JOIN plaid_tokens t ON a.item_id = t.item_id
    |WHERE a.user_id = ?
    |  AND a.status = 'unlinked'
    |  AND t.institution_id ${ if (institutionId == null) "IS" else "=" } ?
    |ORDER BY a.last_synced_at DESC NULLS LAST
    """.trimMargin(), mapper, 2) {
      bindString(0, userId)
      bindString(1, institutionId)
    }

    override fun toString(): String = "schema.sq:selectUnlinkedAccountsForUserAndInstitution"
  }

  private inner class FindStaleDuplicateAccountMappingsQuery<out T : Any>(
    public val userId: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("accounts", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("accounts", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(930_374_093, """
    |SELECT stale.account_id AS old_account_id, survivor.account_id AS new_account_id
    |FROM accounts stale
    |JOIN accounts survivor ON stale.user_id = survivor.user_id
    |  AND stale.name = survivor.name
    |  AND stale.type = survivor.type
    |  AND COALESCE(stale.subtype, '') = COALESCE(survivor.subtype, '')
    |  AND COALESCE(stale.mask, '') = COALESCE(survivor.mask, '')
    |  AND stale.id < survivor.id
    |WHERE stale.user_id = ?
    |  AND stale.id NOT IN (
    |    SELECT MAX(id) FROM accounts
    |    WHERE user_id = ?
    |    GROUP BY name, COALESCE(mask, ''), type, COALESCE(subtype, '')
    |  )
    """.trimMargin(), mapper, 2) {
      bindString(0, userId)
      bindString(1, userId)
    }

    override fun toString(): String = "schema.sq:findStaleDuplicateAccountMappings"
  }

  private inner class SelectAccountsByItemIdQuery<out T : Any>(
    public val item_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("accounts", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("accounts", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_881_518_207,
        """SELECT accounts.id, accounts.account_id, accounts.user_id, accounts.item_id, accounts.name, accounts.official_name, accounts.mask, accounts.type, accounts.subtype, accounts.balance_available, accounts.balance_current, accounts.balance_limit, accounts.iso_currency_code, accounts.unofficial_currency_code, accounts.balance_last_updated, accounts.verification_status, accounts.persistent_account_id, accounts.created_at, accounts.updated_at, accounts.last_synced_at, accounts.status, accounts.custom_name FROM accounts WHERE item_id = ? ORDER BY name""",
        mapper, 1) {
      bindString(0, item_id)
    }

    override fun toString(): String = "schema.sq:selectAccountsByItemId"
  }

  private inner class SelectTokensByUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("plaid_tokens", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("plaid_tokens", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(38_918_322,
        """SELECT plaid_tokens.id, plaid_tokens.user_id, plaid_tokens.link_token, plaid_tokens.link_token_expiration, plaid_tokens.public_token, plaid_tokens.access_token, plaid_tokens.item_id, plaid_tokens.institution_id, plaid_tokens.institution_name, plaid_tokens.state, plaid_tokens.security_level, plaid_tokens.created_at, plaid_tokens.updated_at, plaid_tokens.last_used_at, plaid_tokens.error_code, plaid_tokens.error_message, plaid_tokens.webhook_url, plaid_tokens.metadata, plaid_tokens.plaid_removal_status, plaid_tokens.transactions_cursor, plaid_tokens.initial_sync_complete, plaid_tokens.historical_sync_complete, plaid_tokens.last_sync_at, plaid_tokens.last_relinked_at, plaid_tokens.last_transactions_refresh_at, plaid_tokens.days_requested FROM plaid_tokens WHERE user_id = ?""",
        mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectTokensByUser"
  }

  private inner class SelectTokenByItemIdQuery<out T : Any>(
    public val item_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("plaid_tokens", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("plaid_tokens", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_930_189_032,
        """SELECT plaid_tokens.id, plaid_tokens.user_id, plaid_tokens.link_token, plaid_tokens.link_token_expiration, plaid_tokens.public_token, plaid_tokens.access_token, plaid_tokens.item_id, plaid_tokens.institution_id, plaid_tokens.institution_name, plaid_tokens.state, plaid_tokens.security_level, plaid_tokens.created_at, plaid_tokens.updated_at, plaid_tokens.last_used_at, plaid_tokens.error_code, plaid_tokens.error_message, plaid_tokens.webhook_url, plaid_tokens.metadata, plaid_tokens.plaid_removal_status, plaid_tokens.transactions_cursor, plaid_tokens.initial_sync_complete, plaid_tokens.historical_sync_complete, plaid_tokens.last_sync_at, plaid_tokens.last_relinked_at, plaid_tokens.last_transactions_refresh_at, plaid_tokens.days_requested FROM plaid_tokens WHERE item_id = ?""",
        mapper, 1) {
      bindString(0, item_id)
    }

    override fun toString(): String = "schema.sq:selectTokenByItemId"
  }

  private inner class GetSyncStatusQuery<out T : Any>(
    public val item_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("plaid_tokens", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("plaid_tokens", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(500_501_385, """
    |SELECT transactions_cursor, initial_sync_complete, historical_sync_complete, last_sync_at, days_requested
    |FROM plaid_tokens
    |WHERE item_id = ?
    """.trimMargin(), mapper, 1) {
      bindString(0, item_id)
    }

    override fun toString(): String = "schema.sq:getSyncStatus"
  }

  private inner class SelectCorrectionsByUserQuery<out T : Any>(
    public val user_id: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("user_corrections", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("user_corrections", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_271_204_351, """
    |SELECT user_corrections.id, user_corrections.transaction_id, user_corrections.user_id, user_corrections.old_category, user_corrections.new_category, user_corrections.original_confidence, user_corrections.correction_reason, user_corrections.timestamp, user_corrections.ml_model_version, user_corrections.merchant_name, user_corrections.transaction_amount FROM user_corrections 
    |WHERE user_id = ? 
    |ORDER BY timestamp DESC
    |LIMIT ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindLong(1, value)
    }

    override fun toString(): String = "schema.sq:selectCorrectionsByUser"
  }

  private inner class SelectCorrectionsByTransactionQuery<out T : Any>(
    public val transaction_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("user_corrections", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("user_corrections", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_268_973_592, """
    |SELECT user_corrections.id, user_corrections.transaction_id, user_corrections.user_id, user_corrections.old_category, user_corrections.new_category, user_corrections.original_confidence, user_corrections.correction_reason, user_corrections.timestamp, user_corrections.ml_model_version, user_corrections.merchant_name, user_corrections.transaction_amount FROM user_corrections 
    |WHERE transaction_id = ?
    |ORDER BY timestamp DESC
    """.trimMargin(), mapper, 1) {
      bindString(0, transaction_id)
    }

    override fun toString(): String = "schema.sq:selectCorrectionsByTransaction"
  }

  private inner class GetCorrectionPatternsQuery<out T : Any>(
    public val user_id: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("user_corrections", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("user_corrections", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(49_005_949, """
    |SELECT 
    |    old_category,
    |    new_category,
    |    COUNT(*) AS pattern_count,
    |    AVG(original_confidence) AS avg_confidence,
    |    merchant_name
    |FROM user_corrections 
    |WHERE user_id = ?
    |GROUP BY old_category, new_category, merchant_name
    |ORDER BY pattern_count DESC
    |LIMIT ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindLong(1, value)
    }

    override fun toString(): String = "schema.sq:getCorrectionPatterns"
  }

  private inner class GetCorrectionsByCategoryQuery<out T : Any>(
    public val user_id: String,
    public val old_category: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("user_corrections", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("user_corrections", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-471_504_242, """
    |SELECT 
    |    old_category,
    |    new_category,
    |    merchant_name,
    |    transaction_amount,
    |    original_confidence,
    |    timestamp
    |FROM user_corrections 
    |WHERE user_id = ? AND old_category = ?
    |ORDER BY timestamp DESC
    |LIMIT ?
    """.trimMargin(), mapper, 3) {
      bindString(0, user_id)
      bindString(1, old_category)
      bindLong(2, value)
    }

    override fun toString(): String = "schema.sq:getCorrectionsByCategory"
  }

  private inner class GetRecentCorrectionsQuery<out T : Any>(
    public val user_id: String,
    public val timestamp: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("user_corrections", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("user_corrections", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(762_882_782, """
    |SELECT user_corrections.id, user_corrections.transaction_id, user_corrections.user_id, user_corrections.old_category, user_corrections.new_category, user_corrections.original_confidence, user_corrections.correction_reason, user_corrections.timestamp, user_corrections.ml_model_version, user_corrections.merchant_name, user_corrections.transaction_amount FROM user_corrections 
    |WHERE user_id = ? AND timestamp >= ?
    |ORDER BY timestamp DESC
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, timestamp)
    }

    override fun toString(): String = "schema.sq:getRecentCorrections"
  }

  private inner class GetCorrectionsByMerchantQuery<out T : Any>(
    public val user_id: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("user_corrections", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("user_corrections", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_027_311_784, """
    |SELECT 
    |    merchant_name,
    |    old_category,
    |    new_category,
    |    COUNT(*) AS correction_count,
    |    AVG(original_confidence) AS avg_original_confidence
    |FROM user_corrections 
    |WHERE user_id = ?
    |GROUP BY merchant_name, old_category, new_category
    |HAVING COUNT(*) >= ?
    |ORDER BY correction_count DESC
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindLong(1, value)
    }

    override fun toString(): String = "schema.sq:getCorrectionsByMerchant"
  }

  private inner class SelectEmbeddingByTransactionQuery<out T : Any>(
    public val transaction_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_embeddings", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_embeddings", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-236_200_410,
        """SELECT transaction_embeddings.id, transaction_embeddings.transaction_id, transaction_embeddings.user_id, transaction_embeddings.embedding_vector, transaction_embeddings.embedding_dimension, transaction_embeddings.source_text, transaction_embeddings.embedding_model, transaction_embeddings.embedding_version, transaction_embeddings.category_hint, transaction_embeddings.similarity_threshold, transaction_embeddings.created_at, transaction_embeddings.updated_at FROM transaction_embeddings WHERE transaction_id = ?""",
        mapper, 1) {
      bindString(0, transaction_id)
    }

    override fun toString(): String = "schema.sq:selectEmbeddingByTransaction"
  }

  private inner class SelectEmbeddingsByUserQuery<out T : Any>(
    public val user_id: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_embeddings", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_embeddings", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_743_936_148, """
    |SELECT transaction_embeddings.id, transaction_embeddings.transaction_id, transaction_embeddings.user_id, transaction_embeddings.embedding_vector, transaction_embeddings.embedding_dimension, transaction_embeddings.source_text, transaction_embeddings.embedding_model, transaction_embeddings.embedding_version, transaction_embeddings.category_hint, transaction_embeddings.similarity_threshold, transaction_embeddings.created_at, transaction_embeddings.updated_at FROM transaction_embeddings 
    |WHERE user_id = ? 
    |ORDER BY created_at DESC
    |LIMIT ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindLong(1, value)
    }

    override fun toString(): String = "schema.sq:selectEmbeddingsByUser"
  }

  private inner class GetEmbeddingsForSimilarityQuery<out T : Any>(
    public val user_id: String,
    public val embedding_model: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_embeddings", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_embeddings", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_116_894_804, """
    |SELECT 
    |    transaction_id, embedding_vector, category_hint, similarity_threshold
    |FROM transaction_embeddings 
    |WHERE user_id = ? AND embedding_model = ?
    |ORDER BY created_at DESC
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, embedding_model)
    }

    override fun toString(): String = "schema.sq:getEmbeddingsForSimilarity"
  }

  private inner class GetEmbeddingsByCategoryQuery<out T : Any>(
    public val user_id: String,
    public val category_hint: String?,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_embeddings", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_embeddings", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT 
    |    transaction_id, embedding_vector, source_text, category_hint
    |FROM transaction_embeddings 
    |WHERE user_id = ? AND category_hint ${ if (category_hint == null) "IS" else "=" } ?
    |ORDER BY created_at DESC
    |LIMIT ?
    """.trimMargin(), mapper, 3) {
      bindString(0, user_id)
      bindString(1, category_hint)
      bindLong(2, value)
    }

    override fun toString(): String = "schema.sq:getEmbeddingsByCategory"
  }

  private inner class GetUncategorizedEmbeddingsQuery<out T : Any>(
    public val user_id: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_embeddings", "transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_embeddings", "transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_643_082_930, """
    |SELECT 
    |    te.transaction_id, te.embedding_vector, te.source_text, te.similarity_threshold
    |FROM transaction_embeddings te
    |JOIN transactions t ON te.transaction_id = t.transaction_id
    |WHERE te.user_id = ? AND t.ai_category IS NULL
    |ORDER BY t.date DESC
    |LIMIT ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindLong(1, value)
    }

    override fun toString(): String = "schema.sq:getUncategorizedEmbeddings"
  }

  private inner class GetTopLevelCategoriesQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_categories", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_categories", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-895_507_513, """
    |SELECT transaction_categories.id, transaction_categories.user_id, transaction_categories.name, transaction_categories.parent_id, transaction_categories.color, transaction_categories.icon, transaction_categories.is_system, transaction_categories.created_at FROM transaction_categories 
    |WHERE user_id = ? AND parent_id IS NULL
    |ORDER BY is_system DESC, name ASC
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getTopLevelCategories"
  }

  private inner class GetSubCategoriesQuery<out T : Any>(
    public val user_id: String,
    public val parent_id: Long?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_categories", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_categories", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT transaction_categories.id, transaction_categories.user_id, transaction_categories.name, transaction_categories.parent_id, transaction_categories.color, transaction_categories.icon, transaction_categories.is_system, transaction_categories.created_at FROM transaction_categories 
    |WHERE user_id = ? AND parent_id ${ if (parent_id == null) "IS" else "=" } ?
    |ORDER BY name ASC
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindLong(1, parent_id)
    }

    override fun toString(): String = "schema.sq:getSubCategories"
  }

  private inner class GetCategoryByIdQuery<out T : Any>(
    public val id: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_categories", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_categories", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_597_973_492,
        """SELECT transaction_categories.id, transaction_categories.user_id, transaction_categories.name, transaction_categories.parent_id, transaction_categories.color, transaction_categories.icon, transaction_categories.is_system, transaction_categories.created_at FROM transaction_categories WHERE id = ?""",
        mapper, 1) {
      bindLong(0, id)
    }

    override fun toString(): String = "schema.sq:getCategoryById"
  }

  private inner class GetAllCategoriesQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_categories", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_categories", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_746_236_415, """
    |SELECT transaction_categories.id, transaction_categories.user_id, transaction_categories.name, transaction_categories.parent_id, transaction_categories.color, transaction_categories.icon, transaction_categories.is_system, transaction_categories.created_at FROM transaction_categories 
    |WHERE user_id = ? OR user_id = 'system'
    |ORDER BY is_system DESC, parent_id ASC, name ASC
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getAllCategories"
  }

  private inner class SearchCategoriesQuery<out T : Any>(
    public val user_id: String,
    public val name: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_categories", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_categories", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_972_215_550, """
    |SELECT transaction_categories.id, transaction_categories.user_id, transaction_categories.name, transaction_categories.parent_id, transaction_categories.color, transaction_categories.icon, transaction_categories.is_system, transaction_categories.created_at FROM transaction_categories 
    |WHERE (user_id = ? OR user_id = 'system') AND name LIKE ?
    |ORDER BY is_system DESC, name ASC
    |LIMIT ?
    """.trimMargin(), mapper, 3) {
      bindString(0, user_id)
      bindString(1, name)
      bindLong(2, value)
    }

    override fun toString(): String = "schema.sq:searchCategories"
  }

  private inner class GetCategoryByNameQuery<out T : Any>(
    public val user_id: String,
    public val name: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_categories", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_categories", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_945_915_708, """
    |SELECT transaction_categories.id, transaction_categories.user_id, transaction_categories.name, transaction_categories.parent_id, transaction_categories.color, transaction_categories.icon, transaction_categories.is_system, transaction_categories.created_at FROM transaction_categories 
    |WHERE (user_id = ? OR user_id = 'system') AND name = ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, name)
    }

    override fun toString(): String = "schema.sq:getCategoryByName"
  }

  private inner class SelectActiveGoalsByUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("savings_goals", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("savings_goals", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-887_678_522, """
    |SELECT savings_goals.id, savings_goals.user_id, savings_goals.name, savings_goals.description, savings_goals.target_amount, savings_goals.current_amount, savings_goals.deadline, savings_goals.category, savings_goals.priority, savings_goals.created_date, savings_goals.is_active, savings_goals.icon_emoji, savings_goals.created_at, savings_goals.updated_at, savings_goals.last_modified, savings_goals.account_type, savings_goals.debt_interest_rate, savings_goals.debt_remaining_balance, savings_goals.debt_remaining_months, savings_goals.debt_payment_frequency, savings_goals.debt_monthly_equivalent_payment FROM savings_goals
    |WHERE user_id = ? AND is_active = 1
    |ORDER BY priority DESC, deadline ASC
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectActiveGoalsByUser"
  }

  private inner class SelectAllGoalsByUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("savings_goals", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("savings_goals", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-267_715_465, """
    |SELECT savings_goals.id, savings_goals.user_id, savings_goals.name, savings_goals.description, savings_goals.target_amount, savings_goals.current_amount, savings_goals.deadline, savings_goals.category, savings_goals.priority, savings_goals.created_date, savings_goals.is_active, savings_goals.icon_emoji, savings_goals.created_at, savings_goals.updated_at, savings_goals.last_modified, savings_goals.account_type, savings_goals.debt_interest_rate, savings_goals.debt_remaining_balance, savings_goals.debt_remaining_months, savings_goals.debt_payment_frequency, savings_goals.debt_monthly_equivalent_payment FROM savings_goals
    |WHERE user_id = ?
    |ORDER BY is_active DESC, priority DESC, deadline ASC
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectAllGoalsByUser"
  }

  private inner class SelectGoalByIdQuery<out T : Any>(
    public val id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("savings_goals", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("savings_goals", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(900_330_043,
        """SELECT savings_goals.id, savings_goals.user_id, savings_goals.name, savings_goals.description, savings_goals.target_amount, savings_goals.current_amount, savings_goals.deadline, savings_goals.category, savings_goals.priority, savings_goals.created_date, savings_goals.is_active, savings_goals.icon_emoji, savings_goals.created_at, savings_goals.updated_at, savings_goals.last_modified, savings_goals.account_type, savings_goals.debt_interest_rate, savings_goals.debt_remaining_balance, savings_goals.debt_remaining_months, savings_goals.debt_payment_frequency, savings_goals.debt_monthly_equivalent_payment FROM savings_goals WHERE id = ?""",
        mapper, 1) {
      bindString(0, id)
    }

    override fun toString(): String = "schema.sq:selectGoalById"
  }

  private inner class SelectGoalsByCategoryQuery<out T : Any>(
    public val user_id: String,
    public val category: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("savings_goals", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("savings_goals", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-616_400_673, """
    |SELECT savings_goals.id, savings_goals.user_id, savings_goals.name, savings_goals.description, savings_goals.target_amount, savings_goals.current_amount, savings_goals.deadline, savings_goals.category, savings_goals.priority, savings_goals.created_date, savings_goals.is_active, savings_goals.icon_emoji, savings_goals.created_at, savings_goals.updated_at, savings_goals.last_modified, savings_goals.account_type, savings_goals.debt_interest_rate, savings_goals.debt_remaining_balance, savings_goals.debt_remaining_months, savings_goals.debt_payment_frequency, savings_goals.debt_monthly_equivalent_payment FROM savings_goals
    |WHERE user_id = ? AND category = ? AND is_active = 1
    |ORDER BY deadline ASC
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, category)
    }

    override fun toString(): String = "schema.sq:selectGoalsByCategory"
  }

  private inner class SelectGoalsNearingDeadlineQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("savings_goals", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("savings_goals", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(304_775_944, """
    |SELECT savings_goals.id, savings_goals.user_id, savings_goals.name, savings_goals.description, savings_goals.target_amount, savings_goals.current_amount, savings_goals.deadline, savings_goals.category, savings_goals.priority, savings_goals.created_date, savings_goals.is_active, savings_goals.icon_emoji, savings_goals.created_at, savings_goals.updated_at, savings_goals.last_modified, savings_goals.account_type, savings_goals.debt_interest_rate, savings_goals.debt_remaining_balance, savings_goals.debt_remaining_months, savings_goals.debt_payment_frequency, savings_goals.debt_monthly_equivalent_payment FROM savings_goals
    |WHERE user_id = ? AND is_active = 1
    |  AND date(deadline) BETWEEN date('now') AND date('now', '+30 days')
    |ORDER BY deadline ASC
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectGoalsNearingDeadline"
  }

  private inner class SelectRecommendationsByGoalQuery<out T : Any>(
    public val goal_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("savings_recommendations", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("savings_recommendations", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_445_574_770, """
    |SELECT savings_recommendations.id, savings_recommendations.goal_id, savings_recommendations.user_id, savings_recommendations.spending_category, savings_recommendations.current_monthly_spending, savings_recommendations.recommended_monthly_spending, savings_recommendations.suggested_reduction, savings_recommendations.rationale, savings_recommendations.potential_savings, savings_recommendations.confidence_score, savings_recommendations.priority, savings_recommendations.created_at, savings_recommendations.is_applied, savings_recommendations.applied_at FROM savings_recommendations
    |WHERE goal_id = ?
    |ORDER BY priority ASC, confidence_score DESC
    """.trimMargin(), mapper, 1) {
      bindString(0, goal_id)
    }

    override fun toString(): String = "schema.sq:selectRecommendationsByGoal"
  }

  private inner class SelectUnappliedRecommendationsQuery<out T : Any>(
    public val goal_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("savings_recommendations", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("savings_recommendations", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(606_925_868, """
    |SELECT savings_recommendations.id, savings_recommendations.goal_id, savings_recommendations.user_id, savings_recommendations.spending_category, savings_recommendations.current_monthly_spending, savings_recommendations.recommended_monthly_spending, savings_recommendations.suggested_reduction, savings_recommendations.rationale, savings_recommendations.potential_savings, savings_recommendations.confidence_score, savings_recommendations.priority, savings_recommendations.created_at, savings_recommendations.is_applied, savings_recommendations.applied_at FROM savings_recommendations
    |WHERE goal_id = ? AND is_applied = 0
    |ORDER BY priority ASC, potential_savings DESC
    """.trimMargin(), mapper, 1) {
      bindString(0, goal_id)
    }

    override fun toString(): String = "schema.sq:selectUnappliedRecommendations"
  }

  private inner class SelectProgressHistoryByGoalQuery<out T : Any>(
    public val goal_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("goal_progress_history", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("goal_progress_history", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_681_481_829, """
    |SELECT goal_progress_history.id, goal_progress_history.goal_id, goal_progress_history.user_id, goal_progress_history.contribution_amount, goal_progress_history.contribution_date, goal_progress_history.notes, goal_progress_history.created_at FROM goal_progress_history
    |WHERE goal_id = ?
    |ORDER BY contribution_date DESC
    """.trimMargin(), mapper, 1) {
      bindString(0, goal_id)
    }

    override fun toString(): String = "schema.sq:selectProgressHistoryByGoal"
  }

  private inner class GetTotalContributionsQuery<out T : Any>(
    public val goal_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("goal_progress_history", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("goal_progress_history", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_828_166_299, """
    |SELECT COALESCE(SUM(contribution_amount), 0) AS total
    |FROM goal_progress_history
    |WHERE goal_id = ?
    """.trimMargin(), mapper, 1) {
      bindString(0, goal_id)
    }

    override fun toString(): String = "schema.sq:getTotalContributions"
  }

  private inner class GetMonthlyContributionsQuery<out T : Any>(
    public val goal_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("goal_progress_history", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("goal_progress_history", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-2_128_159_374, """
    |SELECT
    |    strftime('%Y-%m', contribution_date) AS month,
    |    SUM(contribution_amount) AS total_contribution,
    |    COUNT(*) AS contribution_count
    |FROM goal_progress_history
    |WHERE goal_id = ?
    |GROUP BY month
    |ORDER BY month DESC
    """.trimMargin(), mapper, 1) {
      bindString(0, goal_id)
    }

    override fun toString(): String = "schema.sq:getMonthlyContributions"
  }

  private inner class SelectRulesByUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("goal_assignment_rules", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("goal_assignment_rules", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_476_304_931, """
    |SELECT goal_assignment_rules.id, goal_assignment_rules.user_id, goal_assignment_rules.goal_id, goal_assignment_rules.merchant_name, goal_assignment_rules.account_id, goal_assignment_rules.description_pattern, goal_assignment_rules.priority, goal_assignment_rules.is_default, goal_assignment_rules.rule_mode, goal_assignment_rules.created_at, goal_assignment_rules.updated_at FROM goal_assignment_rules
    |WHERE user_id = ?
    |ORDER BY priority DESC, created_at DESC
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectRulesByUser"
  }

  private inner class SelectRulesByGoalQuery<out T : Any>(
    public val goal_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("goal_assignment_rules", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("goal_assignment_rules", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_475_883_883, """
    |SELECT goal_assignment_rules.id, goal_assignment_rules.user_id, goal_assignment_rules.goal_id, goal_assignment_rules.merchant_name, goal_assignment_rules.account_id, goal_assignment_rules.description_pattern, goal_assignment_rules.priority, goal_assignment_rules.is_default, goal_assignment_rules.rule_mode, goal_assignment_rules.created_at, goal_assignment_rules.updated_at FROM goal_assignment_rules
    |WHERE goal_id = ?
    |ORDER BY priority DESC
    """.trimMargin(), mapper, 1) {
      bindString(0, goal_id)
    }

    override fun toString(): String = "schema.sq:selectRulesByGoal"
  }

  private inner class SelectRulesByMerchantQuery<out T : Any>(
    public val user_id: String,
    public val merchant_name: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("goal_assignment_rules", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("goal_assignment_rules", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT goal_assignment_rules.id, goal_assignment_rules.user_id, goal_assignment_rules.goal_id, goal_assignment_rules.merchant_name, goal_assignment_rules.account_id, goal_assignment_rules.description_pattern, goal_assignment_rules.priority, goal_assignment_rules.is_default, goal_assignment_rules.rule_mode, goal_assignment_rules.created_at, goal_assignment_rules.updated_at FROM goal_assignment_rules
    |WHERE user_id = ? AND merchant_name ${ if (merchant_name == null) "IS" else "=" } ?
    |ORDER BY priority DESC
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, merchant_name)
    }

    override fun toString(): String = "schema.sq:selectRulesByMerchant"
  }

  private inner class SelectRulesByAccountQuery<out T : Any>(
    public val user_id: String,
    public val account_id: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("goal_assignment_rules", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("goal_assignment_rules", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT goal_assignment_rules.id, goal_assignment_rules.user_id, goal_assignment_rules.goal_id, goal_assignment_rules.merchant_name, goal_assignment_rules.account_id, goal_assignment_rules.description_pattern, goal_assignment_rules.priority, goal_assignment_rules.is_default, goal_assignment_rules.rule_mode, goal_assignment_rules.created_at, goal_assignment_rules.updated_at FROM goal_assignment_rules
    |WHERE user_id = ? AND account_id ${ if (account_id == null) "IS" else "=" } ?
    |ORDER BY priority DESC
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, account_id)
    }

    override fun toString(): String = "schema.sq:selectRulesByAccount"
  }

  private inner class SelectRulesByMerchantOrAccountQuery<out T : Any>(
    public val user_id: String,
    public val merchant_name: String?,
    public val account_id: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("goal_assignment_rules", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("goal_assignment_rules", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT goal_assignment_rules.id, goal_assignment_rules.user_id, goal_assignment_rules.goal_id, goal_assignment_rules.merchant_name, goal_assignment_rules.account_id, goal_assignment_rules.description_pattern, goal_assignment_rules.priority, goal_assignment_rules.is_default, goal_assignment_rules.rule_mode, goal_assignment_rules.created_at, goal_assignment_rules.updated_at FROM goal_assignment_rules
    |WHERE user_id = ? AND (merchant_name ${ if (merchant_name == null) "IS" else "=" } ? OR (merchant_name IS NULL AND account_id ${ if (account_id == null) "IS" else "=" } ?))
    |ORDER BY priority DESC
    """.trimMargin(), mapper, 3) {
      bindString(0, user_id)
      bindString(1, merchant_name)
      bindString(2, account_id)
    }

    override fun toString(): String = "schema.sq:selectRulesByMerchantOrAccount"
  }

  private inner class SelectAssignmentByTransactionQuery<out T : Any>(
    public val transaction_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_goal_assignments", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_goal_assignments", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-2_051_286_844, """
    |SELECT transaction_goal_assignments.id, transaction_goal_assignments.transaction_id, transaction_goal_assignments.goal_id, transaction_goal_assignments.user_id, transaction_goal_assignments.amount_allocated, transaction_goal_assignments.assignment_source, transaction_goal_assignments.rule_id, transaction_goal_assignments.created_at FROM transaction_goal_assignments
    |WHERE transaction_id = ?
    """.trimMargin(), mapper, 1) {
      bindString(0, transaction_id)
    }

    override fun toString(): String = "schema.sq:selectAssignmentByTransaction"
  }

  private inner class SelectAssignmentsByGoalQuery<out T : Any>(
    public val goal_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_goal_assignments", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_goal_assignments", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(729_528_058, """
    |SELECT transaction_goal_assignments.id, transaction_goal_assignments.transaction_id, transaction_goal_assignments.goal_id, transaction_goal_assignments.user_id, transaction_goal_assignments.amount_allocated, transaction_goal_assignments.assignment_source, transaction_goal_assignments.rule_id, transaction_goal_assignments.created_at FROM transaction_goal_assignments
    |WHERE goal_id = ?
    |ORDER BY created_at DESC
    """.trimMargin(), mapper, 1) {
      bindString(0, goal_id)
    }

    override fun toString(): String = "schema.sq:selectAssignmentsByGoal"
  }

  private inner class GetTotalAssignedToGoalQuery<out T : Any>(
    public val goal_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_goal_assignments", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_goal_assignments", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-853_541_276, """
    |SELECT COALESCE(SUM(amount_allocated), 0) AS total
    |FROM transaction_goal_assignments
    |WHERE goal_id = ?
    """.trimMargin(), mapper, 1) {
      bindString(0, goal_id)
    }

    override fun toString(): String = "schema.sq:getTotalAssignedToGoal"
  }

  private inner class SelectGoalAllocationsForUserInRangeQuery<out T : Any>(
    public val user_id: String,
    public val date: String,
    public val date_: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_goal_assignments", "transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_goal_assignments", "transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_018_105_838, """
    |SELECT tga.amount_allocated, tga.goal_id, t.date,
    |       t.is_split_parent, t.is_distributed_parent, t.is_excluded
    |FROM transaction_goal_assignments tga
    |JOIN transactions t ON t.transaction_id = tga.transaction_id AND t.user_id = tga.user_id
    |WHERE tga.user_id = ? AND t.date >= ? AND t.date <= ?
    """.trimMargin(), mapper, 3) {
      bindString(0, user_id)
      bindString(1, date)
      bindString(2, date_)
    }

    override fun toString(): String = "schema.sq:selectGoalAllocationsForUserInRange"
  }

  private inner class SelectAssignmentForChildrenOfQuery<out T : Any>(
    public val parent_transaction_id: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_goal_assignments", "transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_goal_assignments", "transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT transaction_goal_assignments.id, transaction_goal_assignments.transaction_id, transaction_goal_assignments.goal_id, transaction_goal_assignments.user_id, transaction_goal_assignments.amount_allocated, transaction_goal_assignments.assignment_source, transaction_goal_assignments.rule_id, transaction_goal_assignments.created_at FROM transaction_goal_assignments
    |WHERE transaction_id IN (
    |    SELECT transaction_id FROM transactions WHERE parent_transaction_id ${ if (parent_transaction_id == null) "IS" else "=" } ?
    |)
    |LIMIT 1
    """.trimMargin(), mapper, 1) {
      bindString(0, parent_transaction_id)
    }

    override fun toString(): String = "schema.sq:selectAssignmentForChildrenOf"
  }

  private inner class SelectGoalIdsAssignedInAccountQuery<out T : Any>(
    public val account_id: String,
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_goal_assignments", "transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_goal_assignments", "transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_798_061_995, """
    |SELECT DISTINCT g.goal_id FROM transaction_goal_assignments g
    |JOIN transactions t ON t.transaction_id = g.transaction_id
    |WHERE t.account_id = ? AND t.user_id = ?
    """.trimMargin(), mapper, 2) {
      bindString(0, account_id)
      bindString(1, user_id)
    }

    override fun toString(): String = "schema.sq:selectGoalIdsAssignedInAccount"
  }

  private inner class SelectAllGoalAssignmentsByUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_goal_assignments", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_goal_assignments", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-911_797_654, """
    |SELECT transaction_goal_assignments.id, transaction_goal_assignments.transaction_id, transaction_goal_assignments.goal_id, transaction_goal_assignments.user_id, transaction_goal_assignments.amount_allocated, transaction_goal_assignments.assignment_source, transaction_goal_assignments.rule_id, transaction_goal_assignments.created_at FROM transaction_goal_assignments
    |WHERE user_id = ?
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectAllGoalAssignmentsByUser"
  }

  private inner class SelectAssignmentWithGoalQuery<out T : Any>(
    public val transaction_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_goal_assignments", "savings_goals", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_goal_assignments", "savings_goals", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-274_458_596, """
    |SELECT
    |    tga.id,
    |    tga.transaction_id,
    |    tga.goal_id,
    |    tga.user_id,
    |    tga.amount_allocated,
    |    tga.assignment_source,
    |    tga.rule_id,
    |    tga.created_at,
    |    sg.name AS goal_name,
    |    sg.icon_emoji AS goal_icon
    |FROM transaction_goal_assignments tga
    |INNER JOIN savings_goals sg ON tga.goal_id = sg.id
    |WHERE tga.transaction_id = ?
    """.trimMargin(), mapper, 1) {
      bindString(0, transaction_id)
    }

    override fun toString(): String = "schema.sq:selectAssignmentWithGoal"
  }

  private inner class SelectAllAssignmentsWithGoalsByUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_goal_assignments", "savings_goals", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_goal_assignments", "savings_goals", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(2_130_812_769, """
    |SELECT
    |    tga.transaction_id,
    |    tga.goal_id,
    |    tga.amount_allocated,
    |    sg.name AS goal_name,
    |    sg.icon_emoji AS goal_icon
    |FROM transaction_goal_assignments tga
    |INNER JOIN savings_goals sg ON tga.goal_id = sg.id
    |WHERE tga.user_id = ?
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectAllAssignmentsWithGoalsByUser"
  }

  private inner class SelectGoalAssignmentPreferencesQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", "transaction_goal_assignments", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", "transaction_goal_assignments", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_811_704_158, """
    |SELECT COALESCE(t.merchant_name, t.name) AS merchant_key,
    |       tga.goal_id,
    |       tga.amount_allocated,
    |       t.amount AS transaction_amount
    |FROM transaction_goal_assignments tga
    |INNER JOIN transactions t ON tga.transaction_id = t.transaction_id AND t.user_id = tga.user_id
    |WHERE tga.user_id = ?
    |  AND tga.assignment_source = 'user'
    |  AND (t.merchant_name IS NOT NULL OR t.name IS NOT NULL)
    |ORDER BY tga.created_at DESC
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectGoalAssignmentPreferences"
  }

  private inner class SelectBudgetPlansByYearQuery<out T : Any>(
    public val user_id: String,
    public val year: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("budget_plans", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("budget_plans", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_647_806_403, """
    |SELECT budget_plans.id, budget_plans.user_id, budget_plans.year, budget_plans.month, budget_plans.category_type, budget_plans.category_name, budget_plans.planned_amount, budget_plans.created_at, budget_plans.updated_at FROM budget_plans
    |WHERE user_id = ? AND year = ?
    |ORDER BY category_type, category_name, month
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindLong(1, year)
    }

    override fun toString(): String = "schema.sq:selectBudgetPlansByYear"
  }

  private inner class SelectBudgetPlansByMonthQuery<out T : Any>(
    public val user_id: String,
    public val year: Long,
    public val month: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("budget_plans", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("budget_plans", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-468_380_742, """
    |SELECT budget_plans.id, budget_plans.user_id, budget_plans.year, budget_plans.month, budget_plans.category_type, budget_plans.category_name, budget_plans.planned_amount, budget_plans.created_at, budget_plans.updated_at FROM budget_plans
    |WHERE user_id = ? AND year = ? AND month = ?
    |ORDER BY category_type, category_name
    """.trimMargin(), mapper, 3) {
      bindString(0, user_id)
      bindLong(1, year)
      bindLong(2, month)
    }

    override fun toString(): String = "schema.sq:selectBudgetPlansByMonth"
  }

  private inner class SelectBudgetPlansByTypeQuery<out T : Any>(
    public val user_id: String,
    public val year: Long,
    public val category_type: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("budget_plans", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("budget_plans", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_647_677_120, """
    |SELECT budget_plans.id, budget_plans.user_id, budget_plans.year, budget_plans.month, budget_plans.category_type, budget_plans.category_name, budget_plans.planned_amount, budget_plans.created_at, budget_plans.updated_at FROM budget_plans
    |WHERE user_id = ? AND year = ? AND category_type = ?
    |ORDER BY category_name, month
    """.trimMargin(), mapper, 3) {
      bindString(0, user_id)
      bindLong(1, year)
      bindString(2, category_type)
    }

    override fun toString(): String = "schema.sq:selectBudgetPlansByType"
  }

  private inner class GetYearlyTotalsByTypeQuery<out T : Any>(
    public val user_id: String,
    public val year: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("budget_plans", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("budget_plans", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-145_715_322, """
    |SELECT
    |    category_type,
    |    category_name,
    |    SUM(planned_amount) AS total_amount
    |FROM budget_plans
    |WHERE user_id = ? AND year = ?
    |GROUP BY category_type, category_name
    |ORDER BY category_type, total_amount DESC
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindLong(1, year)
    }

    override fun toString(): String = "schema.sq:getYearlyTotalsByType"
  }

  private inner class GetMonthlyTotalsQuery<out T : Any>(
    public val user_id: String,
    public val year: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("budget_plans", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("budget_plans", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_721_577_088, """
    |SELECT
    |    month,
    |    category_type,
    |    SUM(planned_amount) AS total_amount
    |FROM budget_plans
    |WHERE user_id = ? AND year = ?
    |GROUP BY month, category_type
    |ORDER BY month
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindLong(1, year)
    }

    override fun toString(): String = "schema.sq:getMonthlyTotals"
  }

  private inner class SelectBudgetPlansForCopyQuery<out T : Any>(
    public val user_id: String,
    public val year: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("budget_plans", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("budget_plans", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_466_764_497, """
    |SELECT category_type, category_name, month, planned_amount
    |FROM budget_plans
    |WHERE user_id = ? AND year = ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindLong(1, year)
    }

    override fun toString(): String = "schema.sq:selectBudgetPlansForCopy"
  }

  private inner class SelectAdviceCardsByCategoryQuery<out T : Any>(
    public val category: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("advice_cards", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("advice_cards", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_137_191_210, """
    |SELECT advice_cards.id, advice_cards.category, advice_cards.title, advice_cards.content, advice_cards.icon, advice_cards.priority, advice_cards.is_active, advice_cards.country FROM advice_cards
    |WHERE category = ? AND is_active = 1
    |ORDER BY priority ASC
    """.trimMargin(), mapper, 1) {
      bindString(0, category)
    }

    override fun toString(): String = "schema.sq:selectAdviceCardsByCategory"
  }

  private inner class SelectPinnedCardsQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("advice_cards", "user_advice_cards", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("advice_cards", "user_advice_cards", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-592_354_827, """
    |SELECT ac.id, ac.category, ac.title, ac.content, ac.icon, ac.priority, ac.is_active, ac.country FROM advice_cards ac
    |INNER JOIN user_advice_cards uac ON ac.id = uac.card_id
    |WHERE uac.user_id = ? AND uac.is_pinned = 1 AND uac.is_dismissed = 0
    |ORDER BY ac.priority ASC
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectPinnedCards"
  }

  private inner class SelectDismissedCardsQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("advice_cards", "user_advice_cards", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("advice_cards", "user_advice_cards", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-157_108_112, """
    |SELECT ac.id, ac.category, ac.title, ac.content, ac.icon, ac.priority, ac.is_active, ac.country FROM advice_cards ac
    |INNER JOIN user_advice_cards uac ON ac.id = uac.card_id
    |WHERE uac.user_id = ? AND uac.is_dismissed = 1
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectDismissedCards"
  }

  private inner class SelectAvailableCardsQuery<out T : Any>(
    public val user_id: String,
    public val country: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("advice_cards", "user_advice_cards", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("advice_cards", "user_advice_cards", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT ac.id, ac.category, ac.title, ac.content, ac.icon, ac.priority, ac.is_active, ac.country FROM advice_cards ac
    |LEFT JOIN user_advice_cards uac ON ac.id = uac.card_id AND uac.user_id = ?
    |WHERE ac.is_active = 1 AND (uac.is_dismissed IS NULL OR uac.is_dismissed = 0)
    |  AND (ac.country IS NULL OR ac.country ${ if (country == null) "IS" else "=" } ?)
    |ORDER BY ac.category, ac.priority ASC
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, country)
    }

    override fun toString(): String = "schema.sq:selectAvailableCards"
  }

  private inner class GetUserCountryQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("institutions", "plaid_tokens", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("institutions", "plaid_tokens", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_764_620_369, """
    |SELECT DISTINCT i.country_codes FROM institutions i
    |INNER JOIN plaid_tokens pt ON pt.institution_id = i.institution_id
    |WHERE pt.user_id = ? AND i.country_codes IS NOT NULL
    |LIMIT 1
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getUserCountry"
  }

  private inner class GetUserCardStatusQuery<out T : Any>(
    public val user_id: String,
    public val card_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("user_advice_cards", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("user_advice_cards", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-2_033_231_767,
        """SELECT user_advice_cards.id, user_advice_cards.user_id, user_advice_cards.card_id, user_advice_cards.is_pinned, user_advice_cards.is_dismissed, user_advice_cards.viewed_at, user_advice_cards.created_at FROM user_advice_cards WHERE user_id = ? AND card_id = ?""",
        mapper, 2) {
      bindString(0, user_id)
      bindString(1, card_id)
    }

    override fun toString(): String = "schema.sq:getUserCardStatus"
  }

  private inner class GetUserSettingQuery<out T : Any>(
    public val user_id: String,
    public val setting_key: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("user_settings", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("user_settings", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-736_510_359, """
    |SELECT setting_value FROM user_settings
    |WHERE user_id = ? AND setting_key = ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, setting_key)
    }

    override fun toString(): String = "schema.sq:getUserSetting"
  }

  private inner class GetAllUserSettingsQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("user_settings", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("user_settings", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_453_748_659, """
    |SELECT setting_key, setting_value FROM user_settings
    |WHERE user_id = ?
    |ORDER BY setting_key
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getAllUserSettings"
  }

  private inner class GetDismissedPersonalizedCardsQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("dismissed_personalized_cards", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("dismissed_personalized_cards", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-213_671_706, """
    |SELECT card_id FROM dismissed_personalized_cards
    |WHERE user_id = ?
    |AND (expires_at IS NULL OR expires_at > CURRENT_TIMESTAMP)
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getDismissedPersonalizedCards"
  }

  private inner class IsPersonalizedCardDismissedQuery<out T : Any>(
    public val user_id: String,
    public val card_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("dismissed_personalized_cards", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("dismissed_personalized_cards", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_756_701_147, """
    |SELECT COUNT(*) FROM dismissed_personalized_cards
    |WHERE user_id = ? AND card_id = ?
    |AND (expires_at IS NULL OR expires_at > CURRENT_TIMESTAMP)
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, card_id)
    }

    override fun toString(): String = "schema.sq:isPersonalizedCardDismissed"
  }

  private inner class GetPersonalizedCardViewQuery<out T : Any>(
    public val user_id: String,
    public val card_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("personalized_card_views", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("personalized_card_views", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_878_102_047,
        """SELECT personalized_card_views.id, personalized_card_views.user_id, personalized_card_views.card_id, personalized_card_views.shown_at, personalized_card_views.shown_count FROM personalized_card_views WHERE user_id = ? AND card_id = ?""",
        mapper, 2) {
      bindString(0, user_id)
      bindString(1, card_id)
    }

    override fun toString(): String = "schema.sq:getPersonalizedCardView"
  }

  private inner class GetAllPersonalizedCardViewsQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("personalized_card_views", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("personalized_card_views", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_680_673_719, """
    |SELECT card_id, shown_at, shown_count FROM personalized_card_views
    |WHERE user_id = ?
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getAllPersonalizedCardViews"
  }

  private inner class GetCustomCategoriesForUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("user_custom_categories", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("user_custom_categories", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(2_026_379_115, """
    |SELECT id, user_id, primary_name, secondary_name, is_income, icon, created_at
    |FROM user_custom_categories
    |WHERE user_id = ? AND deleted_at IS NULL
    |ORDER BY primary_name, secondary_name
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getCustomCategoriesForUser"
  }

  private inner class GetCustomCategoryByIdQuery<out T : Any>(
    public val id: Long,
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("user_custom_categories", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("user_custom_categories", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_165_497_629, """
    |SELECT id, user_id, primary_name, secondary_name, is_income, icon, created_at
    |FROM user_custom_categories
    |WHERE id = ? AND user_id = ?
    """.trimMargin(), mapper, 2) {
      bindLong(0, id)
      bindString(1, user_id)
    }

    override fun toString(): String = "schema.sq:getCustomCategoryById"
  }

  private inner class CountTransactionsForUserCategoryQuery<out T : Any>(
    public val user_id: String,
    public val user_category: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT COUNT(*) FROM transactions
    |WHERE user_id = ? AND user_category ${ if (user_category == null) "IS" else "=" } ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, user_category)
    }

    override fun toString(): String = "schema.sq:countTransactionsForUserCategory"
  }

  private inner class ListTransactionsForUserCategoryQuery<out T : Any>(
    public val user_id: String,
    public val user_category: String?,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT transaction_id, COALESCE(merchant_name, name) AS merchant, amount, date
    |FROM transactions
    |WHERE user_id = ? AND user_category ${ if (user_category == null) "IS" else "=" } ?
    |ORDER BY date DESC
    |LIMIT ?
    """.trimMargin(), mapper, 3) {
      bindString(0, user_id)
      bindString(1, user_category)
      bindLong(2, value)
    }

    override fun toString(): String = "schema.sq:listTransactionsForUserCategory"
  }

  private inner class ListFullTransactionsForUserCategoryQuery<out T : Any>(
    public val user_id: String,
    public val user_category: String?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null, """
    |SELECT transaction_id, name, merchant_name, amount, original_description
    |FROM transactions
    |WHERE user_id = ? AND user_category ${ if (user_category == null) "IS" else "=" } ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, user_category)
    }

    override fun toString(): String = "schema.sq:listFullTransactionsForUserCategory"
  }

  private inner class SelectSubscriptionMerchantsQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("subscription_merchants", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("subscription_merchants", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_899_783_656, """
    |SELECT subscription_merchants.id, subscription_merchants.user_id, subscription_merchants.merchant_key, subscription_merchants.typical_amount, subscription_merchants.status, subscription_merchants.confirmed_at, subscription_merchants.created_at FROM subscription_merchants
    |WHERE user_id = ?
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectSubscriptionMerchants"
  }

  private inner class SelectConfirmedSubscriptionsQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("subscription_merchants", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("subscription_merchants", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_102_600_205, """
    |SELECT subscription_merchants.id, subscription_merchants.user_id, subscription_merchants.merchant_key, subscription_merchants.typical_amount, subscription_merchants.status, subscription_merchants.confirmed_at, subscription_merchants.created_at FROM subscription_merchants
    |WHERE user_id = ? AND status = 'confirmed'
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectConfirmedSubscriptions"
  }

  private inner class GetTemplateBySignatureQuery<out T : Any>(
    public val user_id: String,
    public val institution_signature: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("statement_templates", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("statement_templates", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(634_975_147, """
    |SELECT statement_templates.id, statement_templates.user_id, statement_templates.institution_signature, statement_templates.institution_alias, statement_templates.file_type, statement_templates.column_mapping, statement_templates.date_format, statement_templates.delimiter, statement_templates.template_data, statement_templates.is_valid, statement_templates.created_at, statement_templates.updated_at FROM statement_templates
    |WHERE user_id = ? AND institution_signature = ? AND is_valid = 1
    |ORDER BY updated_at DESC LIMIT 1
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, institution_signature)
    }

    override fun toString(): String = "schema.sq:getTemplateBySignature"
  }

  private inner class GetTemplatesForUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("statement_templates", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("statement_templates", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_048_812_841, """
    |SELECT statement_templates.id, statement_templates.user_id, statement_templates.institution_signature, statement_templates.institution_alias, statement_templates.file_type, statement_templates.column_mapping, statement_templates.date_format, statement_templates.delimiter, statement_templates.template_data, statement_templates.is_valid, statement_templates.created_at, statement_templates.updated_at FROM statement_templates
    |WHERE user_id = ? AND is_valid = 1
    |ORDER BY institution_signature
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getTemplatesForUser"
  }

  private inner class GetManualAccountsForUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("manual_accounts", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("manual_accounts", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_079_564_844,
        """SELECT manual_accounts.id, manual_accounts.account_id, manual_accounts.user_id, manual_accounts.institution_name, manual_accounts.account_name, manual_accounts.account_type, manual_accounts.balance, manual_accounts.template_id, manual_accounts.created_at FROM manual_accounts WHERE user_id = ?""",
        mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getManualAccountsForUser"
  }

  private inner class GetManualAccountQuery<out T : Any>(
    public val account_id: String,
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("manual_accounts", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("manual_accounts", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_700_342_549,
        """SELECT manual_accounts.id, manual_accounts.account_id, manual_accounts.user_id, manual_accounts.institution_name, manual_accounts.account_name, manual_accounts.account_type, manual_accounts.balance, manual_accounts.template_id, manual_accounts.created_at FROM manual_accounts WHERE account_id = ? AND user_id = ?""",
        mapper, 2) {
      bindString(0, account_id)
      bindString(1, user_id)
    }

    override fun toString(): String = "schema.sq:getManualAccount"
  }

  private inner class GetManualAccountByAccountIdQuery<out T : Any>(
    public val account_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("manual_accounts", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("manual_accounts", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_926_751_142,
        """SELECT manual_accounts.id, manual_accounts.account_id, manual_accounts.user_id, manual_accounts.institution_name, manual_accounts.account_name, manual_accounts.account_type, manual_accounts.balance, manual_accounts.template_id, manual_accounts.created_at FROM manual_accounts WHERE account_id = ?""",
        mapper, 1) {
      bindString(0, account_id)
    }

    override fun toString(): String = "schema.sq:getManualAccountByAccountId"
  }

  private inner class GetTransactionsForAccountQuery<out T : Any>(
    public val account_id: String,
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_000_594_293,
        """SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions WHERE account_id = ? AND user_id = ?""",
        mapper, 2) {
      bindString(0, account_id)
      bindString(1, user_id)
    }

    override fun toString(): String = "schema.sq:getTransactionsForAccount"
  }

  private inner class GetNotificationTokenQuery<out T : Any>(
    public val token: String,
    public val expires_at: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("notification_tokens", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("notification_tokens", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_426_493_234,
        """SELECT notification_tokens.id, notification_tokens.user_id, notification_tokens.token, notification_tokens.transaction_ids, notification_tokens.phone_number, notification_tokens.action, notification_tokens.created_at, notification_tokens.expires_at, notification_tokens.acted_at FROM notification_tokens WHERE token = ? AND expires_at > ? AND action IS NULL""",
        mapper, 2) {
      bindString(0, token)
      bindString(1, expires_at)
    }

    override fun toString(): String = "schema.sq:getNotificationToken"
  }

  private inner class GetNotificationTokenByPhoneQuery<out T : Any>(
    public val phone_number: String?,
    public val expires_at: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("notification_tokens", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("notification_tokens", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(null,
        """SELECT notification_tokens.id, notification_tokens.user_id, notification_tokens.token, notification_tokens.transaction_ids, notification_tokens.phone_number, notification_tokens.action, notification_tokens.created_at, notification_tokens.expires_at, notification_tokens.acted_at FROM notification_tokens WHERE phone_number ${ if (phone_number == null) "IS" else "=" } ? AND expires_at > ? AND action IS NULL ORDER BY created_at DESC LIMIT 1""",
        mapper, 2) {
      bindString(0, phone_number)
      bindString(1, expires_at)
    }

    override fun toString(): String = "schema.sq:getNotificationTokenByPhone"
  }

  private inner class GetNotificationTokenByIdQuery<out T : Any>(
    public val token: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("notification_tokens", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("notification_tokens", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_141_339_684,
        """SELECT notification_tokens.id, notification_tokens.user_id, notification_tokens.token, notification_tokens.transaction_ids, notification_tokens.phone_number, notification_tokens.action, notification_tokens.created_at, notification_tokens.expires_at, notification_tokens.acted_at FROM notification_tokens WHERE token = ?""",
        mapper, 1) {
      bindString(0, token)
    }

    override fun toString(): String = "schema.sq:getNotificationTokenById"
  }

  private inner class GetUnnotifiedTransactionsQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_138_280_002,
        """SELECT transactions.id, transactions.transaction_id, transactions.account_id, transactions.user_id, transactions.amount, transactions.iso_currency_code, transactions.unofficial_currency_code, transactions.date, transactions.authorized_date, transactions.authorized_datetime, transactions.datetime, transactions.name, transactions.merchant_name, transactions.original_description, transactions.category, transactions.category_id, transactions.personal_finance_category_primary, transactions.personal_finance_category_detailed, transactions.personal_finance_category_confidence, transactions.transaction_type, transactions.transaction_code, transactions.pending, transactions.pending_transaction_id, transactions.location_json, transactions.payment_meta_json, transactions.account_owner, transactions.created_at, transactions.updated_at, transactions.last_synced_at, transactions.user_category, transactions.embedding, transactions.embedding_version, transactions.user_notes, transactions.is_hidden, transactions.is_expense_report, transactions.tags, transactions.ai_category, transactions.confidence_score, transactions.original_ai_category, transactions.corrected_at, transactions.correction_confidence, transactions.ml_model_version, transactions.category_source, transactions.parent_transaction_id, transactions.child_type, transactions.is_split_parent, transactions.is_distributed_parent, transactions.split_order, transactions.distribution_start_month, transactions.distribution_end_month, transactions.distribution_month_index, transactions.distribution_total_months, transactions.is_verified, transactions.verified_at, transactions.is_excluded, transactions.notified_at FROM transactions WHERE user_id = ? AND notified_at IS NULL AND is_verified = 0 AND pending = 0""",
        mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getUnnotifiedTransactions"
  }

  private inner class GetRecentTransactionIdsForUserQuery<out T : Any>(
    public val user_id: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-354_829_661, """
    |SELECT transaction_id FROM transactions
    |WHERE user_id = ? AND pending = 0
    |ORDER BY date DESC, created_at DESC
    |LIMIT ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindLong(1, value)
    }

    override fun toString(): String = "schema.sq:getRecentTransactionIdsForUser"
  }

  private inner class GetLastActivityQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("users", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("users", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_105_137_183,
        """SELECT last_activity_at FROM users WHERE user_id = ?""", mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getLastActivity"
  }

  private inner class SelectContextsByUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("contexts", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("contexts", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_053_339_972,
        """SELECT contexts.id, contexts.user_id, contexts.name, contexts.color, contexts.created_at, contexts.updated_at FROM contexts WHERE user_id = ? ORDER BY created_at ASC""",
        mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectContextsByUser"
  }

  private inner class SelectContextByIdQuery<out T : Any>(
    public val id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("contexts", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("contexts", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_697_026_645,
        """SELECT contexts.id, contexts.user_id, contexts.name, contexts.color, contexts.created_at, contexts.updated_at FROM contexts WHERE id = ?""",
        mapper, 1) {
      bindString(0, id)
    }

    override fun toString(): String = "schema.sq:selectContextById"
  }

  private inner class CountTransactionsForContextQuery<out T : Any>(
    public val context_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_contexts", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_contexts", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_896_229_264,
        """SELECT COUNT(DISTINCT transaction_id) FROM transaction_contexts WHERE context_id = ?""",
        mapper, 1) {
      bindString(0, context_id)
    }

    override fun toString(): String = "schema.sq:countTransactionsForContext"
  }

  private inner class SelectContextIdsForTransactionQuery<out T : Any>(
    public val transaction_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_contexts", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_contexts", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_872_036_364,
        """SELECT context_id FROM transaction_contexts WHERE transaction_id = ?""", mapper, 1) {
      bindString(0, transaction_id)
    }

    override fun toString(): String = "schema.sq:selectContextIdsForTransaction"
  }

  private inner class SelectContextIdsForUserTransactionsQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transaction_contexts", "transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transaction_contexts", "transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_447_246_998, """
    |SELECT tc.transaction_id, tc.context_id
    |FROM transaction_contexts tc
    |JOIN transactions t ON t.transaction_id = tc.transaction_id
    |WHERE t.user_id = ?
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectContextIdsForUserTransactions"
  }

  private inner class SelectContextsForTransactionQuery<out T : Any>(
    public val transaction_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("contexts", "transaction_contexts", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("contexts", "transaction_contexts", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(77_501_007, """
    |SELECT c.id, c.user_id, c.name, c.color, c.created_at, c.updated_at FROM contexts c
    |INNER JOIN transaction_contexts tc ON c.id = tc.context_id
    |WHERE tc.transaction_id = ?
    """.trimMargin(), mapper, 1) {
      bindString(0, transaction_id)
    }

    override fun toString(): String = "schema.sq:selectContextsForTransaction"
  }

  private inner class GetLatestWiz3RCardGenerationQuery<out T : Any>(
    public val user_id: String,
    public val card_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("wiz3r_card_generations", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("wiz3r_card_generations", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(291_644_652, """
    |SELECT wiz3r_card_generations.id, wiz3r_card_generations.user_id, wiz3r_card_generations.card_id, wiz3r_card_generations.generated_at, wiz3r_card_generations.context_json, wiz3r_card_generations.response_json FROM wiz3r_card_generations
    |WHERE user_id = ? AND card_id = ?
    |ORDER BY generated_at DESC
    |LIMIT 1
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, card_id)
    }

    override fun toString(): String = "schema.sq:getLatestWiz3RCardGeneration"
  }

  private inner class GetLatestWiz3RCardGenerationSinceQuery<out T : Any>(
    public val user_id: String,
    public val card_id: String,
    public val generated_at: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("wiz3r_card_generations", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("wiz3r_card_generations", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-2_117_135_954, """
    |SELECT wiz3r_card_generations.id, wiz3r_card_generations.user_id, wiz3r_card_generations.card_id, wiz3r_card_generations.generated_at, wiz3r_card_generations.context_json, wiz3r_card_generations.response_json FROM wiz3r_card_generations
    |WHERE user_id = ? AND card_id = ? AND generated_at >= ?
    |ORDER BY generated_at DESC
    |LIMIT 1
    """.trimMargin(), mapper, 3) {
      bindString(0, user_id)
      bindString(1, card_id)
      bindString(2, generated_at)
    }

    override fun toString(): String = "schema.sq:getLatestWiz3RCardGenerationSince"
  }

  private inner class GetLatestWiz3RCardGenerationForUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("wiz3r_card_generations", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("wiz3r_card_generations", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_525_698_136, """
    |SELECT wiz3r_card_generations.id, wiz3r_card_generations.user_id, wiz3r_card_generations.card_id, wiz3r_card_generations.generated_at, wiz3r_card_generations.context_json, wiz3r_card_generations.response_json FROM wiz3r_card_generations
    |WHERE user_id = ?
    |ORDER BY generated_at DESC
    |LIMIT 1
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getLatestWiz3RCardGenerationForUser"
  }

  private inner class GetLatestWiz3RCardGenerationForUserWithPrefixQuery<out T : Any>(
    public val user_id: String,
    public val `value`: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("wiz3r_card_generations", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("wiz3r_card_generations", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_483_948_352, """
    |SELECT wiz3r_card_generations.id, wiz3r_card_generations.user_id, wiz3r_card_generations.card_id, wiz3r_card_generations.generated_at, wiz3r_card_generations.context_json, wiz3r_card_generations.response_json FROM wiz3r_card_generations
    |WHERE user_id = ? AND card_id LIKE ? || '%'
    |ORDER BY generated_at DESC
    |LIMIT 1
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, value)
    }

    override fun toString(): String = "schema.sq:getLatestWiz3RCardGenerationForUserWithPrefix"
  }

  private inner class GetRecentWiz3RCardGenerationsQuery<out T : Any>(
    public val user_id: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("wiz3r_card_generations", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("wiz3r_card_generations", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-637_239_845, """
    |SELECT wiz3r_card_generations.id, wiz3r_card_generations.user_id, wiz3r_card_generations.card_id, wiz3r_card_generations.generated_at, wiz3r_card_generations.context_json, wiz3r_card_generations.response_json FROM wiz3r_card_generations
    |WHERE user_id = ?
    |ORDER BY generated_at DESC
    |LIMIT ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindLong(1, value)
    }

    override fun toString(): String = "schema.sq:getRecentWiz3RCardGenerations"
  }

  private inner class GetMaxTransactionUpdatedAtForUserQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("transactions", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("transactions", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_744_565_220,
        """SELECT MAX(updated_at) FROM transactions WHERE user_id = ?""", mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:getMaxTransactionUpdatedAtForUser"
  }

  private inner class GetWiz3RChatThreadQuery<out T : Any>(
    public val user_id: String,
    public val card_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("wiz3r_chat_threads", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("wiz3r_chat_threads", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_843_402_835, """
    |SELECT wiz3r_chat_threads.thread_id, wiz3r_chat_threads.user_id, wiz3r_chat_threads.card_id, wiz3r_chat_threads.created_at, wiz3r_chat_threads.last_message_at FROM wiz3r_chat_threads
    |WHERE user_id = ? AND card_id = ?
    |LIMIT 1
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, card_id)
    }

    override fun toString(): String = "schema.sq:getWiz3RChatThread"
  }

  private inner class SelectWiz3RChatMessagesQuery<out T : Any>(
    public val thread_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("wiz3r_chat_messages", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("wiz3r_chat_messages", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_703_931_179, """
    |SELECT wiz3r_chat_messages.id, wiz3r_chat_messages.thread_id, wiz3r_chat_messages.user_id, wiz3r_chat_messages.role, wiz3r_chat_messages.content, wiz3r_chat_messages.chart_json, wiz3r_chat_messages.created_at FROM wiz3r_chat_messages
    |WHERE thread_id = ?
    |ORDER BY created_at ASC, id ASC
    """.trimMargin(), mapper, 1) {
      bindString(0, thread_id)
    }

    override fun toString(): String = "schema.sq:selectWiz3RChatMessages"
  }

  private inner class SelectWiz3RChatMessagesRecentQuery<out T : Any>(
    public val thread_id: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("wiz3r_chat_messages", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("wiz3r_chat_messages", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-875_228_784, """
    |SELECT wiz3r_chat_messages.id, wiz3r_chat_messages.thread_id, wiz3r_chat_messages.user_id, wiz3r_chat_messages.role, wiz3r_chat_messages.content, wiz3r_chat_messages.chart_json, wiz3r_chat_messages.created_at FROM wiz3r_chat_messages
    |WHERE thread_id = ?
    |ORDER BY created_at DESC, id DESC
    |LIMIT ?
    """.trimMargin(), mapper, 2) {
      bindString(0, thread_id)
      bindLong(1, value)
    }

    override fun toString(): String = "schema.sq:selectWiz3RChatMessagesRecent"
  }

  private inner class CountWiz3RChatUserMessagesSinceQuery<out T : Any>(
    public val user_id: String,
    public val created_at: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("wiz3r_chat_messages", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("wiz3r_chat_messages", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-272_911_175, """
    |SELECT COUNT(*) AS cnt FROM wiz3r_chat_messages
    |WHERE user_id = ? AND role = 'user' AND created_at >= ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, created_at)
    }

    override fun toString(): String = "schema.sq:countWiz3RChatUserMessagesSince"
  }

  private inner class SelectBudgetClassificationQuery<out T : Any>(
    public val user_id: String,
    public val category_code: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("budget_category_classifications", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("budget_category_classifications", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(939_988_545, """
    |SELECT budget_category_classifications.user_id, budget_category_classifications.category_code, budget_category_classifications.bucket, budget_category_classifications.created_at, budget_category_classifications.updated_at FROM budget_category_classifications
    |WHERE user_id = ? AND category_code = ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindString(1, category_code)
    }

    override fun toString(): String = "schema.sq:selectBudgetClassification"
  }

  private inner class SelectAllBudgetClassificationsQuery<out T : Any>(
    public val user_id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("budget_category_classifications", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("budget_category_classifications", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_004_686_557, """
    |SELECT budget_category_classifications.user_id, budget_category_classifications.category_code, budget_category_classifications.bucket, budget_category_classifications.created_at, budget_category_classifications.updated_at FROM budget_category_classifications
    |WHERE user_id = ?
    |ORDER BY category_code ASC
    """.trimMargin(), mapper, 1) {
      bindString(0, user_id)
    }

    override fun toString(): String = "schema.sq:selectAllBudgetClassifications"
  }

  private inner class SelectRecentBudgetHealthSnapshotsQuery<out T : Any>(
    public val user_id: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("budget_health_snapshots", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("budget_health_snapshots", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-219_446_755, """
    |SELECT budget_health_snapshots.user_id, budget_health_snapshots.year_month, budget_health_snapshots.needs_pct, budget_health_snapshots.wants_pct, budget_health_snapshots.savings_pct, budget_health_snapshots.health_label, budget_health_snapshots.created_at FROM budget_health_snapshots
    |WHERE user_id = ?
    |ORDER BY year_month DESC
    |LIMIT ?
    """.trimMargin(), mapper, 2) {
      bindString(0, user_id)
      bindLong(1, value)
    }

    override fun toString(): String = "schema.sq:selectRecentBudgetHealthSnapshots"
  }
}
